import { useState, useEffect, useMemo } from 'react';
import { Select, Table, Tag, Space, Spin, Empty, Typography, Tooltip, Row, Col, Input, Button, Modal, Form, message, Popconfirm, Menu, Descriptions } from 'antd';
import { FileTextOutlined, PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, CopyOutlined, CheckOutlined, ApiOutlined, BankOutlined, BookOutlined, BarChartOutlined, SettingOutlined, BellOutlined, RightOutlined, CodeOutlined } from '@ant-design/icons';
import ReactJson from '@microlink/react-json-view';
import { metaApi, testApi } from '../services/api';
import { buildChannelOptions, getChannelLogo, getCurrencyIcon } from '../utils/channelLogos';
import { useLanguage } from '../contexts/LanguageContext';
import { fieldLabelMap, fieldTipMap } from '../utils/apiDocI18n';
import { defineUniqueFields } from '../utils/apiDocSchema';
import { ZAND_BANK_PARAM_OVERRIDES, ZAND_BANK_API_DESC, ZAND_BANK_API_EXAMPLES } from '../utils/zandbankApiDoc';
import { DLOCAL_PARAM_OVERRIDES, DLOCAL_API_DESC, DLOCAL_API_EXAMPLES } from '../utils/dlocalApiDoc';

const { Text } = Typography;

// ─── Color tokens (aligned with Admin Console design language) ───
const COLORS = {
  bg: '#0a0a0f',
  bgCard: '#12121a',
  bgCode: '#1a1a2e',
  bgSidebar: '#ffffff',
  bgContent: '#f5f7fa',
  border: '#e2e8f0',
  borderLight: '#f0f2f5',
  accent: '#667eea',
  accentLight: '#764ba2',
  accentBg: '#f0f4ff',
  accentGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  green: '#22c55e',
  greenBg: '#ecfdf5',
  greenBorder: '#bbf7d0',
  blue: '#3b82f6',
  blueBg: '#eff6ff',
  blueBorder: '#bfdbfe',
  orange: '#f59e0b',
  orangeBg: '#fffbeb',
  red: '#ef4444',
  redBg: '#fef2f2',
  textPrimary: '#262626',
  textSecondary: '#64748b',
  textMuted: '#94a3b8',
  textCode: '#e2e8f0',
  codeBg: '#1e293b',
  codeHeaderBg: '#0f172a',
};

const getApiCodeLabels = (t) => ({
  ACCOUNT_CREATE: { label: t('doc_apiAccountCreate'), color: 'blue', icon: '👤', method: 'POST', uri: '/v1/fiat/payment/account/create', desc: t('doc_descAccountCreate') },
  ACCOUNT_CLOSE: { label: t('doc_apiAccountClose'), color: 'default', icon: '🚪', method: 'POST', uri: '/v1/fiat/payment/account/close', desc: t('doc_descAccountClose') },
  ACCOUNT_CLOSE_QUERY: { label: '关户查询', color: 'cyan', icon: '🔍', method: 'GET', uri: '/v1/fiat/payment/account/close/query', desc: '查询关户状态' },
  ACCOUNT_QUERY: { label: t('doc_apiAccountQuery'), color: 'cyan', icon: '🔍', method: 'GET', uri: '/v1/fiat/payment/account/query', desc: t('doc_descAccountQuery') },
  CARD_BIND: { label: t('doc_apiCardBind'), color: 'green', icon: '💳', method: 'POST', uri: '/v1/fiat/payment/bankaccount/bind', desc: t('doc_descCardBind') },
  CARD_QUERY: { label: t('doc_apiCardQuery'), color: 'lime', icon: '🔎', method: 'GET', uri: '/v1/fiat/payment/bankaccount/query', desc: t('doc_descCardQuery') },
  CARD_UNBIND: { label: t('doc_apiCardUnbind'), color: 'orange', icon: '✂️', method: 'POST', uri: '/v1/fiat/payment/bankaccount/unbind', desc: t('doc_descCardUnbind') },
  DEPOSIT_CREATE: { label: t('doc_apiDepositCreate'), color: 'gold', icon: '💰', method: 'POST', uri: '/v1/fiat/payment/deposit/create', desc: t('doc_descDepositCreate') },
  DEPOSIT_QUERY: { label: t('doc_apiDepositQuery'), color: 'geekblue', icon: '📋', method: 'GET', uri: '/v1/fiat/payment/deposit/query', desc: t('doc_descDepositQuery') },
  DEPOSIT_CLOSE: { label: t('doc_apiDepositClose'), color: 'orange', icon: '🚫', method: 'POST', uri: '/v1/fiat/payment/deposit/close', desc: t('doc_descDepositClose') },
  DEPOSIT_CAPTURE: { label: t('doc_apiDepositCapture'), color: 'volcano', icon: '💵', method: 'POST', uri: '/v1/fiat/payment/deposit/capture', desc: t('doc_descDepositCapture') },
  REFUND_CREATE: { label: t('doc_apiRefundCreate'), color: 'magenta', icon: '↩️', method: 'POST', uri: '/v1/fiat/payment/refund/create', desc: t('doc_descRefundCreate') },
  REFUND_QUERY: { label: t('doc_apiRefundQuery'), color: 'purple', icon: '📑', method: 'GET', uri: '/v1/fiat/payment/refund/query', desc: t('doc_descRefundQuery') },
  WITHDRAW_CREATE: { label: t('doc_apiWithdrawCreate'), color: 'red', icon: '🏦', method: 'POST', uri: '/v1/fiat/payment/withdraw/create', desc: t('doc_descWithdrawCreate') },
  WITHDRAW_QUERY: { label: t('doc_apiWithdrawQuery'), color: 'volcano', icon: '📄', method: 'GET', uri: '/v1/fiat/payment/withdraw/query', desc: t('doc_descWithdrawQuery') },
  CHANNEL_CONFIG_QUERY: { label: t('doc_apiChannelConfig'), color: 'blue', icon: '⚙️', method: 'GET', uri: '/v1/fiat/payment/channel/config/query', desc: t('doc_descChannelConfig') },
  ORDER_PAGE: { label: t('doc_apiOrderPage'), color: 'cyan', icon: '📊', method: 'POST', uri: '/v1/fiat/payment/order/page', desc: t('doc_descOrderPage') },
  BANK_LIST: { label: t('doc_apiBankList'), color: 'green', icon: '🏛️', method: 'GET', uri: '/v1/fiat/payment/bank/active/list', desc: t('doc_descBankList') },
  FILE_UPLOAD: { label: t('doc_apiFileUpload'), color: 'gold', icon: '📤', method: 'POST', uri: '/v1/fiat/payment/channel/files/upload', desc: t('doc_descFileUpload') },
  FILE_STATUS_QUERY: { label: t('doc_apiFileStatusQuery'), color: 'lime', icon: '📁', method: 'POST', uri: '/v1/fiat/payment/channel/files/batch-query', desc: t('doc_descFileStatusQuery') },
  USER_CREATE: { label: t('doc_apiUserCreate'), color: 'blue', icon: '👥', method: 'POST', uri: '/v1/fiat/payment/user/create', desc: t('doc_descUserCreate') },
  USER_SUPPLEMENT: { label: t('doc_apiUserSupplement'), color: 'purple', icon: '📎', method: 'POST', uri: '/v1/fiat/payment/user/supplement', desc: t('doc_descUserSupplement') },
  USER_QUERY: { label: t('doc_apiUserQuery'), color: 'cyan', icon: '🧑', method: 'GET', uri: '/v1/fiat/payment/user/query', desc: t('doc_descUserQuery') },
  BALANCE_QUERY: { label: t('doc_apiBalanceQuery'), color: 'green', icon: '💵', method: 'POST', uri: '/v1/fiat/payment/channel/balance/query', desc: t('doc_descBalanceQuery') },
  RATE_QUERY: { label: t('doc_apiRateQuery'), color: 'gold', icon: '💱', method: 'POST', uri: '/v1/fiat/payment/channel/rate/query', desc: t('doc_descRateQuery') },
  MMO_LIST: { label: t('doc_apiMmoList'), color: 'magenta', icon: '📱', method: 'GET', uri: '/v1/fiat/payment/mmo/list', desc: t('doc_descMmoList') },
  PAYOUT_REQUIREMENTS: { label: t('doc_apiPayoutRequirements'), color: 'purple', icon: '📋', method: 'GET', uri: '/v1/fiat/payment/bankaccount/payout/requirements', desc: t('doc_descPayoutRequirements') },
  REMITTER_CREATE: { label: '付款人创建', color: 'green', icon: '👔', method: 'POST', uri: '/v1/fiat/payment/remitter/create', desc: '创建/编辑付款人(Remitter)，用于 Corpay 代付场景' },
  REMITTER_QUERY: { label: '付款人查询', color: 'cyan', icon: '🔍', method: 'GET', uri: '/v1/fiat/payment/remitter/query', desc: '查询付款人状态和详情' },
  FUND_TRANSFER_CREATE: { label: '资金调拨创建', color: 'green', icon: '🔄', method: 'POST', uri: '/v1/fiat/payment/fund/transfer/create', desc: '创建资金调拨(Fund Transfer)订单，支持 Banking Circle / SGB' },
  FUND_TRANSFER_QUERY: { label: '资金调拨查询', color: 'blue', icon: '📄', method: 'GET', uri: '/v1/fiat/payment/fund/transfer/query', desc: '查询资金调拨订单状态' },
  CHANNEL_TRANSACTIONS: { label: '渠道流水查询', color: 'purple', icon: '📊', method: 'POST', uri: '/v1/fiat/payment/fund/transfer/channel-transactions', desc: '查询渠道账户交易流水' },
});

// 各场景的基础参数（所有渠道通用，请求体骨架）
export const BASE_PARAMS = {
  ACCOUNT_CREATE: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'externalUserId', label: '外部用户ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'currency', label: '币种', type: 'STRING', required: true, tip: 'EQUALS_MONEY仅USD; LEAN_TECH仅AED; CVPAY仅VND; CALLPAY仅ZAR; ZAND_BANK仅AED; MDAQ多币种(SGD/HKD/IDR/USD等); 其他渠道需checkSupportCurrency' },
    { name: 'channelCode', label: '渠道代码', type: 'ENUM', required: true, tip: 'EQUALS_MONEY / PAGSMILE / SGB / PYVIO / LEAN_TECH / KORAPAY / PALMPAY / BANK_FRICK / BANKING_CIRCLE / INSWITCH / CVPAY / CALLPAY / MDAQ / MOOLAHGO / ZAND_BANK' },
    { name: 'paymentMethod', label: '支付方式', type: 'ENUM', required: true, tip: 'LEAN_TECH仅LEAN; BANKING_CIRCLE仅VIRTUAL_ACCOUNT; INSWITCH仅VIRTUAL_ACCOUNT(BRL); CVPAY仅BANK_TRANSFER; CALLPAY仅BANK_TRANSFER; MDAQ仅VIRTUAL_ACCOUNT; MOOLAHGO仅VIRTUAL_ACCOUNT; ZAND_BANK仅VIRTUAL_ACCOUNT; 其他: VIRTUAL_ACCOUNT / PIX / VIETQR / ZEN_WALLET / INTERACE_REQUEST / LEAN / BANK_TRANSFER' },
    { name: 'userType', label: '用户类型', type: 'ENUM', required: true, tip: 'LEAN_TECH仅INDIVIDUAL; MDAQ仅BUSINESS(本期仅KYB); MOOLAHGO仅BUSINESS; ZAND_BANK支持INDIVIDUAL/BUSINESS; 其他: INDIVIDUAL / BUSINESS / ADMINISTRATOR / ALL' },
    { name: 'extParam', label: '扩展参数', type: 'JSON_STRING', required: false, tip: 'CVPAY无需传入; MDAQ需传purpose(必填)及可选bankCountry/network/description; MOOLAHGO必填(KYC信息+企业信息+付款人画像+callbackUrl); ZAND_BANK必填(accountName/type/identifierDocuments/whitelistIbans，INDIVIDUAL需nationality+dateOfBirth，BUSINESS需licenseIssuer); 其他渠道均需传入，JSON格式字符串，见下方渠道差异字段' },
  ],
  CARD_BIND: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'accountId', label: '账户ID', type: 'STRING', required: false, tip: 'BANK_FRICK/PYVIO/DA5外所有渠道必填; MOOLAHGO必填; TAZAPAY非必填传入时校验; FV_BANK不校验(不使用)' },
    { name: 'channelCode', label: '渠道代码', type: 'ENUM', required: true, tip: 'EQUALS_MONEY / PAGSMILE / SGB / PYVIO / LEAN_TECH / KORAPAY / PALMPAY / BANK_FRICK / BANKING_CIRCLE / INSWITCH / TAZAPAY / CVPAY / CALLPAY / DA5 / MOOLAHGO / FV_BANK / ZAND_BANK' },
    { name: 'externalUserId', label: '外部用户ID', type: 'STRING', required: true, maxLen: 64, tip: 'FV_BANK 不校验该值' },
    { name: 'accountName', label: '账户姓名', type: 'STRING', required: true, maxLen: 64, tip: 'MOOLAHGO映射渠道bene_name; TAZAPAY必填，同时映射渠道 name(收款人名称, 始终上送); FV_BANK必填，映射渠道 Nickname' },
    { name: 'userType', label: '用户类型', type: 'ENUM', required: true, tip: 'INDIVIDUAL / BUSINESS / ADMINISTRATOR / ALL; MDAQ渠道必须是 BUSINESS; MOOLAHGO必须是 BUSINESS; TAZAPAY必须是 BUSINESS(仅B类账户); FV_BANK不校验(不使用)' },
    { name: 'bic', label: '银行编码', type: 'STRING', required: false, maxLen: 16, tip: 'EQUALS_MONEY/SGB/LEAN_TECH/KORAPAY/CVPAY/CALLPAY/DA5必填; BANK_FRICK仅SWIFT必填; PYVIO仅USD必填; TAZAPAY按走廊动态要求; DA5需符合SWIFT/BIC格式(8或11位字母数字)，会调用DA5机构接口校验有效性; MOOLAHGO条件必填(与swiftCode至少其一)，映射渠道bene_bank_code(LOC清算用); FV_BANK必填，传客户 Routing Number，映射渠道 Routing_Number' },
    { name: 'accountNumber', label: '卡号', type: 'STRING', required: false, maxLen: 64, tip: 'EQUALS_MONEY/SGB/LEAN_TECH/KORAPAY/PYVIO/CVPAY/CALLPAY/DA5/MOOLAHGO必填; BANK_FRICK/BANKING_CIRCLE仅bindType=BANKACCOUNT必填; TAZAPAY与iban至少其一必填; MOOLAHGO映射渠道bene_account_number; FV_BANK必填，映射渠道 Account_Number' },
    { name: 'bankName', label: '银行名字', type: 'STRING', required: true, maxLen: 64, tip: 'BANKING_CIRCLE仅bindType=BANKACCOUNT必填; DA5非必填; MOOLAHGO必填(落库本地,渠道靠bank_code/swift识别行); TAZAPAY按走廊动态要求(metadata要求时才必填/上送); FV_BANK非必填，传入时映射渠道 Additional_Info.Bank_Name; 其余渠道均必填' },
    { name: 'iban', label: 'IBAN', type: 'STRING', required: false, minLen: 15, maxLen: 34, tip: '@Size(min=15,max=34); LEAN_TECH必填; BANK_FRICK/BANKING_CIRCLE仅bindType=IBAN必填; TAZAPAY与accountNumber至少其一必填; ZAND_BANK必填(UAE IBAN格式AE+21位数字); DA5不使用; MOOLAHGO不使用; FV_BANK不使用' },
    { name: 'bankCountry', label: '银行国家', type: 'STRING', required: false, maxLen: 64, tip: 'EQUALS_MONEY/SGB必填(2位); LEAN_TECH必填(3位); PYVIO/BANKING_CIRCLE/DA5/MOOLAHGO必填(2位); TAZAPAY必填;MOOLAHGO映射渠道destination_country，须在CountryMapping映射表; FV_BANK非必填，传入时映射渠道 Additional_Info.Bank_Country' },
    { name: 'currency', label: '币种', type: 'STRING', required: true, maxLen: 16, tip: 'DA5仅支持PHP; MOOLAHGO触发币种条件校验(HKD→beneAddress必填); TAZAPAY必填; FV_BANK本期仅支持USD; 其他渠道按渠道支持币种校验' },
    { name: 'bindType', label: '账户类型', type: 'STRING', required: false, tip: 'BANK_FRICK/BANKING_CIRCLE必填: IBAN / BANKACCOUNT; TAZAPAY不使用; DA5不需要; MOOLAHGO非必填(仅BANKACCOUNT); FV_BANK不使用' },
    { name: 'extParam', label: '扩展参数', type: 'JSON_STRING', required: false, tip: 'EQUALS_MONEY/LEAN_TECH/BANK_FRICK/BANKING_CIRCLE/PYVIO/TAZAPAY/CVPAY/DA5/MOOLAHGO/FV_BANK必填，JSON格式字符串，见下方; DA5必填 provider 字段，仅允许 instapay 或 pesonet (大小写不敏感)，绑卡成功后回填 channelId(DA5机构ID); MOOLAHGO含bankCity(必填)及多个条件必填收款人字段; FV_BANK必填 BeneficiaryId(渠道控制台预建)+Account_Type(Saving/Checking)，可选 Payment_Type(本期固定 BUS_USD_Account.Domestic_Wire_BUS)/Bank_Address/Bank_City/Bank_State/Bank_Postal_Code(组装渠道 Additional_Info)' },
  ],
  DEPOSIT_CREATE: defineUniqueFields('DEPOSIT_CREATE', [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'externalUserId', label: '外部用户ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'externalOrderId', label: '外部订单号', type: 'STRING', required: true, maxLen: 256, tip: '幂等key' },
    { name: 'accountId', label: '账户ID', type: 'STRING', required: true, maxLen: 64, tip: 'DA5、PAYPAL非必填（PayPal 不校验中台侧开户记录，accountId 仅作上游用户标识透传）' },
    { name: 'currency', label: '币种', type: 'ENUM', required: true, _conditionalTip: true, tip: 'CurrencyEnum校验: USD / RUB / UAH / GBP / EUR / DKK / BRL / MXN / ARS / COP / AED / NGN / HKD / SGD / VND / ALL; DA5仅PHP; INSWITCH仅BRL或MXN; KORAPAY支持NGN(VA入金)及KES/XAF/XOF/EGP/TZS(Mobile Money Checkout入金)' },
    { name: 'amount', label: '金额', type: 'DECIMAL', required: true, tip: '正数，最大20位整数+2位小数; CVPAY最低100000' },
    { name: 'channelCode', label: '渠道代码', type: 'ENUM', required: true, tip: 'EQUALS_MONEY / PAGSMILE / SGB / PYVIO / LEAN_TECH / KORAPAY / PALMPAY / BANK_FRICK / BANKING_CIRCLE / INSWITCH / CVPAY / CALLPAY / DA5 / TRANSFI / AVENIA / PAYPAL / DLOCAL' },
    // 合并冲突只能保留一条 depositMethod：同名字段会在参数表重复展示，并使渠道规则散落在不同提示中。
    // 因此统一在单条提示中维护 KoraPay 与 TransFi 规则，同时保留条件提示标记，避免后续渠道筛选语义丢失。
    { name: 'depositMethod', label: '充值方式', type: 'ENUM', required: true, _conditionalTip: true, tip: 'VIRTUAL_ACCOUNT / PIX / VIETQR / ZEN_WALLET / INTERACE_REQUEST / LEAN / BANK_TRANSFER / SPEI / MOBILE_MONEY / PAYPAL_WALLET; PAYPAL仅PAYPAL_WALLET; CVPAY仅BANK_TRANSFER; CALLPAY仅BANK_TRANSFER; DA5仅DA5_WALLET; INSWITCH: BRL仅PIX、MXN仅SPEI; AVENIA仅BRL+PIX(动态码收款); KORAPAY: NGN仅VIRTUAL_ACCOUNT, KES/XAF/XOF/EGP/TZS仅MOBILE_MONEY; TRANSFI: CRYPTO_ONRAMP(onramp 法币→稳定币) / FIAT_PAYIN(payin 法币→法币本币，其余值均按 payin 处理)——bank_transfer 用 BANK_TRANSFER+extParam.bankCode，钱包类直接用钱包编码(如 GCASH/OVO/DANA/QRIS 等，大写)' },
    { name: 'callbackUrl', label: '回调地址', type: 'STRING', required: true, maxLen: 2048, tip: 'StrictCallbackUrl校验，allowHttp=true，不可为空' },
    { name: 'extParam', label: '扩展参数', type: 'JSON_STRING', required: false, tip: '各渠道差异字段，JSON格式字符串，见下方; CVPAY必填(buyerName/buyerPhone/buyerEmail/buyerIDType/buyerID); CALLPAY必填(内部字段均为可选); DA5无需传入; INSWITCH必填(documentType/documentNumber/email/phoneNumber + 地区字段); KORAPAY Mobile Money充值(KES/XAF/XOF/EGP/TZS)必填narration、customerEmail，可选customerName、redirectUrl' },
  ]),
  DEPOSIT_CLOSE: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'externalOrderId', label: '外部订单号', type: 'STRING', required: false, maxLen: 64, tip: '与orderId至少传一个' },
    { name: 'orderId', label: '订单号', type: 'STRING', required: false, maxLen: 64, tip: '与externalOrderId至少传一个' },
  ],
  DEPOSIT_CAPTURE: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'externalOrderId', label: '外部订单号', type: 'STRING', required: false, maxLen: 64, tip: '与orderId至少传一个' },
    { name: 'orderId', label: '订单号', type: 'STRING', required: false, maxLen: 64, tip: '与externalOrderId至少传一个' },
  ],
  WITHDRAW_CREATE: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'externalUserId', label: '外部用户ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'externalOrderId', label: '外部订单号', type: 'STRING', required: true, maxLen: 256, tip: '幂等key' },
    { name: 'currency', label: '币种', type: 'STRING', required: true, maxLen: 16, _conditionalTip: true, tip: 'EQUALS_MONEY仅USD; LEAN_TECH仅AED; INSWITCH仅BRL或MXN; CVPAY仅VND; CALLPAY仅ZAR; DA5仅PHP; ZAND_BANK仅AED; SGB需checkSupportCurrency; TAZAPAY为holdingCurrency(扣款币种); KORAPAY支持NGN(Bank Transfer)及KES/XAF/XOF/EGP/TZS(Mobile Money); FV_BANK本期仅支持USD; 其他渠道无特殊限制' },
    { name: 'amount', label: '金额', type: 'DECIMAL', required: true, tip: '正数，AmountValid校验; KORAPAY最低1000; CVPAY最低50000' },
    { name: 'channelCode', label: '渠道代码', type: 'ENUM', required: true, tip: 'EQUALS_MONEY / SGB / PYVIO / LEAN_TECH / KORAPAY / BANK_FRICK / BANKING_CIRCLE / INSWITCH / TAZAPAY / CVPAY / CALLPAY / DA5 / TRANSFI / MOOLAHGO / AVENIA / FV_BANK / ZAND_BANK / PAYPAL / DLOCAL' },
    { name: 'withdrawMethod', label: '提现方式', type: 'ENUM', required: true, tip: 'BANK_TRANSFER / PIX / SPEI / PIX_CODE / INTERAC / EFT / CRYPTO_OFFRAMP / PAYPAL_WALLET; PAYPAL仅支持PAYPAL_WALLET(收款人由extParam.receiver指定，只支持PayPal ID：13位大写字母数字); INSWITCH: BRL仅PIX、MXN仅SPEI; TAZAPAY仅支持BANK_TRANSFER; CVPAY仅支持BANK_TRANSFER; CALLPAY仅支持BANK_TRANSFER; DA5仅支持BANK_TRANSFER; MOOLAHGO仅支持BANK_TRANSFER; AVENIA仅BRL+PIX(必须先绑卡，extParam 只传 recipientId); FV_BANK仅支持BANK_TRANSFER(Send Bank Payment); ZAND_BANK仅支持BANK_TRANSFER(Domestic Transfer); DLOCAL仅PIX; TRANSFI: CRYPTO_OFFRAMP(offramp 稳定币→法币) / 其余值均为 payout(法币→法币本币,扣 prefunding)——bank_transfer 用 BANK_TRANSFER+extParam.bankCode，钱包类直接用钱包编码(如 GCASH/OVO/DANA/SHOPEEPAY 等，大写)' },
    { name: 'callbackUrl', label: '回调地址', type: 'STRING', required: true, maxLen: 2048, tip: '必须以http://或https://开头' },
    { name: 'accountId', label: '开户ID', type: 'STRING', required: false, maxLen: 64, tip: 'POBO场景传入，指定虚拟账户扣款；优先级: request.accountId > fiatCard.accountId' },
    { name: 'extParam', label: '扩展参数', type: 'JSON_STRING', required: true, tip: '所有渠道均需传入，JSON格式字符串，见下方; CVPAY/DA5需传recipientId; INSWITCH必填(documentType/documentNumber/email/phoneNumber + 地区字段); MOOLAHGO必填(recipientId+rateId，可选transferChannel/reason/sourceOfFunds/paymentDetails/sender*/product*); FV_BANK必填 recipientId(映射渠道 BeneficiaryPaymentInstrumentID=绑卡返回主键)+purpose(映射 Purpose)+cryptoBuySellActivity(Boolean，true/false，映射渠道 CryptoBuySellActivity=Yes/No)+supportingDocuments(出金附件 fileId 列表,1~3 个,映射 SupportingDocument 多个以 | 拼接,缺失在同步入口挡下不下单)，可选 description(映射 Description)/intermediaryAba(映射 Intermediary_ABA); ZAND_BANK必填 recipientId(绑卡返回的recipientId)+paymentPurposeCode(3位大写字母,IPI/FTS用途码)，可选remarks; DLOCAL必填 recipientId(绑卡返回的recipientId)+pixKeyType(EVP/CPF/CNPJ/EMAIL/PHONE)' },
  ],
  ACCOUNT_CLOSE: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'externalUserId', label: '外部用户ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'accountId', label: '账户ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'reason', label: '关户原因', type: 'STRING', required: false },
    { name: 'reasonCode', label: '关户原因编码', type: 'STRING', required: false, maxLen: 10, tip: 'BankingCircle: C01/C02/C03' },
  ],
  ACCOUNT_CLOSE_QUERY: [
    { name: 'accountId', label: '账户ID', type: 'STRING', required: true },
    { name: 'externalUserId', label: '外部用户ID', type: 'STRING', required: true },
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
  ],
  ACCOUNT_QUERY: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'accountId', label: '账户ID', type: 'STRING', required: true },
  ],
  CARD_QUERY: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'recipientId', label: '绑卡ID', type: 'STRING', required: true },
  ],
  CARD_UNBIND: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'recipientId', label: '绑卡ID', type: 'STRING', required: true },
    { name: 'channelCode', label: '渠道代码', type: 'ENUM', required: true, tip: 'BANK_FRICK' },
  ],
  DEPOSIT_QUERY: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'externalOrderId', label: '外部订单号', type: 'STRING', required: false, tip: '与orderId至少传一个' },
    { name: 'orderId', label: '订单号', type: 'STRING', required: false, tip: '与externalOrderId至少传一个' },
  ],
  REFUND_CREATE: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'externalUserId', label: '外部用户ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'externalOrderId', label: '外部退款订单号', type: 'STRING', required: true, maxLen: 256 },
    { name: 'depositOrderId', label: '充值订单号', type: 'STRING', required: true },
    { name: 'callbackUrl', label: '回调地址', type: 'STRING', required: true, maxLen: 2048, tip: 'StrictCallbackUrl校验，allowHttp=true，不可为空' },
    { name: 'description', label: '退款描述', type: 'STRING', required: false },
    { name: 'amount', label: '退款金额', type: 'DECIMAL', required: false, tip: '不传则全额退款' },
    { name: 'extParam', label: '扩展参数', type: 'JSON_STRING', required: false },
  ],
  REFUND_QUERY: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'externalOrderId', label: '外部退款订单号', type: 'STRING', required: false, tip: '与orderId至少传一个' },
    { name: 'orderId', label: '退款单号', type: 'STRING', required: false, tip: '与externalOrderId至少传一个' },
  ],
  WITHDRAW_QUERY: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'externalOrderId', label: '外部订单号', type: 'STRING', required: false, tip: '与orderId至少传一个' },
    { name: 'orderId', label: '订单号', type: 'STRING', required: false, tip: '与externalOrderId至少传一个' },
  ],
  CHANNEL_CONFIG_QUERY: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'channelCode', label: '渠道代码', type: 'STRING', required: true },
    { name: 'currency', label: '币种', type: 'STRING', required: false },
  ],
  ORDER_PAGE: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'channelCode', label: '渠道代码', type: 'STRING', required: true },
    { name: 'filters.currency', label: '币种', type: 'STRING', required: true },
    { name: 'filters.orderType', label: '交易类型', type: 'STRING', required: true, tip: 'DEPOSIT / WITHDRAW / REFUND' },
    { name: 'filters.orderStatusList', label: '订单状态', type: 'ARRAY', required: false, tip: '如 ["SUCCESS","FAILED"]' },
    { name: 'filters.externalUserId', label: '外部用户ID', type: 'STRING', required: false },
    { name: 'timeRange.startTime', label: '开始时间', type: 'LONG', required: true, tip: '毫秒时间戳，最大支持一周' },
    { name: 'timeRange.endTime', label: '结束时间', type: 'LONG', required: true, tip: '毫秒时间戳' },
    { name: 'pagination.page', label: '页码', type: 'INTEGER', required: true, tip: '最小值1' },
    { name: 'pagination.pageSize', label: '每页条数', type: 'INTEGER', required: true, tip: '1~100' },
  ],
  BANK_LIST: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'channelCode', label: '渠道代码', type: 'STRING', required: true },
    { name: 'provider', label: '收款机构渠道提供商', type: 'STRING', required: false, tip: 'DA5必填: instapay / pesonet；其他渠道不使用' },
    { name: 'country', label: '国家代码', type: 'STRING', required: false, tip: 'DA5不使用（固定PH）' },
    { name: 'currency', label: '币种', type: 'STRING', required: false, tip: 'DA5不使用（固定PHP）' },
  ],
  FILE_UPLOAD: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true },
    { name: 'channelCode', label: '渠道代码', type: 'STRING', required: true, tip: 'FV_BANK 本期仅支持出金附件上传' },
    { name: 'scene', label: '业务场景', type: 'STRING', required: true, tip: 'ACCOUNT_OPEN / DEPOSIT / WITHDRAW / CARD_BIND / REFUND; FV_BANK 仅支持 WITHDRAW(出金附件)' },
    { name: 'documentType', label: '文件类型', type: 'STRING', required: true, tip: '通用: ID_CARD / PASSPORT / DRIVER_LICENSE / SELFIE / ADDRESS_PROOF 等；WITHDRAW 场景标准枚举: SOURCE_OF_FUNDS / SOURCE_OF_WEALTH / BANK_STATEMENT / AUTHORIZATION_LETTER；MDAQ: bank_statement / company_proof / company_address_proof / ktp / national_identity / nric / passport / phone_bill / tax_bill / utility_bill / work_permit；FV_BANK 不区分 documentType，统一映射渠道 customField=Payment_Invoice(真机枚举 Payment_Invoice/Document_File/Front_Document/Back_Document)' },
    { name: 'bizId', label: '业务ID', type: 'STRING', required: true, tip: 'accountId / depositId / withdrawId; FV_BANK 传 withdrawId' },
    { name: 'externalUserId', label: '外部用户ID', type: 'STRING', required: true},
    { name: 'file', label: '文件', type: 'FILE', required: true, tip: 'jpg/png/pdf，最大10MB(平台约束); FV_BANK 渠道侧硬约束: ≤5MB 且 fileName≤100字符(超长截尾); Filenames can only contain letters, numbers, dots (.), underscores (_), and hyphens (-).' },
    { name: 'documentCountry', label: '文件国家', type: 'STRING', required: false },
    { name: 'documentSide', label: '文件面', type: 'STRING', required: false, tip: 'FRONT / BACK; FV_BANK 不使用' },
  ],
  FILE_STATUS_QUERY: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true },
    { name: 'fileIds', label: '文件ID列表', type: 'ARRAY', required: true, tip: '@NotEmpty, @Size(max=50)' },
  ],
  USER_CREATE: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'operateType', label: '操作类型', type: 'STRING', required: false, tip: 'CREATE / UPDATE; MDAQ不校验该字段，关键看用户状态是否可编辑(非SUCCESS/PROCESSING); MOOLAHGO必填(CREATE/UPDATE)' },
    { name: 'channelCode', label: '渠道代码', type: 'ENUM', required: true, tip: 'PYVIO / TAZAPAY / MDAQ / TRANSFI / MOOLAHGO / AVENIA / DLOCAL' },
    { name: 'externalUserId', label: '外部用户ID', type: 'STRING', required: true, maxLen: 64, tip: 'MOOLAHGO关联文件(merchant_id)与出金sender_user_id; AVENIA 关联 subAccountId(落 fiat_channel_user.channelUserId)，Payin/Payout 下单据此定位' },
    { name: 'userType', label: '用户类型', type: 'STRING', required: false, tip: 'INDIVIDUAL / BUSINESS; TAZAPAY仅支持BUSINESS; MDAQ仅支持BUSINESS(本期仅KYB); TRANSFI仅支持INDIVIDUAL; MOOLAHGO仅支持INDIVIDUAL(KYC仅个人,BUSINESS直接拒); AVENIA仅支持BUSINESS(本期仅KYB)' },
    { name: 'currency', label: '币种', type: 'STRING', required: false, tip: 'MOOLAHGO非必填,KYC无币种维度,中台固定用HKD; AVENIA固定BRL; 其他渠道不使用' },
    { name: 'extParam', label: '扩展参数', type: 'JSON_STRING', required: false, tip: 'PYVIO/TAZAPAY/MDAQ/TRANSFI/MOOLAHGO/AVENIA渠道必填，JSON格式字符串，见下方渠道差异字段; MDAQ含companyInfo(必填)及cddInfo(非必填); TRANSFI含个人信息(firstName/lastName/date/email/phone/phoneCode/country/address)及必填sumsubToken(Sumsub共享KYC token,缺失前置拦截); MOOLAHGO含KYC个人信息(全部必填); AVENIA含companyInfo(必填,含address与企业文档attachments)+uboList(必填,≤50,至少一个合法hasControl角色且年龄≥18)+可选sandboxReject(仅非生产透传)' },
  ],
  // 提交补充资料：审核 requires_action 后补件，仅 TAZAPAY 支持。字段/文件处理与创建用户一致，
  // 补充文件明细放在 extParam.documents 中；补件不走 update-entity，而是「上传文件 + Add Document 挂到 entity」等待复审。
  USER_SUPPLEMENT: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'channelCode', label: '渠道代码', type: 'ENUM', required: true, tip: '仅 TAZAPAY 支持；其它渠道调此接口会抛 UNSUPPORTED_OPERATION' },
    { name: 'externalUserId', label: '外部用户ID', type: 'STRING', required: true, maxLen: 64, tip: '配合 channelCode + businessPartner 定位待补件的渠道用户（须为审核 requires_action 后置为 FAILED 的用户）' },
    { name: 'userType', label: '用户类型', type: 'STRING', required: false, tip: '与创建用户一致，可选；TAZAPAY 仅支持 BUSINESS' },
    { name: 'extParam', label: '扩展参数', type: 'JSON_STRING', required: true, tip: 'JSON字符串，仅含 documents 数组；documents 为空则抛 MISSING_PARAMETER。子字段见下方' },
    // 请求参数区不渲染 children，故用「extParam.documents[].xxx」扁平命名 + _isChild 缩进逐项展开，
    // 便于业务方直接照抄补件报文，不必再回头翻创建用户文档。
    { name: 'extParam.documents', label: '补充文件清单', type: 'ARRAY', required: true, _isChild: true, tip: '待补充的文件数组，不能为空；需要补多个类型时，必须每个类型都上传，渠道状态才会从 Requires Action 变为 Submitted' },
    { name: 'extParam.documents[].fileType', label: '文件类型', type: 'ENUM', required: true, _isChild: true, tip: '映射渠道 documents.type，取值范围与创建用户一致（须为 TazapayDocumentTypeEnum 合法值，非法抛 INVALID_PARAMETER）；须与后台审核要求补充的资料类型一致，否则渠道状态不会从 Requires Action 变为 Submitted' },
    { name: 'extParam.documents[].fileId', label: '文件地址', type: 'STRING', required: true, _isChild: true, tip: '业务方 S3 pre-signed 文件地址，与创建用户 fileId 一致；补件时会真实下载并上传给渠道，必须可下载' },
    { name: 'extParam.documents[].name', label: '文件名', type: 'STRING', required: false, _isChild: true, tip: '透传渠道 name' },
    { name: 'extParam.documents[].describe', label: '文件描述', type: 'STRING', required: false, _isChild: true, tip: '透传渠道 description' },
  ],
  USER_QUERY: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'userId', label: '用户ID', type: 'STRING', required: false },
    { name: 'externalUserId', label: '外部用户ID', type: 'STRING', required: false },
  ],
  BALANCE_QUERY: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: false },
    { name: 'channelCode', label: '渠道代码', type: 'ENUM', required: true, tip: 'EQUALS_MONEY / PAGSMILE / SGB / PYVIO / LEAN_TECH / KORAPAY / PALMPAY / BANK_FRICK / BANKING_CIRCLE / INSWITCH / CVPAY / CALLPAY / TRANSFI / AVENIA / ZAND_BANK / DLOCAL' },
    { name: 'currency', label: '币种', type: 'STRING', required: true, tip: 'Service层checkParams校验; TRANSFI 支持 GHS/IDR/KES/PHP/TZS/UGX/VND/XOF/ZMW，不传或传 ALL 返回全部币种底仓; AVENIA 仅 BRL(底层为 BRLA 余额，1:1 对外呈现 BRL); DLOCAL仅BRL' },
    { name: 'channelAccountNo', label: '渠道账户号', type: 'STRING', required: false, tip: '渠道有多个账号时可指定; TRANSFI 不返回该字段（MID 维度，账户由凭证隐式确定）; AVENIA 为 subAccountId 维度' },
  ],
  RATE_QUERY: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'channelCode', label: '渠道代码', type: 'ENUM', required: true, tip: 'PYVIO / TAZAPAY / MDAQ / TRANSFI / MOOLAHGO' },
    { name: 'originCurrency', label: '原始币种', type: 'STRING', required: true },
    { name: 'targetCurrency', label: '目标币种', type: 'STRING', required: true },
    { name: 'orderType', label: '订单类型', type: 'STRING', required: false, tip: 'TRANSFI必填: onramp(法币→稳定币) / offramp(稳定币→法币)，决定报价方向' },
    { name: 'externalUserId', label: '外部用户ID', type: 'STRING', required: false, tip: 'MDAQ/MOOLAHGO必填' },
    { name: 'payeeType', label: '收款人类型', type: 'STRING', required: false, tip: 'PYVIO必填: BENEFICIARY / SUPPLIER; TAZAPAY必填: local / swift / local_payment_network / wallet / tazapay_account; MDAQ必填; TRANSFI映射为渠道paymentType: bank_transfer / local_wallet / card / DEFAULT; MOOLAHGO必填: BENEFICIARY' },
    { name: 'userId', label: '用户ID', type: 'STRING', required: false, tip: 'PYVIO: payeeType=BENEFICIARY时必填' },
    { name: 'destinationCountry', label: '目标国家', type: 'STRING', required: false, tip: 'TAZAPAY必填: ISO 3166-1 alpha-2 国家码，如 NG、DE、IN' },
    { name: 'amount', label: '出金金额(分)', type: 'LONG', required: false, tip: 'TAZAPAY必填: 单位为分，如 10000 表示 100.00; TRANSFI必填: 报价金额' },
    { name: 'localPaymentNetworkType', label: '本地支付网络类型', type: 'STRING', required: false, tip: 'TAZAPAY: payeeType=local_payment_network 时必填，如 pix_brl' },
    { name: 'fundTransferNetwork', label: '资金转账网络', type: 'STRING', required: false, tip: 'TAZAPAY: payeeType=local 时可选，如 sepa / fedach / fedwire; TRANSFI映射为paymentCode: onramp用源法币支付编码(如gcash)、offramp用目标法币出款编码(如bpi)' },
    { name: 'beneficiaryId', label: '收款人 ID', type: 'STRING', required: false, tip: 'MDAQ必填：值为绑定银行查询返回字段：channelRecipientId' },
    { name: 'purposeOfTransfer', label: '转账目的代码', type: 'STRING', required: false, tip: 'MDAQ必填: 如 INTC' },
    { name: 'transferChannel', label: '转账渠道', type: 'STRING', required: false, tip: 'MOOLAHGO必填: LOC(本地清算) / SHA(SWIFT共担) / OUR(付款方承担) / BEN(收款方承担)' },
  ],
  MMO_LIST: [
    { name: 'channelCode', label: '渠道代码', type: 'STRING', required: true, tip: 'KORAPAY' },
    { name: 'currency', label: '币种', type: 'STRING', required: true, tip: 'KES / XAF / XOF / EGP / TZS' },
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
  ],
  PAYOUT_REQUIREMENTS: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'channelCode', label: '渠道代码', type: 'ENUM', required: true, tip: 'TAZAPAY / MDAQ' },
    { name: 'country', label: '目标国家', type: 'STRING', required: true, tip: 'ISO 3166-1 alpha-2，如 US / IN / HK / GB' },
    { name: 'currency', label: '目标币种', type: 'STRING', required: true, tip: 'ISO 4217，如 USD / INR / HKD / GBP' },
  ],
  REMITTER_CREATE: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'externalUserId', label: '外部用户ID', type: 'STRING', required: true },
    { name: 'channelCode', label: '渠道代码', type: 'ENUM', required: true, tip: 'CORPAY' },
    { name: 'classification', label: '分类', type: 'ENUM', required: true, tip: 'individual / corporation' },
    { name: 'name', label: '付款人姓名/公司名', type: 'STRING', required: true },
    { name: 'remitterId', label: '付款人ID', type: 'STRING', required: false, tip: '可选，不传则自动生成' },
    { name: 'dateOfBirth', label: '出生日期', type: 'STRING', required: false, tip: 'yyyy-MM-dd，个人(individual)时渠道要求必填' },
    { name: 'country', label: '国家', type: 'STRING', required: false, tip: 'ISO2 国家代码' },
    { name: 'addressLine1', label: '地址行1', type: 'STRING', required: false },
    { name: 'addressLine2', label: '地址行2', type: 'STRING', required: false },
    { name: 'city', label: '城市', type: 'STRING', required: false },
    { name: 'province', label: '省/州', type: 'STRING', required: false },
    { name: 'postalCode', label: '邮编', type: 'STRING', required: false },
    { name: 'email', label: '邮箱', type: 'STRING', required: false },
    { name: 'phone', label: '电话', type: 'STRING', required: false },
    { name: 'bankName', label: '银行名称', type: 'STRING', required: false },
    { name: 'bankCode', label: '银行代码', type: 'STRING', required: false },
    { name: 'accountNumber', label: '银行账号', type: 'STRING', required: false },
    { name: 'extParam', label: '扩展参数', type: 'JSON_STRING', required: false, tip: 'JSON 格式，企业可传 incorporationNumber/incorporationType 等' },
  ],
  REMITTER_QUERY: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'remitterId', label: '付款人ID', type: 'STRING', required: true },
    { name: 'channelCode', label: '渠道代码', type: 'ENUM', required: false, tip: 'CORPAY' },
  ],
  FUND_TRANSFER_CREATE: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'channelCode', label: '渠道代码', type: 'ENUM', required: true, tip: 'BANKING_CIRCLE / SGB' },
    { name: 'externalUserId', label: '外部用户ID', type: 'STRING', required: true },
    { name: 'externalOrderId', label: '外部订单ID', type: 'STRING', required: true, tip: '幂等键' },
    { name: 'currency', label: '币种', type: 'STRING', required: true },
    { name: 'amount', label: '金额', type: 'NUMBER', required: true },
    { name: 'transferMethod', label: '调拨方式', type: 'ENUM', required: false, tip: '默认 BANK_TRANSFER' },
    { name: 'callbackUrl', label: '回调地址', type: 'STRING', required: false },
    { name: 'purpose', label: '用途', type: 'STRING', required: false },
    { name: 'description', label: '描述', type: 'STRING', required: false },
    { name: 'chargeType', label: '费用承担方', type: 'ENUM', required: false, tip: 'BEN / OUR / SHA' },
    { name: 'extParam', label: '扩展参数', type: 'JSON_STRING', required: true, tip: 'JSON 格式，包含 senderAccount 和 beneficiaryId 或 beneficiaryAccount（二选一）', children: [
      { name: 'senderAccount', label: '出金方账户（必填）', type: 'OBJECT', required: true, children: [
        { name: 'bankCode', label: '银行编码(BIC/SwiftCode)', type: 'STRING', required: true },
        { name: 'bankAccountNo', label: '银行账号', type: 'STRING', required: false, tip: '不传则从渠道配置获取' },
        { name: 'bankAccountName', label: '银行账户名', type: 'STRING', required: false },
        { name: 'bankCountry', label: '银行国家', type: 'STRING', required: false },
      ] },
      { name: 'beneficiaryId', label: '收款方ID', type: 'STRING', required: false, tip: '与 beneficiaryAccount 二选一，已创建收款人时填此字段' },
      { name: 'beneficiaryAccount', label: '收款方账户', type: 'OBJECT', required: false, tip: '与 beneficiaryId 二选一', children: [
        { name: 'bankAccountNo', label: '银行账号', type: 'STRING', required: true },
        { name: 'bankAccountName', label: '银行账户名', type: 'STRING', required: true },
        { name: 'bankCountry', label: '银行国家', type: 'STRING', required: true },
        { name: 'bankCode', label: '银行编码(BIC/SwiftCode)', type: 'STRING', required: true },
        { name: 'beneficiaryCountry', label: '收款人国家', type: 'STRING', required: true },
        { name: 'beneficiaryCity', label: '收款人城市', type: 'STRING', required: true },
        { name: 'beneficiaryAddress1', label: '收款人地址1', type: 'STRING', required: true },
        { name: 'beneficiaryAddress2', label: '收款人地址2', type: 'STRING', required: false },
        { name: 'beneficiaryTown', label: '收款人城镇', type: 'STRING', required: false, tip: 'BankingCircle 必填' },
        { name: 'bankState', label: '银行州/省', type: 'STRING', required: false },
        { name: 'bankCity', label: '银行城市', type: 'STRING', required: false },
        { name: 'bankPostCode', label: '银行邮编', type: 'STRING', required: false },
        { name: 'bankAddress', label: '银行地址', type: 'STRING', required: false },
      ] },
      { name: 'purpose', label: '转账用途', type: 'STRING', required: false },
      { name: 'description', label: '转账备注', type: 'STRING', required: false },
      { name: 'chargeType', label: '费用类型', type: 'STRING', required: false, tip: 'BEN / OUR / SHA，默认 OUR' },
      { name: 'transferMethod', label: '转账方式', type: 'STRING', required: false },
    ] },
  ],
  FUND_TRANSFER_QUERY: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: false },
    { name: 'externalOrderId', label: '外部订单ID', type: 'STRING', required: false, tip: '与 orderId 至少传一个' },
    { name: 'orderId', label: '平台订单ID', type: 'STRING', required: false, tip: '与 externalOrderId 至少传一个' },
    { name: 'channelCode', label: '渠道代码', type: 'ENUM', required: false, tip: 'BANKING_CIRCLE / SGB' },
  ],
  CHANNEL_TRANSACTIONS: [
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
    { name: 'channelCode', label: '渠道代码', type: 'ENUM', required: true, tip: 'BANKING_CIRCLE / SGB' },
    { name: 'channelAccountNo', label: '渠道账号', type: 'STRING', required: true, tip: '渠道侧账户编号' },
    { name: 'currency', label: '币种', type: 'STRING', required: true },
    { name: 'startDate', label: '开始日期', type: 'STRING', required: true, tip: 'yyyyMMdd 格式' },
    { name: 'endDate', label: '结束日期', type: 'STRING', required: true, tip: 'yyyyMMdd 格式' },
    { name: 'pageIndex', label: '页码', type: 'NUMBER', required: false, tip: '默认 1' },
    { name: 'pageSize', label: '每页条数', type: 'NUMBER', required: false, tip: '默认 50' },
  ],
};

// 各接口的响应参数定义（data 部分）
const RESPONSE_PARAMS = {
  ACCOUNT_CREATE: [
    { name: 'accountId', label: '账户ID', type: 'STRING', required: true },
    { name: 'status', label: '开户状态', type: 'STRING', required: true, tip: 'PROCESSING / ACTIVE / FAILED / CLOSED / DENIED' },
  ],
  ACCOUNT_CLOSE: [
    { name: 'accountId', label: '账户ID', type: 'STRING', required: true },
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true },
    { name: 'status', label: '关户状态', type: 'STRING', required: true, tip: 'SUCCESS / PROCESSING / FAILED' },
    { name: 'errorCode', label: '错误码', type: 'STRING', required: false },
    { name: 'errorMsg', label: '错误信息', type: 'STRING', required: false },
  ],
  ACCOUNT_CLOSE_QUERY: [
    { name: 'accountId', label: '账户ID', type: 'STRING', required: true },
    { name: 'requestId', label: '请求ID', type: 'STRING', required: true },
    { name: 'status', label: '关户状态', type: 'STRING', required: true, tip: 'SUCCESS / PROCESSING / FAILED' },
    { name: 'errorCode', label: '错误码', type: 'STRING', required: false },
    { name: 'errorMsg', label: '错误信息', type: 'STRING', required: false },
  ],
  ACCOUNT_QUERY: [
    { name: 'accountId', label: '账户ID', type: 'STRING', required: true },
    { name: 'status', label: '开户状态', type: 'STRING', required: true, tip: 'PROCESSING / ACTIVE / FAILED / CLOSED / DENIED' },
    { name: 'subStatus', label: '开户子状态', type: 'STRING', required: true, tip: 'INIT / PRE_CHECKING / CREATING_CHANNEL / WAITING_CONNECT / WAITING_ENTITY_CALLBACK / WAITING_CREATE_BENEFICIARY / AWAITING_BENEFICIARY_COOL_OFF / SUCCESS / FAILED' },
    { name: 'externalUserId', label: '业务线用户ID', type: 'STRING', required: true },
    { name: 'channelCode', label: '开户渠道', type: 'STRING', required: true },
    { name: 'paymentMethod', label: '支付方式', type: 'STRING', required: true },
    { name: 'currency', label: '开户币种', type: 'STRING', required: true },
    { name: 'redirectUrl', label: '跳转链接', type: 'STRING', required: false, tip: '开户需要跳转外部链接时返回' },
    { name: 'errorCode', label: '错误码', type: 'STRING', required: false, tip: '开户失败时返回' },
    { name: 'errorMsg', label: '错误信息', type: 'STRING', required: false, tip: '开户失败时返回' },
    { name: 'createTime', label: '创建时间', type: 'LONG', required: true },
    { name: 'updateTime', label: '最后更新时间', type: 'LONG', required: true },
    { name: 'accountDetail', label: '账户详情', type: 'OBJECT', required: false, tip: '开户成功时返回', children: [
      { name: 'accountName', label: '账户名', type: 'STRING', required: false },
      { name: 'accountNumber', label: '账户号码', type: 'STRING', required: true, tip: 'IBAN 或 accountNumber' },
      { name: 'bic', label: 'SWIFT Code', type: 'STRING', required: false, tip: 'Bank Identifier Code (BIC)' },
      { name: 'bankName', label: '银行名称', type: 'STRING', required: false },
      { name: 'bankCountry', label: '银行所在国家', type: 'STRING', required: false },
      { name: 'bankAddress', label: '银行地址', type: 'STRING', required: false },
      { name: 'bankBranch', label: '分行名称', type: 'STRING', required: false, tip: 'MOOLAHGO返回(按币种回填,取自channel_ext_info.bankBranch);其他渠道一般不返回' },
        { name: 'beneficiaryVaNo', label: '受益人VA号', type: 'STRING', required: false },
    ] },
    { name: 'userType', label: '用户类型', type: 'STRING', required: true, tip: 'INDIVIDUAL / BUSINESS' },
    { name: 'extParam', label: '扩展参数', type: 'JSON_STRING', required: false, tip: '根据渠道返回不同字段', children: [
      { name: 'customerId', label: '渠道用户ID', type: 'STRING', required: true, _channel: 'LEAN_TECH' },
      { name: 'accessToken', label: 'Access Token', type: 'STRING', required: true, _channel: 'LEAN_TECH' },
      { name: 'channelAppToken', label: 'Channel App Token', type: 'STRING', required: true, _channel: 'LEAN_TECH' },
      { name: 'paymentDestinationId', label: '支付目的地ID', type: 'STRING', required: true, _channel: 'LEAN_TECH' },
      { name: 'fullNameRetrieved', label: '银行获取的用户全名', type: 'STRING', required: false, _channel: 'LEAN_TECH' },
      { name: 'nameMatchType', label: '同名校验结果', type: 'STRING', required: false, _channel: 'LEAN_TECH', tip: 'PERFECT_MATCH / PARTIAL_MATCH / NO_MATCH / RECONNECT_REQUIRED / NO_RESULT' },
      { name: 'vibans', label: '多个IBAN', type: 'ARRAY', required: true, _channel: 'BANKING_CIRCLE' },
      // TAZAPAY 线下开户：渠道额外信息均放在 extParam（JSON 字符串），含 metadata / currencies / virtual_account.*
      { name: 'metadata', label: '渠道元数据', type: 'OBJECT', required: false, _channel: 'TAZAPAY', tip: '线下开户透传的渠道元数据对象，可能为空对象 {}' },
      { name: 'currencies', label: '支持币种列表', type: 'ARRAY', required: false, _channel: 'TAZAPAY', tip: '该虚拟账户支持的币种，如 ["USD"]' },
      { name: 'virtual_account', label: '虚拟账户信息', type: 'OBJECT', required: false, _channel: 'TAZAPAY', tip: '虚拟账户对象，下列为其内部字段' },
      { name: 'virtual_account.id', label: '虚拟账户ID', type: 'STRING', required: false, _channel: 'TAZAPAY' },
      { name: 'virtual_account.account_holder_name', label: '虚拟账户持有人名称', type: 'STRING', required: false, _channel: 'TAZAPAY' },
      { name: 'virtual_account.account_number', label: '虚拟账户号', type: 'STRING', required: false, _channel: 'TAZAPAY' },
      { name: 'virtual_account.bank_name', label: '开户银行名称', type: 'STRING', required: false, _channel: 'TAZAPAY' },
      { name: 'virtual_account.iban', label: 'IBAN', type: 'STRING', required: false, _channel: 'TAZAPAY', tip: '部分币种/地区返回' },
      { name: 'virtual_account.bank_codes', label: '银行编码', type: 'OBJECT', required: false, _channel: 'TAZAPAY', tip: '含 swift_code、aba' },
      { name: 'virtual_account.currencies', label: '虚拟账户支持币种', type: 'ARRAY', required: false, _channel: 'TAZAPAY' },
    ] },
  ],
  CARD_BIND: [
    { name: 'recipientId', label: '绑卡ID', type: 'STRING', required: true },
    { name: 'status', label: '绑卡状态', type: 'STRING', required: true, tip: 'PROCESSING / SUCCESS / FAILED' },
  ],
  CARD_QUERY: [
    { name: 'recipientId', label: '绑定银行账户ID', type: 'STRING', required: true },
    { name: 'status', label: '状态', type: 'STRING', required: true, tip: 'PROCESSING / SUCCESS / FAILED' },
    { name: 'channelCode', label: '渠道编码', type: 'STRING', required: true },
    { name: 'externalUserId', label: '外部用户ID', type: 'STRING', required: false },
    { name: 'accountName', label: '账户名称', type: 'STRING', required: true },
    { name: 'accountNumber', label: '账户号码', type: 'STRING', required: true },
    { name: 'bic', label: 'BIC/SWIFT', type: 'STRING', required: false },
    { name: 'bankName', label: '银行名称', type: 'STRING', required: false },
    { name: 'currency', label: '币种', type: 'STRING', required: true },
    { name: 'city', label: '城市', type: 'STRING', required: false },
    { name: 'country', label: '国家', type: 'STRING', required: false },
    { name: 'address', label: '地址', type: 'STRING', required: false },
    { name: 'bankCity', label: '银行城市', type: 'STRING', required: false },
    { name: 'bankCountry', label: '银行国家', type: 'STRING', required: false },
    { name: 'bankAddress', label: '银行地址', type: 'STRING', required: false },
    { name: 'errorCode', label: '错误码', type: 'STRING', required: false },
    { name: 'errorMsg', label: '错误信息', type: 'STRING', required: false },
    { name: 'availableChannels', label: '可用支付渠道列表', type: 'ARRAY', required: false, tip: 'MDAQ渠道必填返回', children: [
      { name: 'settlementChannel', label: '结算渠道类型', type: 'STRING', required: false, tip: '如 LOCAL / SWIFT-SHA / SWIFT-OUR' },
      { name: 'limit', label: '单笔限额', type: 'NUMBER', required: false, tip: 'null 表示无限制' },
      { name: 'splitSettlement', label: '是否支持拆分结算', type: 'BOOLEAN', required: false },
    ] },
  ],
  CARD_UNBIND: [
    { name: 'recipientId', label: '绑卡ID', type: 'STRING', required: true },
    { name: 'status', label: '状态', type: 'STRING', required: true, tip: 'SUCCESS: 解绑成功' },
  ],
  DEPOSIT_CREATE: [
    { name: 'orderId', label: '订单号', type: 'STRING', required: true },
    { name: 'status', label: '状态', type: 'STRING', required: false, tip: 'PROCESSING，最终状态通过webhook回调' },
    { name: 'externalOrderId', label: '上游订单号', type: 'STRING', required: true },
  ],
  DEPOSIT_QUERY: [
    { name: 'externalOrderId', label: '业务线订单号', type: 'STRING', required: false },
    { name: 'orderId', label: '订单号', type: 'STRING', required: true },
    { name: 'status', label: '充值单状态', type: 'STRING', required: true, tip: 'PROCESSING / SUCCESS / FAILED' },
    { name: 'subStatus', label: '充值子状态', type: 'STRING', required: true, tip: 'CREATED / CREATE_CALLBACK_WAIT / SUCCESS / FAILED' },
    { name: 'refundStatus', label: '退款状态', type: 'STRING', required: true, tip: 'NOT_REFUNDED / REFUNDING / REFUNDED / REFUND_FAILED' },
    { name: 'channelCode', label: '订单使用渠道', type: 'STRING', required: true },
    { name: 'accountId', label: '渠道开户账户ID', type: 'STRING', required: false },
    { name: 'depositMethod', label: '支付方式', type: 'STRING', required: true, tip: 'VIRTUAL_ACCOUNT / PIX / VIETQR / ZEN_WALLET / INTERACE_REQUEST / DA5_WALLET' },
    { name: 'currency', label: '支付币种', type: 'STRING', required: true },
    { name: 'amount', label: '订单金额', type: 'DECIMAL', required: true },
    { name: 'fee', label: '手续费', type: 'DECIMAL', required: true },
    { name: 'errorCode', label: '错误码', type: 'STRING', required: false },
    { name: 'errorMsg', label: '错误信息', type: 'STRING', required: false },
    { name: 'completedTime', label: '完成时间', type: 'LONG', required: false },
    { name: 'createTime', label: '创建时间', type: 'LONG', required: true },
    { name: 'updateTime', label: '更新时间', type: 'LONG', required: false },
    { name: 'remark', label: '备注', type: 'STRING', required: false },
    { name: 'paymentInfo', label: '支付信息', type: 'OBJECT', required: false, tip: '非VA模式充值时返回；DA5同时返回checkoutUrl(payUrl)和qrcode(barCode)，前端二选一展示', children: [
      { name: 'payType', label: '类型', type: 'STRING', required: true, tip: 'URL / QRCODE / BARCODE / AUTHORIZATION_ID; DA5 取值 URL（仅 checkoutUrl）或 BARCODE（含 qrcode 时优先）' },
      { name: 'payUrl', label: '支付链接', type: 'STRING', required: false, tip: 'DA5 渠道返回 DA5 托管的 checkout 页面 URL' },
      { name: 'barCode', label: '二维码', type: 'STRING', required: false, tip: 'DA5 渠道返回 base64 编码的 PNG QR Code（data:image/png;base64,...），前端可直接渲染为图片' },
      { name: 'authorizationId', label: '授权ID', type: 'STRING', required: false, tip: 'payType=AUTHORIZATION_ID 时有值' },
      { name: 'authorizationToken', label: '授权Token', type: 'STRING', required: false, tip: 'payType=AUTHORIZATION_ID 时有值' },
      { name: 'authorizationAppToken', label: '前端授权Token', type: 'STRING', required: false, tip: 'payType=AUTHORIZATION_ID 时有值' },
      { name: 'connectType', label: '前端链接方式', type: 'STRING', required: false, tip: 'payType=AUTHORIZATION_ID 时有值; REVERSE_ENGINEERING / OPEN_BANKING' },
    ] },
    { name: 'senderInfo', label: '付款人信息', type: 'OBJECT', required: false, tip: 'VA模式充值时返回（BANK_FRICK）', children: [
      { name: 'senderAcctNo', label: '发送方账户', type: 'STRING', required: false },
      { name: 'senderAcctName', label: '发送方账户名称', type: 'STRING', required: false },
      { name: 'senderBic', label: 'SWIFT/BIC Code', type: 'STRING', required: false },
      { name: 'senderAddress', label: '发送方地址', type: 'STRING', required: false },
      { name: 'senderCountryCode', label: '发送方国家代码', type: 'STRING', required: false },
      { name: 'purpose', label: '充值意图', type: 'STRING', required: false },
      { name: 'remarks', label: '备注', type: 'STRING', required: false },
      { name: 'tranTime', label: '转账时间', type: 'LONG', required: false },
      { name: 'iban', label: 'IBAN', type: 'STRING', required: false },
      { name: 'vban', label: 'VBAN', type: 'STRING', required: false },
      { name: 'bankName', label: '银行名称', type: 'STRING', required: false },
      { name: 'senderFirstName', label: '发送方名', type: 'STRING', required: false, tip: 'TRANSFI 等渠道返回付款人名' },
      { name: 'senderLastName', label: '发送方姓', type: 'STRING', required: false, tip: 'TRANSFI 等渠道返回付款人姓' },
      { name: 'senderDocumentNumber', label: '发送方证件号', type: 'STRING', required: false },
      { name: 'senderDocumentType', label: '发送方证件类型', type: 'STRING', required: false },
      { name: 'senderNetwork', label: '付款网络类型', type: 'STRING', required: false, tip: 'SWIFT / LOCAL' },
    ] },
    { name: 'receiverInfo', label: '收款人信息', type: 'OBJECT', required: false, tip: 'VA模式充值时返回（BANK_FRICK）', children: [
      { name: 'holderName', label: '持有人名称', type: 'STRING', required: false },
      { name: 'bankName', label: '银行名称', type: 'STRING', required: false },
      { name: 'iban', label: 'IBAN', type: 'STRING', required: false },
      { name: 'vban', label: 'VBAN', type: 'STRING', required: false },
      { name: 'bic', label: 'BIC', type: 'STRING', required: false },
      { name: 'accountNumber', label: '账号', type: 'STRING', required: false },
      { name: 'network', label: '网络类型', type: 'STRING', required: false, tip: 'SWIFT / LOCAL' },
    ] },
    { name: 'extParams', label: '渠道扩展信息', type: 'JSON_STRING', required: false, tip: '渠道返回的额外信息，JSON格式', children: [
      { name: 'channelOrderId', label: '渠道订单ID', type: 'STRING', required: true, _channel: 'BANK_FRICK' },
    ] },
  ],
  DEPOSIT_CLOSE: [
    { name: 'orderId', label: '订单号', type: 'STRING', required: true },
    { name: 'externalOrderId', label: '外部订单号', type: 'STRING', required: true },
    { name: 'state', label: '订单状态', type: 'STRING', required: true, tip: 'FAILED(关单成功) / 原状态(关单失败)' },
    { name: 'closeResult', label: '关单结果', type: 'STRING', required: true, tip: 'SUCCESS / PROCESSING / FAILED' },
    { name: 'closeMessage', label: '关单描述', type: 'STRING', required: false },
  ],
  REFUND_CREATE: [
    { name: 'orderId', label: '退款单号', type: 'STRING', required: true },
  ],
  REFUND_QUERY: [
    { name: 'externalOrderId', label: '外部退款单号', type: 'STRING', required: true },
    { name: 'orderId', label: '退款单号', type: 'STRING', required: true },
    { name: 'status', label: '退款状态', type: 'STRING', required: true, tip: 'INIT / PENDING / SUCCESS / FAILED' },
    { name: 'errorCode', label: '错误码', type: 'STRING', required: false },
    { name: 'errorMsg', label: '错误信息', type: 'STRING', required: false },
    { name: 'refundReason', label: '退款原因', type: 'STRING', required: false },
    { name: 'remark', label: '备注', type: 'STRING', required: false },
    { name: 'amount', label: '原订单金额', type: 'DECIMAL', required: true },
    { name: 'arrivalAmount', label: '用户退款到账金额', type: 'DECIMAL', required: false },
    { name: 'currency', label: '币种', type: 'STRING', required: true },
    { name: 'channelCode', label: '渠道', type: 'STRING', required: true },
    { name: 'createTime', label: '创建时间', type: 'LONG', required: true },
    { name: 'updateTime', label: '更新时间', type: 'LONG', required: false },
    { name: 'completedTime', label: '完成时间', type: 'LONG', required: false },
  ],
  WITHDRAW_CREATE: [
    { name: 'orderId', label: '订单号', type: 'STRING', required: true },
    { name: 'status', label: '状态', type: 'STRING', required: true, tip: 'PROCESSING / SUCCESS / FAILED' },
  ],
  WITHDRAW_QUERY: [
    { name: 'externalOrderId', label: '外部订单号', type: 'STRING', required: true },
    { name: 'orderId', label: '订单号', type: 'STRING', required: true },
    { name: 'status', label: '提现单状态', type: 'STRING', required: true, tip: 'PROCESSING / SUCCESS / FAILED' },
    { name: 'subStatus', label: '提现子状态', type: 'STRING', required: true, tip: 'INIT / PRE_CHECKING / CREATING_CHANNEL_ORDER / WAITING_CHANNEL_CALLBACK / SUCCESS / FAILED' },
    { name: 'channelCode', label: '订单使用渠道', type: 'STRING', required: true },
    { name: 'withdrawMethod', label: '提现方式', type: 'STRING', required: true, tip: 'BANK_TRANSFER / PIX_CODE / INTERAC / EFT' },
    { name: 'currency', label: '支付币种', type: 'STRING', required: true },
    { name: 'amount', label: '订单金额', type: 'DECIMAL', required: true },
    { name: 'fee', label: '手续费', type: 'DECIMAL', required: true },
    { name: 'feeCurrency', label: '手续费币种', type: 'STRING', required: true },
    { name: 'createTime', label: '创建时间', type: 'LONG', required: true },
    { name: 'updateTime', label: '更新时间', type: 'LONG', required: true },
    { name: 'completedTime', label: '完成时间', type: 'LONG', required: false },
    { name: 'remark', label: '备注', type: 'STRING', required: false },
    { name: 'errorCode', label: '错误码', type: 'STRING', required: false },
    { name: 'errorMsg', label: '错误信息', type: 'STRING', required: false },
    { name: 'senderInfo', label: '付款人信息', type: 'OBJECT', required: false, children: [
      { name: 'holderName', label: '持有人名称', type: 'STRING', required: false },
      { name: 'bankName', label: '银行名称', type: 'STRING', required: false },
      { name: 'iban', label: 'IBAN', type: 'STRING', required: false },
      { name: 'vban', label: 'VBAN', type: 'STRING', required: false },
      { name: 'bic', label: 'BIC', type: 'STRING', required: false },
      { name: 'accountNumber', label: '账号', type: 'STRING', required: false },
      { name: 'network', label: '网络类型', type: 'STRING', required: false, tip: 'SWIFT / LOCAL' },
    ] },
    { name: 'receiverInfo', label: '收款人信息', type: 'OBJECT', required: false, children: [
      { name: 'holderName', label: '持有人名称', type: 'STRING', required: false },
      { name: 'bankName', label: '银行名称', type: 'STRING', required: false },
      { name: 'iban', label: 'IBAN', type: 'STRING', required: false },
      { name: 'vban', label: 'VBAN', type: 'STRING', required: false },
      { name: 'bic', label: 'BIC', type: 'STRING', required: false },
      { name: 'accountNumber', label: '账号', type: 'STRING', required: false },
      { name: 'network', label: '网络类型', type: 'STRING', required: false, tip: 'SWIFT / LOCAL' },
    ] },
    { name: 'channelOrderId', label: '渠道订单号', type: 'STRING', required: false },
    { name: 'extParams', label: '渠道扩展信息', type: 'JSON_STRING', required: false, tip: '渠道返回的额外信息，JSON格式', children: [
      { name: 'channelOrderId', label: '渠道订单ID', type: 'STRING', required: true, _channel: 'BANK_FRICK' },
      { name: 'originalAmount', label: '原始金额', type: 'STRING', required: true, _channel: 'PYVIO' },
      { name: 'originalCurrency', label: '原始币种', type: 'STRING', required: true, _channel: 'PYVIO' },
      { name: 'targetAmount', label: '目标金额', type: 'STRING', required: true, _channel: 'PYVIO' },
      { name: 'targetCurrency', label: '目标币种', type: 'STRING', required: true, _channel: 'PYVIO' },
      { name: 'feeAmount', label: '手续费金额', type: 'STRING', required: true, _channel: 'PYVIO' },
      { name: 'feeCurrency', label: '手续费币种', type: 'STRING', required: true, _channel: 'PYVIO' },
      { name: 'originalAmount', label: '原始 payout 金额', type: 'STRING', required: false, _channel: 'TAZAPAY', tip: '非换汇场景与 amount 一致，换汇场景为 payout 币种金额' },
      { name: 'originalCurrency', label: '原始 payout 币种', type: 'STRING', required: false, _channel: 'TAZAPAY', tip: '非换汇场景与 currency 一致，换汇场景为 payout 币种' },
      { name: 'targetAmount', label: '目标金额', type: 'STRING', required: false, _channel: 'MDAQ' },
      { name: 'targetCurrency', label: '目标币种', type: 'STRING', required: false, _channel: 'MDAQ' },
    ] },
  ],
  CHANNEL_CONFIG_QUERY: [
    { name: 'channelCode', label: '渠道编码', type: 'STRING', required: true },
    { name: 'channelName', label: '渠道名称', type: 'STRING', required: true },
    { name: 'channelType', label: '渠道类型', type: 'STRING', required: true, tip: 'BANK / WALLET / PSP' },
    { name: 'businessType', label: '业务类型', type: 'STRING', required: true, tip: 'BUSINESS / INDIVIDUAL / ALL' },
    { name: 'region', label: '区域', type: 'STRING', required: false },
    { name: 'country', label: '国家', type: 'STRING', required: true },
    { name: 'currency', label: '渠道结算币种', type: 'STRING', required: true },
    { name: 'sameNameVa', label: '是否需要VA同名', type: 'STRING', required: true, tip: 'true / false' },
    { name: 'paymentMethod', label: '支付方式', type: 'STRING', required: true },
    { name: 'needOpenAccount', label: '是否需要开户', type: 'BOOLEAN', required: true },
    { name: 'depositOrderMode', label: '充值订单模式', type: 'STRING', required: false, tip: 'OFFLINE_DEPOSIT / PRE_ORDER' },
    { name: 'supportDeposit', label: '支持充值', type: 'BOOLEAN', required: true },
    { name: 'supportWithdraw', label: '支持提现', type: 'BOOLEAN', required: true },
    { name: 'supportRefund', label: '支持退款', type: 'BOOLEAN', required: true },
    { name: 'supportPartialRefund', label: '支持部分退款', type: 'BOOLEAN', required: true },
  ],
  ORDER_PAGE: [
    { name: 'total', label: '总数', type: 'INTEGER', required: true },
    { name: 'page', label: '页码', type: 'INTEGER', required: true },
    { name: 'pageSize', label: '每页条数', type: 'INTEGER', required: true },
    { name: 'list[].channelCode', label: '渠道编号', type: 'STRING', required: true },
    { name: 'list[].currency', label: '币种', type: 'STRING', required: true },
    { name: 'list[].businessPartner', label: '合作伙伴', type: 'STRING', required: true },
    { name: 'list[].orderId', label: '订单号', type: 'STRING', required: true },
    { name: 'list[].accountId', label: '账户号', type: 'STRING', required: true },
    { name: 'list[].businessType', label: '业务类型', type: 'STRING', required: true, tip: 'DEPOSIT / WITHDRAW / REFUND' },
    { name: 'list[].transAmount', label: '交易金额', type: 'DECIMAL', required: true },
    { name: 'list[].transTime', label: '交易时间', type: 'LONG', required: true },
    { name: 'list[].status', label: '订单状态', type: 'STRING', required: true },
    { name: 'list[].isRefund', label: '是否退款', type: 'BOOLEAN', required: true },
    { name: 'list[].refundOrderId', label: '退款单号', type: 'STRING', required: false },
    { name: 'list[].externalUserId', label: '外部用户ID', type: 'STRING', required: false },
  ],
  BANK_LIST: [
    { name: 'identifier', label: '银行唯一标识', type: 'STRING', required: true, tip: 'DA5: 收款机构 BIC 代码（provider_code）' },
    { name: 'name', label: '银行名称', type: 'STRING', required: true, tip: 'DA5: 收款机构名称' },
    { name: 'type', label: '银行类型', type: 'STRING', required: false, tip: '如 OPEN_BANKING；DA5不返回' },
    { name: 'logo', label: 'Logo URL', type: 'STRING', required: false, tip: 'DA5不返回' },
    { name: 'countryCode', label: '国家码', type: 'STRING', required: true, tip: 'ISO 三字母国家代码；DA5固定返回 "PH"' },
    { name: 'transferLimits', label: '转账限额', type: 'ARRAY', required: false, tip: 'DA5不返回', children: [
      { name: 'currency', label: '币种', type: 'STRING', required: true, tip: 'ISO 4217' },
      { name: 'min', label: '最小转账金额', type: 'STRING', required: true },
      { name: 'max', label: '最大转账金额', type: 'STRING', required: true },
    ] },
    { name: 'internationalTransferLimits', label: '国际转账限额', type: 'ARRAY', required: false, tip: 'DA5不返回', children: [
      { name: 'currency', label: '币种', type: 'STRING', required: true, tip: 'ISO 4217' },
      { name: 'min', label: '最小转账金额', type: 'STRING', required: true },
      { name: 'max', label: '最大转账金额', type: 'STRING', required: true },
    ] },
  ],
  FILE_UPLOAD: [
    { name: 'fileId', label: '文件ID', type: 'STRING', required: true, tip: '平台文件ID；上传接口同步返回、状态固定 PROCESSING(异步存 S3 + 分发渠道)。FV_BANK: 渠道分发成功后渠道侧返回 SupportingDocumentID，供出金 Send Bank Payment 的 SupportingDocument 使用(上游先上传拿 fileId，再提现带上)' },
    { name: 'status', label: '状态', type: 'STRING', required: true, tip: 'PROCESSING / SUCCESS / REPLACED / FAILED; 上传接口固定返回 PROCESSING(受理成功)' },
    { name: 'bizId', label: '业务ID', type: 'STRING', required: true },
    { name: 'downloadUrl', label: '文件可下载地址', type: 'STRING', required: false, tip: '可直接填入支持 URL 附件的渠道业务请求；调用方须确保异步处理期间该 http(s) URL 仍可下载，平台不约定固定有效期' },
  ],
  FILE_STATUS_QUERY: [
    { name: 'fileId', label: '文件ID', type: 'STRING', required: true },
    { name: 'status', label: '状态', type: 'STRING', required: true, tip: 'PROCESSING / SUCCESS / REPLACED / FAILED' },
    { name: 'bizId', label: '业务ID', type: 'STRING', required: true },
    { name: 'errorMessage', label: '错误信息', type: 'STRING', required: false, tip: '仅 FAILED(不可恢复)时返回' },
  ],
  USER_CREATE: [
    { name: 'userId', label: '用户ID', type: 'STRING', required: true },
    { name: 'clientId', label: '渠道ClientID', type: 'STRING', required: true, tip: '多个externalUserId可对应同一个clientId' },
    { name: 'status', label: '状态', type: 'STRING', required: true, tip: 'PROCESSING / SUCCESS / FAILED / DENIED' },
    { name: 'externalUserId', label: '外部用户ID', type: 'STRING', required: true },
    { name: 'failedReason', label: '失败原因', type: 'STRING', required: false },
  ],
  // 提交补充资料返回该用户提交动作完成后的当前状态（补件成功后通常仍为 FAILED，等渠道复审 webhook 再转 SUCCESS/DENIED）
  USER_SUPPLEMENT: [
    { name: 'userId', label: '用户ID', type: 'STRING', required: true },
    { name: 'status', label: '状态', type: 'STRING', required: true, tip: '提交后当前状态；补件成功后通常仍为 FAILED，等复审转 SUCCESS/DENIED' },
    { name: 'externalUserId', label: '外部用户ID', type: 'STRING', required: true },
    { name: 'failedReason', label: '失败原因', type: 'STRING', required: false, tip: 'requires_action 时含待补材料清单' },
  ],
  USER_QUERY: [
    { name: 'userId', label: '用户ID', type: 'STRING', required: true },
    { name: 'channelUserId', label: '渠道用户ID', type: 'STRING', required: true },
    { name: 'clientId', label: '渠道ClientID', type: 'STRING', required: true },
    { name: 'status', label: '状态', type: 'STRING', required: true, tip: 'PROCESSING / SUCCESS / FAILED / DENIED' },
    { name: 'externalUserId', label: '外部用户ID', type: 'STRING', required: true },
    { name: 'failedReason', label: '失败原因', type: 'STRING', required: false },
  ],
  BALANCE_QUERY: [
    { name: 'currency', label: '币种', type: 'STRING', required: true },
    { name: 'channelCode', label: '渠道', type: 'STRING', required: true },
    { name: 'channelAccountNo', label: '渠道账户号', type: 'STRING', required: false, tip: '渠道账户标识（如 SGB 返回大账号）; TRANSFI 不返回' },
    { name: 'availableBalance', label: '可用余额', type: 'STRING', required: true, tip: '主余额（多数渠道为账户可用余额；TRANSFI 为可用预充值底仓 totalAvailablePrefundingBalance）' },
    { name: 'balanceDetails', label: '多维度余额明细', type: 'ARRAY', required: false, tip: '渠道有多种余额概念时返回（如 TRANSFI），单一余额渠道不返回该字段', children: [
      { name: 'type', label: '余额类型', type: 'STRING', required: true, tip: 'AVAILABLE(可用余额) / AVAILABLE_PREFUNDING(可用预充值底仓) / COLLECTIONS(收单归集金额)' },
      { name: 'amount', label: '金额', type: 'STRING', required: true },
    ] },
  ],
  MMO_LIST: [
    { name: 'name', label: '运营商名称', type: 'STRING', required: true, tip: '如 SAFARICOM' },
    { name: 'slug', label: '运营商slug', type: 'STRING', required: true, tip: '用于发起提现，如 safaricom-ke' },
    { name: 'code', label: '运营商code', type: 'STRING', required: true, tip: '用于账户校验，如 0001' },
    { name: 'country', label: '国家代码', type: 'STRING', required: true, tip: '如 KE' },
    { name: 'min', label: '最小金额', type: 'DECIMAL', required: false },
    { name: 'max', label: '最大金额', type: 'DECIMAL', required: false },
  ],
  RATE_QUERY: [
    { name: 'exchangeRate', label: '汇率', type: 'STRING', required: true, tip: '格式: 0.0000000000' },
    { name: 'rateId', label: '汇率ID', type: 'STRING', required: false, tip: '后续提现使用; TRANSFI 报价非锁定式，不返回该字段' },
    { name: 'originCurrency', label: '原始币种', type: 'STRING', required: true },
    { name: 'targetCurrency', label: '目标币种', type: 'STRING', required: true },
    { name: 'expireTime', label: '过期时间', type: 'STRING', required: false, tip: '时间戳; TRANSFI 报价非锁定式，不返回该字段' },
    { name: 'supportingDocumentsRequired', label: '是否需要上传补充文件', type: 'BOOLEAN', required: false, tip: 'MDAQ返回: true时提现必填上传补充文件' },
  ],
  PAYOUT_REQUIREMENTS: [
    { name: 'country', label: '国家', type: 'STRING', required: true },
    { name: 'currency', label: '币种', type: 'STRING', required: true },
    { name: 'channelCode', label: '渠道', type: 'STRING', required: true },
    { name: 'payoutMethods', label: 'Payout 方式列表', type: 'ARRAY', required: true, tip: '每个元素包含 payoutType、requiredFields、recommendedFields、transferLimit', children: [
      { name: 'payoutType', label: 'Payout 类型', type: 'STRING', required: true, tip: 'swift / local' },
      { name: 'beneficiaryTypes', label: '支持的收款人类型', type: 'ARRAY', required: true, tip: 'individual / business' },
      { name: 'requiredFields', label: '必填字段', type: 'OBJECT', required: true, children: [
        { name: 'mainFields', label: '主字段（FiatCardBindRequest 直接字段）', type: 'ARRAY', required: false, tip: '可能的值: accountName / bic / accountNumber / bankName / iban。具体哪些必填取决于走廊，通过本接口动态返回' },
        { name: 'extParamFields', label: 'extParam 中的字段', type: 'ARRAY', required: false, tip: '可能的值: ifscCode / abaCode / sortCode / bankCode / addressLine1 / addressLine2 / city / state / postalCode / addressCountry / taxId / nationality / dateOfBirth / nationalIdentificationNumber / registrationNumber / email / accountType / branchName。具体哪些必填取决于走廊' },
      ] },
      { name: 'recommendedFields', label: '推荐字段（非必填但建议传）', type: 'OBJECT', required: false, children: [
        { name: 'mainFields', label: '主字段', type: 'ARRAY', required: false, tip: '可能的值: bic / accountName / bankName / accountNumber / iban' },
        { name: 'extParamFields', label: 'extParam 中的字段', type: 'ARRAY', required: false, tip: '可能的值: addressLine1 / city / state / postalCode / nationality / email 等' },
      ] },
      { name: 'transferLimit', label: '转账限额', type: 'OBJECT', required: false, children: [
        { name: 'currency', label: '币种', type: 'STRING', required: true },
        { name: 'minimum', label: '最小金额', type: 'STRING', required: true },
        { name: 'maximum', label: '最大金额', type: 'STRING', required: true },
      ] },
      { name: 'fundTransferNetworks', label: '本地清算网络', type: 'ARRAY', required: false, tip: '仅 local 模式，如 FedACH / Fedwire / CHATS / FPS' },
    ] },
  ],
  REMITTER_CREATE: [
    { name: 'remitterId', label: '付款人ID', type: 'STRING', required: true },
    { name: 'status', label: '状态', type: 'STRING', required: true, tip: 'SUCCESS / PROCESSING / FAILED' },
    { name: 'channelCode', label: '渠道代码', type: 'STRING', required: true },
  ],
  REMITTER_QUERY: [
    { name: 'remitterId', label: '付款人ID', type: 'STRING', required: true },
    { name: 'status', label: '状态', type: 'STRING', required: true },
    { name: 'channelCode', label: '渠道代码', type: 'STRING', required: true },
    { name: 'classification', label: '分类', type: 'STRING', required: true },
    { name: 'name', label: '付款人姓名', type: 'STRING', required: true },
  ],
  FUND_TRANSFER_CREATE: [
    { name: 'orderId', label: '平台订单ID', type: 'STRING', required: true },
    { name: 'externalOrderId', label: '外部订单ID', type: 'STRING', required: true },
    { name: 'status', label: '状态', type: 'STRING', required: true, tip: 'PROCESSING / SUCCESS / FAILED / CLOSED' },
    { name: 'channelCode', label: '渠道代码', type: 'STRING', required: true },
  ],
  FUND_TRANSFER_QUERY: [
    { name: 'orderId', label: '平台订单ID', type: 'STRING', required: true },
    { name: 'externalOrderId', label: '外部订单ID', type: 'STRING', required: true },
    { name: 'status', label: '状态', type: 'STRING', required: true },
    { name: 'amount', label: '金额', type: 'NUMBER', required: true },
    { name: 'currency', label: '币种', type: 'STRING', required: true },
    { name: 'channelCode', label: '渠道代码', type: 'STRING', required: true },
  ],
  CHANNEL_TRANSACTIONS: [
    { name: 'total', label: '总条数', type: 'NUMBER', required: true },
    { name: 'records', label: '流水记录列表', type: 'ARRAY', required: true, tip: 'ChannelTransactionRecord[]', children: [
      { name: 'billNo', label: '渠道交易编号', type: 'STRING', required: true },
      { name: 'channelCode', label: '渠道编码', type: 'STRING', required: true },
      { name: 'businessType', label: '业务类型', type: 'STRING', required: true, tip: 'DEPOSIT / WITHDRAW / TRANSFER / OTHER' },
      { name: 'currency', label: '币种', type: 'STRING', required: true },
      { name: 'amount', label: '金额', type: 'NUMBER', required: true, tip: '出金为负，入金为正' },
      { name: 'fee', label: '手续费', type: 'NUMBER', required: false },
      { name: 'channelStatus', label: '渠道交易状态', type: 'STRING', required: true },
      { name: 'tradeTime', label: '交易时间', type: 'STRING', required: true, tip: 'ISO 8601' },
      { name: 'creditDebitIndicator', label: '借贷方向', type: 'STRING', required: true, tip: 'DBIT(出金) / CRDT(入金)' },
      { name: 'senderAccount', label: '付款方账号', type: 'STRING', required: false },
      { name: 'senderName', label: '付款方名称', type: 'STRING', required: false },
      { name: 'beneficiaryAccount', label: '收款方账号', type: 'STRING', required: false },
      { name: 'beneficiaryName', label: '收款方名称', type: 'STRING', required: false },
      { name: 'reference', label: '业务参考号', type: 'STRING', required: false },
      { name: 'remark', label: '备注', type: 'STRING', required: false },
    ] },
  ],
};

// 充值请款的响应【不再与充值查询同构】：它有自己的 DTO(FiatDepositCaptureResponse)，
// 核心是 captureResult —— 本次请款结果，取自渠道 capture 的同步响应，返回即可信；
// 而 status 是订单主状态(读库)，因终态异步落库可能滞后。两者分开表意，勿混用。
RESPONSE_PARAMS.DEPOSIT_CAPTURE = [
  { name: 'orderId', label: '订单号', type: 'STRING', required: true },
  { name: 'externalOrderId', label: '外部订单号', type: 'STRING', required: false },
  { name: 'captureResult', label: '本次请款结果', type: 'STRING', required: true, tip: 'SUCCESS=扣款成功(即时可信，无需轮询查询接口) / FAILED=渠道拒绝(见captureErrorCode) / PROCESSING=渠道处理中或本次未实际触发(并发被幂等丢弃、或订单未到可请款阶段；切勿当失败处理，否则会重复下单) / UNKNOWN=渠道未给明确结果，需等回调或对账确认' },
  { name: 'captureErrorCode', label: '请款错误码', type: 'STRING', required: false, tip: 'captureResult=FAILED 时为渠道错误码' },
  { name: 'captureErrorMsg', label: '请款错误描述', type: 'STRING', required: false, tip: 'captureResult=FAILED 时为渠道错误描述' },
  { name: 'status', label: '订单当前主状态', type: 'STRING', required: true, tip: 'PROCESSING / SUCCESS / FAILED。⚠️ 可能滞后于 captureResult：请款成功后主单转终态由中台异步落库，故 captureResult=SUCCESS 而 status=PROCESSING 是正常组合' },
  { name: 'subStatus', label: '订单当前子状态', type: 'STRING', required: false },
];


// Avenia 专属响应字段。共享 DTO 仍服务其他渠道；这里只展示 Avenia 实际赋值的结构。
const AVENIA_RESPONSE_OVERRIDES = {
  USER_CREATE: [
    { name: 'userId', label: '用户ID', type: 'STRING', required: true },
    { name: 'channelUserId', label: 'Avenia 子账户ID', type: 'STRING', required: false, tip: '子账户创建后返回；异步处理中可能暂为空' },
    { name: 'status', label: '状态', type: 'STRING', required: true, tip: 'PROCESSING / SUCCESS / FAILED / DENIED' },
    { name: 'externalUserId', label: '外部用户ID', type: 'STRING', required: true },
    { name: 'failedReason', label: '失败原因', type: 'STRING', required: false },
  ],
  USER_QUERY: [
    { name: 'userId', label: '用户ID', type: 'STRING', required: true },
    { name: 'channelUserId', label: 'Avenia 子账户ID', type: 'STRING', required: false },
    { name: 'status', label: '状态', type: 'STRING', required: true, tip: 'PROCESSING / SUCCESS / FAILED / DENIED' },
    { name: 'externalUserId', label: '外部用户ID', type: 'STRING', required: true },
    { name: 'failedReason', label: '失败原因', type: 'STRING', required: false },
  ],
  DEPOSIT_QUERY: [
    ...RESPONSE_PARAMS.DEPOSIT_QUERY.filter(f => !['senderInfo', 'receiverInfo', 'extParams'].includes(f.name)),
    { name: 'senderInfo', label: '付款人信息', type: 'OBJECT', required: false, tip: 'Avenia Ticket Detail 返回付款方后才有值', children: [
      { name: 'senderAcctName', label: '付款方账户名', type: 'STRING', required: false },
      { name: 'senderAcctNo', label: '付款方账号', type: 'STRING', required: false },
      { name: 'senderDocumentNumber', label: '付款方税号(CPF/CNPJ)', type: 'STRING', required: false, tip: '渠道只返回号码，不返回 senderDocumentType' },
      { name: 'bankCode', label: '付款方银行代码(ISPB)', type: 'STRING', required: false },
      { name: 'branchCode', label: '付款方分行号', type: 'STRING', required: false },
      { name: 'accountType', label: '付款方账户类型', type: 'STRING', required: false, tip: 'checking / payment 等渠道原值' },
    ] },
    { name: 'extParams', label: '渠道扩展信息', type: 'JSON_STRING', required: false, children: [
      { name: 'endToEndId', label: 'PIX 端到端流水号', type: 'STRING', required: false },
    ] },
  ],
  WITHDRAW_QUERY: [
    ...RESPONSE_PARAMS.WITHDRAW_QUERY.filter(f => !['senderInfo', 'receiverInfo', 'extParams'].includes(f.name)),
    { name: 'senderInfo', label: '付款方信息', type: 'OBJECT', required: false, tip: '仅渠道 Ticket Detail 确实返回时展示', children: [
      { name: 'accountName', label: '付款方账户名', type: 'STRING', required: false },
      { name: 'accountNumber', label: '付款方账号', type: 'STRING', required: false },
      { name: 'taxId', label: '付款方税号', type: 'STRING', required: false },
      { name: 'bankCode', label: '付款方银行代码(ISPB)', type: 'STRING', required: false },
      { name: 'branchCode', label: '付款方分行号', type: 'STRING', required: false },
      { name: 'accountType', label: '付款方账户类型', type: 'STRING', required: false },
    ] },
    { name: 'receiverInfo', label: '收款方信息', type: 'OBJECT', required: false, children: [
      { name: 'accountName', label: '收款方账户名', type: 'STRING', required: false },
      { name: 'accountNumber', label: '收款方账号', type: 'STRING', required: false },
      { name: 'bankName', label: '收款方银行名称', type: 'STRING', required: false },
      { name: 'taxId', label: '收款方税号(CNPJ)', type: 'STRING', required: false },
      { name: 'bankCode', label: '收款方银行代码(ISPB)', type: 'STRING', required: false },
      { name: 'branchCode', label: '收款方分行号', type: 'STRING', required: false },
      { name: 'accountType', label: '收款方账户类型', type: 'STRING', required: false },
    ] },
    { name: 'extParams', label: '渠道扩展信息', type: 'JSON_STRING', required: false, children: [
      { name: 'endToEndId', label: 'PIX 端到端流水号', type: 'STRING', required: false },
      { name: 'recipientId', label: '中台绑卡ID', type: 'STRING', required: false },
      { name: 'channelFailureReason', label: '渠道原始失败原因', type: 'STRING', required: false },
    ] },
  ],
};

const AVENIA_API_DESC = {
  USER_CREATE: '创建 Avenia 企业用户：仅支持 CREATE 和 BUSINESS。',
  CARD_BIND: '绑定 Avenia 企业收款方：仅 BUSINESS、BRL；PIX Key 与完整银行账户信息二选一且必须选择一种。',
  DEPOSIT_CREATE: '创建 Avenia BRL PIX 充值订单。amount 为顶层字段，extParam 仅支持 expire。',
  WITHDRAW_CREATE: '创建 Avenia BRL PIX 提现订单。须先绑卡，extParam 仅支持 recipientId、remark、expire。',
  USER_QUERY: '查询 Avenia 企业用户状态；Avenia 不返回 clientId。',
  DEPOSIT_QUERY: '查询 Avenia 充值状态及真实付款方信息。充值通常不返回 receiverInfo。',
  WITHDRAW_QUERY: '查询 Avenia 提现状态及真实收付款方信息。',
};

const AVENIA_API_EXAMPLES = {
  USER_QUERY: {
    request: { requestId: 'req_avenia_user_query_01', channelCode: 'AVENIA', externalUserId: 'osl_corp_001' },
    response: { code: '200', subCode: '000000', msg: 'success', data: { userId: 'UR20260812AV00000001', channelUserId: 'f8735ea3-a658-4f43-8dd4-0ba1c9c75bfe', status: 'SUCCESS', externalUserId: 'osl_corp_001', failedReason: '' } },
  },
  DEPOSIT_QUERY: {
    request: { requestId: 'req_avenia_deposit_query_01', orderId: 'DP20260812AV00000001' },
    response: { code: '200', subCode: '000000', msg: 'success', data: { orderId: 'DP20260812AV00000001', status: 'SUCCESS', channelCode: 'AVENIA', depositMethod: 'PIX', currency: 'BRL', amount: 100, senderInfo: { senderAcctName: 'PAYER LTDA', senderAcctNo: '12345', senderDocumentNumber: '40261549000154', bankCode: '20018183', branchCode: '0001', accountType: 'payment' }, extParams: '{"endToEndId":"E123"}' } },
  },
  WITHDRAW_QUERY: {
    request: { requestId: 'req_avenia_withdraw_query_01', orderId: 'WD20260812AV00000001' },
    response: { code: '200', subCode: '000000', msg: 'success', data: { orderId: 'WD20260812AV00000001', status: 'SUCCESS', channelCode: 'AVENIA', withdrawMethod: 'PIX', currency: 'BRL', amount: 50, receiverInfo: { accountName: 'SUPPLIER LTDA', accountNumber: '1234567', bankName: 'BANCO EXAMPLE', taxId: '40261549000154', bankCode: '20018183', branchCode: '0001', accountType: 'checking' }, extParams: '{"endToEndId":"E456","recipientId":"RP20260827AV00000029"}' } },
  },
};

// MDAQ 渠道的 PAYOUT_REQUIREMENTS 响应字段描述
const RESPONSE_PARAMS_MDAQ_PAYOUT_REQUIREMENTS = [
  { name: 'country', label: '国家', type: 'STRING', required: true },
  { name: 'currency', label: '币种', type: 'STRING', required: true },
  { name: 'channelCode', label: '渠道', type: 'STRING', required: true },
  { name: 'data', label: '绑定银行账户字段要求（按 entityType 分组）', type: 'ARRAY', required: true, tip: '字段要求：按照entityType（individual/company）分组，KYB取company分组字段，返回该字段则必填，字段值为正则表达式校验规则', children: [
    { name: 'entityType', label: '实体类型', type: 'STRING', required: true, tip: 'individual / company' },
    { name: 'firstName', label: '名', type: 'STRING', required: false, tip: '走廊返回时必填' },
    { name: 'lastName', label: '姓', type: 'STRING', required: false, tip: '走廊返回时必填' },
    { name: 'identificationType', label: '证件类型', type: 'STRING', required: false, tip: '走廊返回时必填' },
    { name: 'identificationValue', label: '证件号码', type: 'STRING', required: false, tip: '走廊返回时必填' },
    { name: 'mobileNumber', label: '手机号', type: 'STRING', required: false, tip: '走廊返回时必填; 如 ≤36 characters' },
    { name: 'relationship', label: '收款人关系', type: 'STRING', required: false, tip: '走廊返回时必填' },
    { name: 'companyName', label: '公司名称', type: 'STRING', required: false, tip: '走廊返回时必填' },
    { name: 'companyNameInChineseCharacters', label: '公司名称（中文）', type: 'STRING', required: false, tip: '走廊返回时必填' },
    { name: 'businessType', label: '企业类型', type: 'STRING', required: false, tip: '走廊返回时必填' },
    { name: 'address', label: '地址', type: 'STRING', required: false, tip: '走廊返回时必填; Beneficiary address format' },
    { name: 'addressInChineseCharacters', label: '地址（中文）', type: 'STRING', required: false, tip: '走廊返回时必填' },
    { name: 'suburb', label: '区/郊区', type: 'STRING', required: false, tip: '走廊返回时必填' },
    { name: 'postcode', label: '邮编', type: 'STRING', required: false, tip: '走廊返回时必填' },
    { name: 'city', label: '城市', type: 'STRING', required: false, tip: '走廊返回时必填' },
    { name: 'cityInChineseCharacters', label: '城市（中文）', type: 'STRING', required: false, tip: '走廊返回时必填' },
    { name: 'region', label: '区域', type: 'STRING', required: false, tip: '走廊返回时必填' },
    { name: 'stateOrProvince', label: '州/省', type: 'STRING', required: false, tip: '走廊返回时必填' },
    { name: 'country', label: '国家', type: 'STRING', required: false, tip: '走廊返回时必填; alpha-2 ISO country code, 2 characters' },
    { name: 'bankAccount', label: '银行账户信息', type: 'OBJECT', required: false, tip: '走廊返回时必填', children: [
      { name: 'currency', label: '币种', type: 'STRING', required: false, tip: '走廊返回时必填; 3 characters' },
      { name: 'bankAccountHolderName', label: '持卡人姓名', type: 'STRING', required: false, tip: '走廊返回时必填' },
      { name: 'bankAccountHolderNameInChineseCharacters', label: '持卡人姓名（中文）', type: 'STRING', required: false, tip: '走廊返回时必填' },
      { name: 'accountNumber', label: '账号', type: 'STRING', required: false, tip: '走廊返回时必填' },
      { name: 'type', label: '账户类型', type: 'STRING', required: false, tip: '走廊返回时必填; 如 Checking' },
      { name: 'iban', label: 'IBAN', type: 'STRING', required: false, tip: '走廊返回时必填' },
      { name: 'clabe', label: 'CLABE', type: 'STRING', required: false, tip: '走廊返回时必填' },
      { name: 'cnaps', label: 'CNAPS', type: 'STRING', required: false, tip: '走廊返回时必填' },
      { name: 'bicSwift', label: 'BIC/SWIFT', type: 'STRING', required: false, tip: '走廊返回时必填' },
      { name: 'aba', label: 'ABA', type: 'STRING', required: false, tip: '走廊返回时必填' },
      { name: 'bsbCode', label: 'BSB Code', type: 'STRING', required: false, tip: '走廊返回时必填' },
      { name: 'sortCode', label: 'Sort Code', type: 'STRING', required: false, tip: '走廊返回时必填' },
      { name: 'ifscCode', label: 'IFSC Code', type: 'STRING', required: false, tip: '走廊返回时必填' },
      { name: 'routingCode', label: 'Routing Code', type: 'STRING', required: false, tip: '走廊返回时必填' },
      { name: 'bankName', label: '银行名称', type: 'STRING', required: false, tip: '走廊返回时必填' },
      { name: 'bankNameInChineseCharacters', label: '银行名称（中文）', type: 'STRING', required: false, tip: '走廊返回时必填' },
      { name: 'branchName', label: '支行名称', type: 'STRING', required: false, tip: '走廊返回时必填' },
      { name: 'branchCode', label: '支行代码', type: 'STRING', required: false, tip: '走廊返回时必填' },
      { name: 'bankCode', label: '银行代码', type: 'STRING', required: false, tip: '走廊返回时必填' },
      { name: 'country', label: '银行国家', type: 'STRING', required: false, tip: '走廊返回时必填; 2 characters' },
    ] },
  ] },
];

function inferFieldType(value) {
  if (Array.isArray(value)) return 'ARRAY';
  if (value === null || value === undefined) return 'STRING';
  if (typeof value === 'object') return 'OBJECT';
  if (typeof value === 'number') return 'NUMBER';
  if (typeof value === 'boolean') return 'BOOLEAN';
  return 'STRING';
}

function buildFieldsFromObject(obj) {
  if (!obj || typeof obj !== 'object') return [];
  return Object.entries(obj).map(([key, value]) => {
    const field = { name: key, label: key, type: inferFieldType(value), required: false };
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      field.children = buildFieldsFromObject(value);
    } else if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
      field.type = 'ARRAY';
      field.children = buildFieldsFromObject(value[0]);
    }
    return field;
  });
}

function buildDynamicResponseParams(data, channelCode) {
  const fixedFields = [
    { name: 'country', label: '国家', type: 'STRING', required: true },
    { name: 'currency', label: '币种', type: 'STRING', required: true },
    { name: 'channelCode', label: '渠道', type: 'STRING', required: true },
  ];

  if (channelCode === 'MDAQ' && data.data) {
    // MDAQ: data 是数组，每个元素按 entityType 分组
    const sample = Array.isArray(data.data) ? data.data[0] : data.data;
    fixedFields.push({
      name: 'data',
      label: 'Beneficiary Requirements（按 entityType 分组）',
      type: 'ARRAY',
      required: true,
      children: buildFieldsFromObject(sample || {}),
    });
  } else if (data.payoutMethods) {
    // TAZAPAY 等: payoutMethods 数组
    const sample = data.payoutMethods[0] || {};
    fixedFields.push({
      name: 'payoutMethods',
      label: 'Payout 方式列表',
      type: 'ARRAY',
      required: true,
      children: buildFieldsFromObject(sample),
    });
  } else {
    // 其他未知结构：直接从顶层 key 生成（排除已有的 country/currency/channelCode）
    const excluded = new Set(['country', 'currency', 'channelCode']);
    Object.entries(data).forEach(([key, value]) => {
      if (!excluded.has(key)) {
        const field = { name: key, label: key, type: inferFieldType(value), required: false };
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          field.children = buildFieldsFromObject(value);
        } else if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
          field.type = 'ARRAY';
          field.children = buildFieldsFromObject(value[0]);
        }
        fixedFields.push(field);
      }
    });
  }

  return fixedFields;
}

// 通用接口的示例请求 & 响应
const API_EXAMPLES = {
  ACCOUNT_CLOSE: {
    request: { requestId: 'req_close_001', externalUserId: 'user_001', accountId: 'AC20260108SG00000155', reason: '用户主动关闭', reasonCode: 'C03' },
    response: { code: '200', subCode: '000000', msg: 'success', requestTime: 1702609200000, requestId: 'req_close_001', data: { accountId: 'AC20260108SG00000155', requestId: 'req_close_001', status: 'SUCCESS', errorCode: null, errorMsg: null } },
  },
  ACCOUNT_CLOSE_QUERY: {
    request: { accountId: 'AC20260108SG00000155', externalUserId: 'user_001', requestId: 'req_close_query_001' },
    response: { code: '200', subCode: '000000', msg: 'success', requestTime: 1702609200000, requestId: 'req_close_query_001', data: { accountId: 'AC20260108SG00000155', requestId: 'req_close_query_001', status: 'SUCCESS', errorCode: null, errorMsg: null } },
  },
  ACCOUNT_QUERY: {
    request: { accountId: 'AC20260108SG00000155', requestId: '123123123123123' },
    response: { code: '200', subCode: '000000', msg: 'success', requestTime: 1702598400000, requestId: 'req_1702598400123_abc123', data: { accountId: 'ACC202312150001', status: 'ACTIVE', subStatus: 'SUCCESS', externalUserId: 'user123456', channelCode: 'EQUALS_MONEY', paymentMethod: 'VIRTUAL_ACCOUNT', currency: 'USD', redirectUrl: null, errorCode: null, errorMsg: null, createTime: 1702502000000, updateTime: 1702598400000, accountDetail: { accountName: 'JOHN DOE', accountNumber: '123456789012', bic: 'BOFAUS3NXXX', bankName: 'BANK OF AMERICA', bankCountry: 'United States', bankAddress: '100 NORTH TRYON STREET' }, userType: 'INDIVIDUAL', extParam: null } },
  },
  CARD_QUERY: {
    request: { requestId: 'your_request_id_here', recipientId: 'your_receipt_id_here' },
    response: { code: '200', subCode: '000000', msg: 'success', requestTime: 1702598400000, requestId: 'req_1702598400123_abc123', data: { recipientId: 'RECP202312150001', status: 'SUCCESS', channelCode: 'EQUALS_MONEY', externalUserId: 'user123456', accountName: 'JOHN DOE', accountNumber: '123456789012', bic: 'BOFAUS3NXXX', bankName: 'BANK OF AMERICA', currency: 'USD', city: 'CHARLOTTE', country: 'USA', address: '123 MAIN STREET', bankCity: 'CHARLOTTE', bankCountry: 'USA', bankAddress: '100 NORTH TRYON STREET', errorCode: null, errorMsg: null } },
  },
  CARD_UNBIND: {
    request: { recipientId: 'RP20260304BC00000134', channelCode: 'BANK_FRICK', requestId: 'REQ_20260129_00003' },
    response: { code: '200', subCode: '000000', msg: 'success', requestTime: 1702609200000, requestId: 'REQ_20260129_00003', data: { recipientId: 'RP20260304BC00000134', status: 'SUCCESS' } },
  },
  DEPOSIT_QUERY: {
    request: { requestId: 'req_1234567890', orderId: 'DP2025121511060001' },
    response: { code: '200', subCode: '000000', msg: 'success', requestTime: 1702609200000, requestId: 'req_1234567890', data: { externalOrderId: 'EXT_ORD_123', orderId: 'DP2025121511060001', status: 'SUCCESS', subStatus: 'SUCCESS', refundStatus: 'NOT_REFUNDED', channelCode: 'EQUALS_MONEY', accountId: '1000001', depositMethod: 'VIRTUAL_ACCOUNT', currency: 'EUR', amount: 300.00, fee: 5.00, errorCode: null, errorMsg: null, completedTime: 1702609200000, createTime: 1702609200000, updateTime: 1702609200000 } },
  },
  DEPOSIT_CLOSE: {
    request: { requestId: 'req_close_001', orderId: 'DEP202312150001' },
    response: { code: '200', subCode: '000000', msg: 'success', requestTime: 1778136838716, requestId: 'req_close_001', data: { orderId: 'DEP202312150001', externalOrderId: 'ext_dep_001', state: 'FAILED', closeResult: 'SUCCESS', closeMessage: 'Order closed successfully' } },
  },
  DEPOSIT_CAPTURE: {
    request: { requestId: 'req_capture_001', orderId: 'DP20260821PY00000001' },
    // captureResult=SUCCESS 而 status=PROCESSING 是正常组合：钱已扣成功，订单终态由中台异步落库
    response: { code: '200', subCode: '000000', msg: 'success', requestTime: 1778136838716, requestId: 'req_capture_001', data: { orderId: 'DP20260821PY00000001', externalOrderId: 'ext_dep_001', captureResult: 'SUCCESS', captureErrorCode: null, captureErrorMsg: null, status: 'PROCESSING', subStatus: 'CREATE_CALLBACK_WAIT' } },
  },
  REFUND_CREATE: {
    request: { requestId: 'req_refund_01', externalOrderId: 'EXT_REF_01', depositOrderId: 'D12345678', callbackUrl: 'https://merchant.com/callback', description: 'Customer canceled' },
    response: { code: '200', subCode: '000000', msg: 'success', requestTime: 1702609200000, requestId: 'req_1234567890', data: { orderId: '1000001' } },
  },
  REFUND_QUERY: {
    request: { requestId: 'req_q_ref_1', externalOrderId: 'EXT_REF_01' },
    response: { code: '200', subCode: '000000', msg: 'success', requestTime: 1702609200000, requestId: 'req_1234567890', data: { externalOrderId: 'BL202310270001', orderId: 'RF123456789', status: 'SUCCESS', errorCode: null, errorMsg: null, refundReason: '用户取消订单', remark: '通过APP申请', amount: 150.88, arrivalAmount: 150.00, currency: 'EUR', channelCode: 'EQUALS_MONEY', createTime: 1702609200000, updateTime: 1702609200000, completedTime: 1702609200000 } },
  },
  WITHDRAW_QUERY: {
    request: { requestId: 'A123123123', externalOrderId: 'EXT_WITH_71', orderId: 'WD20251218EM00000020' },
    response: { code: '200', subCode: '000000', msg: 'success', requestTime: 1702598400000, requestId: 'req_withdraw_01', data: { externalOrderId: 'EXT_WITH_01', orderId: 'WD20251218EM00000020', status: 'SUCCESS', subStatus: 'SUCCESS', channelCode: 'EQUALS_MONEY', withdrawMethod: 'BANK_TRANSFER', currency: 'EUR', amount: 200.00, fee: 1.50, feeCurrency: 'EUR', createTime: 1702598400000, updateTime: 1702598400000, completedTime: 1702598400000, channelOrderId: 'P2603102019265845234459680', errorCode: null, errorMsg: null } },
  },
  CHANNEL_CONFIG_QUERY: {
    request: { requestId: 'req_q_bal', channelCode: 'EQUALS_MONEY', currency: 'USD' },
    response: { code: 200, subCode: '000000', msg: 'Success', requestTime: '1766644949846', requestId: '12313312312315', data: [{ channelCode: 'EQUALS_MONEY', channelName: 'Equals Money', channelType: 'BANK', businessType: 'ALL', region: 'EU', country: 'UK', currency: 'USD', sameNameVa: true, paymentMethod: 'SWIFT', needOpenAccount: true, depositOrderMode: 'OFFLINE_DEPOSIT', supportDeposit: true, supportWithdraw: true, supportRefund: false, supportPartialRefund: false }] },
  },
  ORDER_PAGE: {
    request: { requestId: 'recon_20260106_sgb', channelCode: 'SGB', filters: { orderType: 'DEPOSIT', currency: 'USD', orderStatusList: ['SUCCESS', 'FAILED'] }, timeRange: { startTime: 1767628800000, endTime: 1767715199000 }, pagination: { page: 1, pageSize: 100 } },
    response: { code: '200', subCode: '000000', msg: 'success', requestTime: 1702609200000, requestId: 'req_1234567890', data: { total: 1, page: 1, pageSize: 100, list: [{ channelCode: 'SGB', currency: 'USD', orderId: 'DP20260106PS00000047', accountId: 'AC20251229SG00000075', businessType: 'DEPOSIT', transAmount: 100.00, businessPartner: 'BIZPAY', transTime: 1748396290481, status: 'SUCCESS', isRefund: false, refundOrderId: null, externalUserId: 'user123456' }] } },
  },
  BANK_LIST: {
    request: { channelCode: 'LEAN_TECH', requestId: '213213234123' },
    response: { code: 200, subCode: '000000', msg: 'Success', requestId: '213213234123', data: [{ identifier: 'CABBPHM1XXX', name: 'CARD BANK INC', countryCode: 'PH' }, { identifier: 'BNORPHMMXXX', name: 'BDO UNIBANK INC', countryCode: 'PH' }] },
  },
  FILE_UPLOAD: {
    // FV_BANK 出金附件上传：multipart/form-data，scene=WITHDRAW；file 为二进制
    request: { channelCode: 'FV_BANK', scene: 'WITHDRAW', documentType: 'SOURCE_OF_FUNDS', bizId: 'WD202401010001', externalUserId: 'user_9988', requestId: 'req_20240101001', file: '(binary, pdf/jpg/png ≤5MB)' },
    // downloadUrl 为 pre-signed 可下载地址，按 URL 传附件的渠道（AVENIA / PYVIO / TAZAPAY 等）可直接引用
    response: { code: '200', subCode: '000000', message: 'success', requestId: 'req_20240101001', data: { fileId: 'FV202401010001', bizId: 'WD202401010001', status: 'PROCESSING', downloadUrl: 'https://s3-eu-central-1.amazonaws.com/bucket/fiat_payment/FV_BANK/WITHDRAW/user_9988/SOURCE_OF_FUNDS/FV202401010001?X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Signature=...' } },
  },
  FILE_STATUS_QUERY: {
    request: { requestId: 'req_20260116_000123', fileIds: ['FILE_202601160001', 'FILE_202601160002', 'FILE_202601160003'] },
    response: { code: '200', subCode: '000000', message: 'success', requestId: 'req-456', data: [{ fileId: 'CF202401010001', bizId: 'ACCOUNT_10001', status: 'PROCESSING' }, { fileId: 'CF202401010002', bizId: 'ACCOUNT_10001', status: 'SUCCESS' }] },
  },
  USER_CREATE: {
    request: { requestId: '1020260121769958461', operateType: 'CREATE', channelCode: 'PYVIO', externalUserId: 'USER_ID_202602021033030', userType: 'BUSINESS', extParam: { email: 'email@gmail.com', merchantType: 'B2B', location: 'HK', industry: ['TRANSLATION_LANGUAGES', 'TELECOMMUNICATIONS'] } },
    response: { code: 200, subCode: '000000', msg: 'Success', requestId: '1020260121769958461', data: { externalUserId: 'USER_ID_202602021033030', status: 'PROCESSING', userId: 'UR20260202PV00000066', clientId: 'CT2016336879406353129', failedReason: null } },
  },
  USER_SUPPLEMENT: {
    // extParam 为 JSON 字符串，documents 每项字段与创建用户一致；仅 TAZAPAY 支持
    // 示例与上方展开的字段保持一致：只给渠道当前实际需要的 fileType/fileId/name/describe，避免业务方照抄到多余字段
    request: { requestId: 'req-supplement-20260724-001', channelCode: 'TAZAPAY', externalUserId: 'tazapay_1784886814', userType: 'BUSINESS', extParam: JSON.stringify({ documents: [{ fileType: 'registration_documents', fileId: 'https://your-s3/presigned/registration_documents.png', name: 'registration_documents.png', describe: 'proof of address' }] }) },
    response: { code: 200, subCode: '000000', msg: 'Success', requestId: 'req-supplement-20260724-001', data: { userId: 'UR20260724TZ00000160', externalUserId: 'tazapay_1784882706', status: 'FAILED', failedReason: 'Additional documents required for KYB review; Documents required: 1) proof_of_address' } },
  },
  USER_QUERY: {
    request: { requestId: 'RequestId_000001', userId: 'UR20260130PV00000002', externalUserId: 'USERID_100015' },
    response: { code: 200, subCode: '000000', msg: 'Success', requestId: 'RequestId_000001', data: { clientId: 'CT2016336879406353129', channelUserId: 'UT2016336879406353130', externalUserId: 'USERID_100015', status: 'DENIED', userId: 'UR20260130PV00000002' } },
  },
  BALANCE_QUERY: {
    request: { channelCode: 'TRANSFI', currency: 'PHP', requestId: 'REQ_20260129_00002' },
    response: { code: 200, subCode: '000000', msg: 'Success', requestId: 'REQ_20260129_00002', data: [{ channelCode: 'TRANSFI', currency: 'PHP', availableBalance: '236418.39', balanceDetails: [{ type: 'AVAILABLE_PREFUNDING', amount: '236418.39' }, { type: 'COLLECTIONS', amount: '1960.00' }] }] },
  },
  RATE_QUERY: {
    request: { originCurrency: 'USD', targetCurrency: 'NGN', channelCode: 'TAZAPAY', destinationCountry: 'NG', amount: 10000, payoutType: 'LOCAL_PAYMENT_NETWORK', requestId: 'REQ_20260418_00001' },
    response: { code: 200, subCode: '000000', msg: 'Success', requestId: 'REQ_20260418_00001', data: { exchangeRate: '1520.5000000000', expireTime: '1770031852995', originCurrency: 'USD', rateId: 'poq_abc123def456', targetCurrency: 'NGN' } },
  },
  MMO_LIST: {
    request: { channelCode: 'KORAPAY', currency: 'KES', requestId: 'req_mmo_list_01' },
    response: { code: 200, subCode: '000000', msg: 'success', requestTime: 1702598400000, requestId: 'req_mmo_list_01', data: [{ name: 'SAFARICOM', slug: 'safaricom-ke', code: '0001', country: 'KE', min: '10.00', max: '250000.00' }, { name: 'AIRTEL', slug: 'airtel-ke', code: '0002', country: 'KE', min: '10.00', max: '150000.00' }] },
  },
  PAYOUT_REQUIREMENTS: {
    request: { channelCode: 'TAZAPAY', country: 'US', currency: 'USD', requestId: 'req_payout_req_001' },
    response: { code: 200, subCode: '000000', msg: 'Success', requestId: 'req_payout_req_001', data: { country: 'US', currency: 'USD', channelCode: 'TAZAPAY', payoutMethods: [{ payoutType: 'swift', beneficiaryTypes: ['individual', 'business'], requiredFields: { mainFields: ['accountName', 'bankName', 'accountNumber', 'bic'], extParamFields: null }, recommendedFields: { mainFields: null, extParamFields: ['addressLine1', 'city', 'state', 'postalCode'] }, transferLimit: { currency: 'USD', minimum: '1.00', maximum: '100000000.00' }, fundTransferNetworks: null }, { payoutType: 'local', beneficiaryTypes: ['individual', 'business'], requiredFields: { mainFields: ['accountName', 'bankName', 'accountNumber'], extParamFields: ['abaCode'] }, recommendedFields: { mainFields: ['bic'], extParamFields: ['addressLine1', 'city', 'state', 'postalCode'] }, transferLimit: { currency: 'USD', minimum: '0.01', maximum: '10000000.00' }, fundTransferNetworks: [{ name: 'fedach', description: 'FedACH batch clearing', transferLimit: { currency: 'USD', minimum: '0.01', maximum: '1000000.00' } }, { name: 'fedwire', description: 'Fedwire real-time RTGS', transferLimit: { currency: 'USD', minimum: '0.01', maximum: '10000000.00' } }] }] } },
  },
};

// ─── TRANSFI 专属参数覆盖 ───
// 选中 TRANSFI 渠道时，用下方 TransFi 专属定义覆盖 BASE_PARAMS / RESPONSE_PARAMS，
// 使页面只展示与 TransFi 相关的枚举/参数/描述，不再混入其它渠道信息。
// 仅当 selectedChannel === 'TRANSFI' 时生效，对其它渠道零影响。
// 说明：TransFi 面向所有调用方（不特指 Banxa），涉及第三方 KYC token 一律表述为"由调用方提供"。
const TRANSFI_PARAM_OVERRIDES = {
  USER_CREATE: {
    request: [
      { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
      { name: 'operateType', label: '操作类型', type: 'ENUM', required: true, tip: 'CREATE' },
      { name: 'channelCode', label: '渠道代码', type: 'ENUM', required: true, tip: 'TRANSFI' },
      { name: 'externalUserId', label: '外部用户ID', type: 'STRING', required: true, maxLen: 64 },
      { name: 'userType', label: '用户类型', type: 'ENUM', required: true, tip: 'INDIVIDUAL（本期仅支持个人）' },
      { name: 'extParam', label: '扩展参数', type: 'JSON_STRING', required: true, tip: '必填，JSON 格式字符串，见下方 extParam 差异字段。含个人信息(firstName/lastName/date/email/phone/phoneCode/country/address)及必填 sumsubToken（Sumsub 共享 KYC token，由调用方提供，缺失前置拦截）' },
    ],
    response: [
      { name: 'userId', label: '用户ID', type: 'STRING', required: true, tip: '平台用户ID（如 UR20260727TF00000132），后续下单用它关联' },
      { name: 'channelUserId', label: '渠道用户ID', type: 'STRING', required: false, tip: 'TransFi 侧用户ID（如 UX-...），创建/回调成功后返回' },
      { name: 'externalUserId', label: '外部用户ID', type: 'STRING', required: true, tip: '请求传入的外部用户ID' },
      { name: 'status', label: '状态', type: 'STRING', required: true, tip: 'PROCESSING / SUCCESS / FAILED / DENIED' },
      { name: 'failedReason', label: '失败原因', type: 'STRING', required: false, tip: '失败时返回，成功为空串' },
    ],
  },
  USER_QUERY: {
    request: [
      { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
      { name: 'channelCode', label: '渠道代码', type: 'STRING', required: false, tip: 'TRANSFI；按 externalUserId 查询时建议指定渠道，避免跨渠道歧义' },
      { name: 'externalUserId', label: '外部用户ID', type: 'STRING', required: false, tip: '与 userId 至少传一个' },
      { name: 'userId', label: '用户ID', type: 'STRING', required: false, tip: '与 externalUserId 至少传一个' },
    ],
    response: [
      { name: 'userId', label: '用户ID', type: 'STRING', required: true, tip: '平台用户ID（如 UR20260727TF00000132）' },
      { name: 'channelUserId', label: '渠道用户ID', type: 'STRING', required: false, tip: 'TransFi 侧用户ID（如 UX-...）' },
      { name: 'externalUserId', label: '外部用户ID', type: 'STRING', required: true },
      { name: 'status', label: '状态', type: 'STRING', required: true, tip: 'PROCESSING / SUCCESS / FAILED / DENIED' },
      { name: 'failedReason', label: '失败原因', type: 'STRING', required: false, tip: '失败时返回，成功为空串' },
    ],
  },
  DEPOSIT_CREATE: {
    request: [
      { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
      { name: 'externalUserId', label: '外部用户ID', type: 'STRING', required: true, maxLen: 64 },
      { name: 'externalOrderId', label: '外部订单号', type: 'STRING', required: true, maxLen: 256, tip: '幂等 key，按 (商户+externalOrderId) 幂等' },
      { name: 'accountId', label: '账户ID', type: 'STRING', required: false, tip: 'TransFi 不需要平台开户，非必填' },
      { name: 'currency', label: '币种', type: 'ENUM', required: true, tip: 'GHS / IDR / KES / PHP / TZS / UGX / VND / XOF / ZMW' },
      { name: 'amount', label: '金额', type: 'DECIMAL', required: true, tip: '正数；平台按 币种+支付编码 校验上下限，超限拦截返回 min/max' },
      { name: 'channelCode', label: '渠道代码', type: 'ENUM', required: true, tip: 'TRANSFI' },
      { name: 'depositMethod', label: '充值方式/支付编码', type: 'STRING', required: true, tip: 'CRYPTO_ONRAMP（onramp 法币→稳定币）; 其余值均为 payin(法币→法币本币)——银行转账用 BANK_TRANSFER+extParam.bankCode，钱包类直接填钱包编码(大写，如 GCASH/OVO/DANA/QRIS 等)' },
      { name: 'callbackUrl', label: '回调地址', type: 'STRING', required: true, maxLen: 2048, tip: '必须以 http:// 或 https:// 开头' },
      { name: 'extParam', label: '扩展参数', type: 'JSON_STRING', required: true, tip: 'JSON 格式字符串，见下方 extParam 差异字段。必填 userId/purposeCode/successUrl/failureUrl；BANK_TRANSFER 时必填 bankCode；onramp 必填 cryptoCurrency/walletAddress/walletOwner' },
    ],
    response: [
      { name: 'orderId', label: '充值订单ID', type: 'STRING', required: true },
      { name: 'externalOrderId', label: '外部订单号', type: 'STRING', required: true },
      { name: 'status', label: '状态', type: 'STRING', required: true, tip: 'PROCESSING / SUCCESS / FAILED' },
    ],
  },
  WITHDRAW_CREATE: {
    request: [
      { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
      { name: 'externalUserId', label: '外部用户ID', type: 'STRING', required: true, maxLen: 64 },
      { name: 'externalOrderId', label: '外部订单号', type: 'STRING', required: true, maxLen: 256, tip: '幂等 key，按 (商户+externalOrderId) 幂等' },
      { name: 'currency', label: '币种', type: 'ENUM', required: true, tip: 'GHS / IDR / KES / PHP / TZS / UGX / VND / XOF / ZMW' },
      { name: 'amount', label: '金额', type: 'DECIMAL', required: true, tip: '法币目标金额（用户实收）；平台按 币种+支付编码 校验上下限，超限拦截返回 min/max' },
      { name: 'channelCode', label: '渠道代码', type: 'ENUM', required: true, tip: 'TRANSFI' },
      { name: 'withdrawMethod', label: '提现方式/支付编码', type: 'STRING', required: true, tip: 'CRYPTO_OFFRAMP（offramp 稳定币→法币）; 其余值均为 payout(法币→法币本币，扣 prefunding)——银行转账用 BANK_TRANSFER+extParam.bankCode，钱包类直接填钱包编码(大写，如 GCASH/OVO/DANA/SHOPEEPAY 等)' },
      { name: 'callbackUrl', label: '回调地址', type: 'STRING', required: true, maxLen: 2048, tip: '必须以 http:// 或 https:// 开头' },
      { name: 'extParam', label: '扩展参数', type: 'JSON_STRING', required: true, tip: 'JSON 格式字符串，见下方 extParam 差异字段。必填 userId/purposeCode；BANK_TRANSFER 时必填 bankCode；payout 收款信息(bank_transfer:accountNumber；local_wallet:phone+phoneCode)；offramp 必填 cryptoCurrency' },
    ],
    response: [
      { name: 'orderId', label: '提现订单ID', type: 'STRING', required: true, tip: '系统订单号' },
      { name: 'status', label: '状态', type: 'STRING', required: true, tip: 'PROCESSING / SUCCESS / FAILED' },
    ],
  },
  BALANCE_QUERY: {
    request: [
      { name: 'requestId', label: '请求ID', type: 'STRING', required: false },
      { name: 'channelCode', label: '渠道代码', type: 'ENUM', required: true, tip: 'TRANSFI' },
      { name: 'currency', label: '币种', type: 'STRING', required: false, tip: '支持 GHS/IDR/KES/PHP/TZS/UGX/VND/XOF/ZMW；不传或传 ALL 返回全部币种底仓' },
    ],
    response: [
      { name: 'channelCode', label: '渠道编码', type: 'STRING', required: true },
      { name: 'currency', label: '币种', type: 'STRING', required: true },
      { name: 'availableBalance', label: '可用余额', type: 'STRING', required: true, tip: '主余额，取可用预充值底仓（totalAvailablePrefundingBalance）' },
      { name: 'balanceDetails', label: '多维度余额明细', type: 'ARRAY', required: false, tip: '数组；仅在有多种余额概念时返回。明细项字段见下', children: [
        { name: 'type', label: '余额类型', type: 'STRING', required: true, tip: 'AVAILABLE_PREFUNDING（可用预充值底仓）/ COLLECTIONS（收单归集金额）' },
        { name: 'amount', label: '金额', type: 'STRING', required: true },
      ] },
    ],
  },
  RATE_QUERY: {
    request: [
      { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
      { name: 'channelCode', label: '渠道代码', type: 'ENUM', required: true, tip: 'TRANSFI' },
      { name: 'originCurrency', label: '原始币种', type: 'STRING', required: true },
      { name: 'targetCurrency', label: '目标币种', type: 'STRING', required: true },
      { name: 'orderType', label: '订单类型', type: 'ENUM', required: true, tip: 'onramp(法币→稳定币) / offramp(稳定币→法币)，决定报价方向' },
      { name: 'amount', label: '报价金额', type: 'LONG', required: true, tip: '报价金额' },
      { name: 'payeeType', label: '收款人类型', type: 'STRING', required: false, tip: '映射为渠道 paymentType: bank_transfer / local_wallet / card / DEFAULT' },
      { name: 'fundTransferNetwork', label: '资金转账网络', type: 'STRING', required: false, tip: '映射为渠道 paymentCode: onramp 用源法币支付编码(如 gcash)、offramp 用目标法币出款编码(如 bpi)' },
    ],
    response: [
      { name: 'exchangeRate', label: '汇率', type: 'STRING', required: true, tip: '格式: 0.0000000000' },
      { name: 'originCurrency', label: '原始币种', type: 'STRING', required: true },
      { name: 'targetCurrency', label: '目标币种', type: 'STRING', required: true },
    ],
  },
  CHANNEL_CONFIG_QUERY: {
    request: [
      { name: 'requestId', label: '请求ID', type: 'STRING', required: true, maxLen: 64 },
      { name: 'channelCode', label: '渠道代码', type: 'STRING', required: true, tip: 'TRANSFI' },
      { name: 'currency', label: '币种', type: 'STRING', required: false, tip: '不传返回全部已接入币种；支持 GHS/IDR/KES/PHP/TZS/UGX/VND/XOF/ZMW' },
    ],
  },
};

// TRANSFI 渠道选中时，接口顶部一句话描述（覆盖通用 desc 里的"支持 PYVIO / TAZAPAY"等其它渠道措辞）
const TRANSFI_API_DESC = {
  USER_CREATE: '创建 TransFi 个人用户（本期仅 INDIVIDUAL）。payin/payout 下单前置：需先创建用户且 KYC=user_approved。',
  DEPOSIT_CREATE: 'TransFi 充值：onramp(法币→稳定币) / payin(法币→法币本币)。订单类型由 depositMethod 反推，上游无需传 orderType。',
  WITHDRAW_CREATE: 'TransFi 提现：offramp(稳定币→法币) / payout(法币→法币本币，扣 prefunding)。订单类型由 withdrawMethod 反推。',
  USER_QUERY: '查询 TransFi 渠道用户（按 userId 或 externalUserId+渠道定位），返回平台/渠道用户ID与状态。',
  BALANCE_QUERY: '查询 TransFi 各币种可用预充值底仓余额（MID 维度）。',
  RATE_QUERY: '查询 TransFi 汇率报价（onramp / offramp）。',
  CHANNEL_CONFIG_QUERY: '查询 TransFi 渠道配置（支持币种、各币种支付方式/限额/收款必填字段）。',
};

// TRANSFI 渠道选中时的请求/响应示例覆盖（避免可选渠道查询接口沿用其它渠道含 clientId 的示例）
const TRANSFI_API_EXAMPLES = {
  USER_QUERY: {
    request: { requestId: 'req_transfi_user_query_01', channelCode: 'TRANSFI', externalUserId: 'chloe01' },
    response: { code: 200, subCode: '000000', msg: 'Success', requestId: 'req_transfi_user_query_01', data: { userId: 'UR20260727TF00000132', channelUserId: 'UX-260727064549137935', externalUserId: 'chloe01', status: 'SUCCESS', failedReason: '' } },
  },
};

// 会读取渠道元数据（channel_api_metadata）的接口。
//
// 不在此集合中的接口，isChannelSpecific 为 false → metadataFields 直接置空 →
// 即便库里已配了该接口的渠道元数据也完全不会被渲染，页面只显示 BASE_PARAMS 通用文案
// （表现为「渠道下拉没有该渠道」「字段说明写着别家渠道」）。
// 因此新增渠道元数据时，对应 apiCode 必须同时出现在这里。
const CHANNEL_SPECIFIC_APIS = new Set(['ACCOUNT_CREATE', 'CARD_BIND', 'DEPOSIT_CREATE', 'DEPOSIT_CLOSE', 'DEPOSIT_CAPTURE', 'WITHDRAW_CREATE', 'REFUND_CREATE', 'USER_CREATE', 'PAYOUT_REQUIREMENTS',
  // 以下为补充：这些接口库中已有渠道元数据，之前因未登记而被忽略
  'USER_SUPPLEMENT', 'USER_QUERY', 'DEPOSIT_QUERY', 'WITHDRAW_QUERY', 'BALANCE_QUERY', 'FILE_UPLOAD']);

// 查询类接口：支持顶部选渠道并按渠道覆盖请求/响应参数（走 TRANSFI_PARAM_OVERRIDES）。
// 注：这些 apiCode 现已同时在 CHANNEL_SPECIFIC_APIS 中，故也会读取元数据；
// 本集合保留用于「overview 无该接口渠道列表时的兜底候选」逻辑。
const CHANNEL_SELECTABLE_APIS = new Set(['USER_QUERY', 'DEPOSIT_QUERY', 'WITHDRAW_QUERY']);

const getSceneOptions = (t) => [
  { label: t('doc_grpAccount'), options: [
    { value: 'ACCOUNT_CREATE', label: '👤 ' + t('doc_apiAccountCreate') },
    { value: 'ACCOUNT_CLOSE', label: '🚪 ' + t('doc_apiAccountClose') },
    { value: 'ACCOUNT_CLOSE_QUERY', label: '🔍 关户查询' },
    { value: 'ACCOUNT_QUERY', label: '🔍 ' + t('doc_apiAccountQuery') },
  ]},
  { label: t('doc_grpBankAccount'), options: [
    { value: 'PAYOUT_REQUIREMENTS', label: '📋 ' + t('doc_apiPayoutRequirements') },
    { value: 'CARD_BIND', label: '💳 ' + t('doc_apiCardBind') },
    { value: 'CARD_QUERY', label: '🔎 ' + t('doc_apiCardQuery') },
    { value: 'CARD_UNBIND', label: '✂️ ' + t('doc_apiCardUnbind') },
  ]},
  { label: t('doc_grpDeposit'), options: [
    { value: 'DEPOSIT_CREATE', label: '💰 ' + t('doc_apiDepositCreate') },
    { value: 'DEPOSIT_QUERY', label: '📋 ' + t('doc_apiDepositQuery') },
    { value: 'DEPOSIT_CLOSE', label: '🚫 ' + t('doc_apiDepositClose') },
    { value: 'DEPOSIT_CAPTURE', label: '💵 ' + t('doc_apiDepositCapture') },
  ]},
  { label: t('doc_grpWithdraw'), options: [
    { value: 'WITHDRAW_CREATE', label: '🏦 ' + t('doc_apiWithdrawCreate') },
    { value: 'WITHDRAW_QUERY', label: '📄 ' + t('doc_apiWithdrawQuery') },
  ]},
  { label: t('doc_grpRefund'), options: [
    { value: 'REFUND_CREATE', label: '↩️ ' + t('doc_apiRefundCreate') },
    { value: 'REFUND_QUERY', label: '📑 ' + t('doc_apiRefundQuery') },
  ]},
  { label: t('doc_grpUser'), options: [
    { value: 'USER_CREATE', label: '👥 ' + t('doc_apiUserCreate') },
    { value: 'USER_SUPPLEMENT', label: '📎 ' + t('doc_apiUserSupplement') },
    { value: 'USER_QUERY', label: '🧑 ' + t('doc_apiUserQuery') },
  ]},
  { label: t('doc_grpFile'), options: [
    { value: 'FILE_UPLOAD', label: '📤 ' + t('doc_apiFileUpload') },
    { value: 'FILE_STATUS_QUERY', label: '📁 ' + t('doc_apiFileStatusQuery') },
  ]},
  { label: t('doc_grpGeneral'), options: [
    { value: 'CHANNEL_CONFIG_QUERY', label: '⚙️ ' + t('doc_apiChannelConfig') },
    { value: 'ORDER_PAGE', label: '📊 ' + t('doc_apiOrderPage') },
    { value: 'BANK_LIST', label: '🏛️ ' + t('doc_apiBankList') },
    { value: 'BALANCE_QUERY', label: '💵 ' + t('doc_apiBalanceQuery') },
    { value: 'RATE_QUERY', label: '💱 ' + t('doc_apiRateQuery') },
    { value: 'MMO_LIST', label: '📱 ' + t('doc_apiMmoList') },
    { value: 'REMITTER_CREATE', label: '👔 付款人创建' },
    { value: 'REMITTER_QUERY', label: '🔍 付款人查询' },
    { value: 'FUND_TRANSFER_CREATE', label: '🔄 资金调拨创建' },
    { value: 'FUND_TRANSFER_QUERY', label: '📄 资金调拨查询' },
    { value: 'CHANNEL_TRANSACTIONS', label: '📊 渠道流水查询' },
  ]},
];

const getCommonResponseFields = (t) => [
  { name: 'code', label: t('doc_colType') === 'Type' ? 'Status Code' : '状态码', type: 'STRING', required: true, tip: '200 = success' },
  { name: 'subCode', label: t('doc_colType') === 'Type' ? 'Business Code' : '业务状态码', type: 'STRING', required: true, tip: '000000 = success' },
  { name: 'msg', label: t('doc_colType') === 'Type' ? 'Message' : '响应消息', type: 'STRING', required: true, tip: 'e.g. success' },
  { name: 'requestId', label: t('doc_colType') === 'Type' ? 'Request ID' : '请求ID', type: 'STRING', required: true, tip: t('doc_colType') === 'Type' ? 'Echoed from request' : '原样返回请求中的 requestId' },
  { name: 'requestTime', label: t('doc_colType') === 'Type' ? 'Request Time' : '请求时间', type: 'LONG', required: true, tip: t('doc_colType') === 'Type' ? 'Epoch milliseconds' : '毫秒时间戳' },
  { name: 'data', label: t('doc_colType') === 'Type' ? 'Business Data' : '业务数据', type: 'OBJECT', required: false, tip: t('doc_colType') === 'Type' ? 'See individual API response params' : '具体结构见各接口响应参数' },
];

const getErrorCodes = (t) => [
  { code: '000000', desc: t('doc_err000000'), action: t('doc_err000000Action'), cat: 'success' },
  { code: '400001', desc: t('doc_err400001'), action: t('doc_err400001Action'), cat: 'client' },
  { code: '400002', desc: t('doc_err400002'), action: t('doc_err400002Action'), cat: 'client' },
  { code: '400003', desc: t('doc_err400003'), action: t('doc_err400003Action'), cat: 'client' },
  { code: '400004', desc: t('doc_err400004'), action: t('doc_err400004Action'), cat: 'client' },
  { code: '400005', desc: t('doc_err400005'), action: t('doc_err400005Action'), cat: 'client' },
  { code: '400006', desc: t('doc_err400006'), action: t('doc_err400006Action'), cat: 'client' },
  { code: '400201', desc: t('doc_err400201'), action: t('doc_err400201Action'), cat: 'signature' },
  { code: '400301', desc: t('doc_err400301'), action: t('doc_err400301Action'), cat: 'verification' },
  { code: '400401', desc: t('doc_err400401'), action: t('doc_err400401Action'), cat: 'risk' },
  { code: '400402', desc: t('doc_err400402'), action: t('doc_err400402Action'), cat: 'risk' },
  { code: '500001', desc: t('doc_err500001'), action: t('doc_err500001Action'), cat: 'system' },
  { code: '500002', desc: t('doc_err500002'), action: t('doc_err500002Action'), cat: 'system' },
  { code: '500003', desc: t('doc_err500003'), action: t('doc_err500003Action'), cat: 'system' },
  { code: '500004', desc: t('doc_err500004'), action: t('doc_err500004Action'), cat: 'system' },
  { code: '500005', desc: t('doc_err500005'), action: t('doc_err500005Action'), cat: 'system' },
  { code: '500006', desc: t('doc_err500006'), action: t('doc_err500006Action'), cat: 'system' },
  { code: '500007', desc: t('doc_err500007'), action: t('doc_err500007Action'), cat: 'system' },
  { code: '500008', desc: t('doc_err500008'), action: t('doc_err500008Action'), cat: 'system' },
  { code: '500009', desc: t('doc_err500009'), action: t('doc_err500009Action'), cat: 'system' },
  { code: '500010', desc: t('doc_err500010'), action: t('doc_err500010Action'), cat: 'system' },
];

const getErrorCatConfig = (t) => ({
  success: { label: t('doc_catSuccess'), color: '#22c55e', bg: '#f0fdf4' },
  client: { label: t('doc_catClient'), color: '#f59e0b', bg: '#fffbeb' },
  signature: { label: t('doc_catSignature'), color: '#8b5cf6', bg: '#f5f3ff' },
  verification: { label: t('doc_catVerification'), color: '#06b6d4', bg: '#ecfeff' },
  risk: { label: t('doc_catRisk'), color: '#ec4899', bg: '#fdf2f8' },
  system: { label: t('doc_catSystem'), color: '#ef4444', bg: '#fef2f2' },
});

// ─── Method Badge (Checkout.com style) ───
const MethodBadge = ({ method }) => {
  const styles = {
    POST: { bg: '#22c55e', color: '#fff' },
    GET: { bg: '#3b82f6', color: '#fff' },
    PUT: { bg: '#f59e0b', color: '#fff' },
    DELETE: { bg: '#ef4444', color: '#fff' },
  };
  const s = styles[method] || styles.GET;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700,
      fontFamily: "'JetBrains Mono', 'SF Mono', monospace",
      background: s.bg, color: s.color, letterSpacing: '0.5px', lineHeight: '18px',
      minWidth: 42, textAlign: 'center',
    }}>{method}</span>
  );
};

// ─── JSON Editor with format/compress/validate ───
function JsonEditor({ value, onChange, rows = 10, placeholder }) {
  const [error, setError] = useState(null);
  const handleChange = (e) => { onChange?.(e.target.value); setError(null); };
  const handleFormat = () => {
    if (!value) return;
    try { onChange(JSON.stringify(JSON.parse(value), null, 2)); setError(null); }
    catch { setError('JSON 格式错误'); }
  };
  const handleCompress = () => {
    if (!value) return;
    try { onChange(JSON.stringify(JSON.parse(value))); setError(null); }
    catch { setError('JSON 格式错误'); }
  };
  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
        <Button size="small" type="default" onClick={handleFormat} style={{ fontSize: 11, padding: '0 8px', height: 24 }}>✨ 格式化</Button>
        <Button size="small" type="default" onClick={handleCompress} style={{ fontSize: 11, padding: '0 8px', height: 24 }}>📦 压缩</Button>
        {error && <span style={{ fontSize: 11, color: '#ef4444', lineHeight: '24px' }}>⚠️ {error}</span>}
        {!error && value && (() => { try { const arr = JSON.parse(value); return <span style={{ fontSize: 11, color: '#22c55e', lineHeight: '24px' }}>✓ {Array.isArray(arr) ? `${arr.length} items` : 'valid'}</span>; } catch { return null; } })()}
      </div>
      <Input.TextArea
        value={value} onChange={handleChange} rows={rows} placeholder={placeholder}
        style={{ fontFamily: "'JetBrains Mono', 'SF Mono', Consolas, monospace", fontSize: 12, lineHeight: 1.6, background: '#fafbfc', border: error ? '1px solid #ef4444' : undefined }}
      />
    </div>
  );
}

// ─── Dark Code Panel (Checkout.com style right panel) ───
function DarkCodePanel({ title, data, t }) {
  const [copied, setCopied] = useState(false);
  const jsonStr = JSON.stringify(data, null, 2);
  const handleCopy = () => {
    navigator.clipboard.writeText(jsonStr);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div style={{ borderRadius: 8, overflow: 'hidden', border: `1px solid ${COLORS.codeBg}`, marginBottom: 16 }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 14px', background: COLORS.codeHeaderBg, borderBottom: `1px solid rgba(255,255,255,0.06)`,
      }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</span>
        <Button size="small" type="text" icon={copied ? <CheckOutlined style={{ color: '#22c55e' }} /> : <CopyOutlined />}
          onClick={handleCopy}
          style={{ fontSize: 11, color: copied ? '#22c55e' : '#64748b', padding: '0 4px', height: 22 }}>
          {copied ? t('doc_copied') : t('doc_copy')}
        </Button>
      </div>
      <div style={{ background: COLORS.codeBg, padding: '12px 14px', maxHeight: 500, overflow: 'auto' }}>
        <ReactJson src={data} name={false} collapsed={2} displayDataTypes={false} enableClipboard={false}
          theme="ocean" style={{ background: 'transparent', fontSize: 12, fontFamily: "'JetBrains Mono', 'SF Mono', monospace" }} />
      </div>
    </div>
  );
}

// ─── Channel Capability Matrix ───
function ChannelCapabilityMatrix({ t }) {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterName, setFilterName] = useState(null);
  const [filterBizType, setFilterBizType] = useState(null);
  const [filterRegion, setFilterRegion] = useState(null);
  const [detailData, setDetailData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await metaApi.getChannelConfigs();
        const body = res.data;
        const list = body?.data?.responseBody || body?.responseBody || body?.data || (Array.isArray(body) ? body : []);
        setConfigs(list);
      } catch (e) { console.error('Failed to load channel configs', e); }
      finally { setLoading(false); }
    })();
  }, []);

  const nameOptions = useMemo(() => [...new Set(configs.map(c => c.channelName || c.channelCode).filter(Boolean))].sort().map(n => {
    const row = configs.find(c => (c.channelName || c.channelCode) === n);
    const l = getChannelLogo(row?.channelCode || n);
    return { label: `${l.emoji} ${n}`, value: n };
  }), [configs]);

  const regionOptions = useMemo(() => [...new Set(configs.map(c => c.region).filter(Boolean))].sort().map(r => ({ label: r, value: r })), [configs]);

  const filtered = useMemo(() => configs.filter(c => {
    if (filterName && (c.channelName || c.channelCode) !== filterName) return false;
    if (filterBizType && c.businessType !== filterBizType) return false;
    if (filterRegion && c.region !== filterRegion) return false;
    return true;
  }), [configs, filterName, filterBizType, filterRegion]);

  // Stats
  const uniqueChannels = useMemo(() => new Set(configs.map(c => c.channelCode)).size, [configs]);
  const uniqueRegions = useMemo(() => new Set(configs.map(c => c.region).filter(Boolean)).size, [configs]);
  const uniqueCurrencies = useMemo(() => new Set(configs.map(c => c.currency).filter(Boolean)).size, [configs]);

  const typeColor = { BANK: 'blue', WALLET: 'purple', PSP: 'orange' };
  const bizColor = { ALL: 'blue', BUSINESS: 'cyan', INDIVIDUAL: 'geekblue' };

  const capDot = (active) => (
    <span style={{
      display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
      background: active ? '#22c55e' : '#e5e7eb',
      boxShadow: active ? '0 0 0 3px rgba(34,197,94,0.15)' : 'none',
      transition: 'all 0.2s',
    }} />
  );

  const capPill = (label, active) => (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      fontSize: 11, fontWeight: 500, lineHeight: '22px',
      padding: '0 8px', borderRadius: 12, whiteSpace: 'nowrap',
      background: active ? 'rgba(34,197,94,0.08)' : '#f9fafb',
      color: active ? '#15803d' : '#c0c5cc',
      border: `1px solid ${active ? 'rgba(34,197,94,0.2)' : '#eef0f3'}`,
      transition: 'all 0.2s',
    }}>
      {capDot(active)} {label}
    </span>
  );

  const statCard = (icon, value, label, color) => (
    <div style={{
      flex: 1, minWidth: 140, padding: '16px 20px',
      background: '#fff', borderRadius: 10,
      border: '1px solid #eef0f3',
      display: 'flex', alignItems: 'center', gap: 14,
      boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10,
        background: `${color}10`, display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        fontSize: 18,
      }}>{icon}</div>
      <div>
        <div style={{ fontSize: 24, fontWeight: 700, color: COLORS.textPrimary, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
        <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 3, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
      </div>
    </div>
  );

  return (
    <div>
      {/* Stats row */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {statCard('🏦', uniqueChannels, t('doc_statChannels'), '#635bff')}
        {statCard('🌍', uniqueRegions, t('doc_statRegions'), '#3b82f6')}
        {statCard('💱', uniqueCurrencies, t('doc_statCurrencies'), '#f59e0b')}
        {statCard('📋', configs.length, t('doc_statConfigurations'), '#22c55e')}
      </div>

      {/* Filter bar */}
      <div style={{
        marginBottom: 16, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap',
        padding: '12px 16px', background: '#fff', borderRadius: 10,
        border: '1px solid #eef0f3', boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
      }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.textSecondary, marginRight: 4 }}>{t('doc_filterLabel')}</span>
        <Select placeholder={t('doc_colChannel')} allowClear showSearch style={{ width: 200 }} onChange={setFilterName} options={nameOptions} size="middle" variant="outlined" />
        <Select placeholder={t('doc_colRegion')} allowClear style={{ width: 130 }} onChange={setFilterRegion} options={regionOptions} size="middle" variant="outlined" />
        <Select placeholder={t('doc_channelType')} allowClear style={{ width: 150 }} onChange={setFilterBizType} options={['ALL', 'BUSINESS', 'INDIVIDUAL'].map(v => ({ label: v, value: v }))} size="middle" variant="outlined" />
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{
            fontSize: 12, fontWeight: 600, color: COLORS.accent,
            background: COLORS.accentBg, padding: '3px 10px', borderRadius: 12,
          }}>{filtered.length}</span>
          <span style={{ fontSize: 12, color: COLORS.textMuted }}>/ {configs.length}</span>
        </div>
      </div>

      {/* Table */}
      <div style={{
        background: '#fff', borderRadius: 10, border: '1px solid #eef0f3',
        overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
      }}>
        <Table dataSource={filtered} rowKey={(r, i) => `${r.channelCode}_${r.currency}_${i}`} size="middle" loading={loading} scroll={{ x: 1500 }}
          pagination={{ pageSize: 15, showSizeChanger: true, showTotal: n => `${n} items`, size: 'small' }}
          className="channel-matrix-table"
          columns={[
            { title: t('doc_colChannel'), dataIndex: 'channelName', width: 180, fixed: 'left',
              render: (v, r) => {
                const logo = getChannelLogo(r.channelCode);
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: 8,
                      background: `${logo.color}12`, display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                      fontSize: 15, flexShrink: 0,
                      border: `1px solid ${logo.color}20`,
                    }}>{logo.emoji}</div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13, color: COLORS.textPrimary, lineHeight: 1.3 }}>{logo.name || v || r.channelCode}</div>
                      {r.channelType && <span style={{ fontSize: 11, color: logo.color, fontWeight: 500 }}>{r.channelType}</span>}
                    </div>
                  </div>
                );
              },
            },
            { title: t('doc_colEntity'), dataIndex: 'entity', width: 110,
              render: v => v ? <span style={{ fontWeight: 500 }}>{v}</span> : <span style={{ color: '#d1d5db' }}>—</span>,
            },
            { title: t('doc_colKeyFunction'), dataIndex: 'keyFunction', width: 170, ellipsis: true,
              render: v => v ? <Tooltip title={v}><span>{v}</span></Tooltip> : <span style={{ color: '#d1d5db' }}>—</span>,
            },
            { title: t('doc_colRegion'), dataIndex: 'region', width: 80, align: 'center',
              render: v => v ? <span style={{ fontWeight: 500, color: COLORS.textSecondary }}>{v}</span> : <span style={{ color: '#d1d5db' }}>—</span>,
            },
            { title: t('doc_colCountry'), dataIndex: 'country', width: 90,
              render: v => v || <span style={{ color: '#d1d5db' }}>—</span>,
            },
            { title: t('doc_colCurrency'), dataIndex: 'currency', width: 120,
              render: v => {
                if (!v) return <span style={{ color: '#d1d5db' }}>—</span>;
                const currencies = v.split(',').map(c => c.trim());
                if (currencies.length <= 2) {
                  return <span>{currencies.map(c => `${getCurrencyIcon(c)} ${c}`).join(', ')}</span>;
                }
                return (
                  <Tooltip title={currencies.map(c => `${getCurrencyIcon(c)} ${c}`).join(', ')}>
                    <span>{getCurrencyIcon(currencies[0])} {currencies[0]}</span>
                    <span style={{ fontSize: 11, color: COLORS.accent, fontWeight: 600, marginLeft: 4, background: COLORS.accentBg, padding: '1px 6px', borderRadius: 8 }}>+{currencies.length - 1}</span>
                  </Tooltip>
                );
              },
            },
            { title: t('doc_colPaymentMethod'), dataIndex: 'paymentMethod', width: 120,
              render: v => v ? <span style={{ fontFamily: "'JetBrains Mono', 'SF Mono', monospace", fontSize: 12, color: '#475569' }}>{v}</span> : <span style={{ color: '#d1d5db' }}>—</span>,
            },
            { title: t('doc_colUserType'), dataIndex: 'businessType', width: 90, align: 'center',
              render: v => v ? <Tag color={bizColor[v] || 'default'} style={{ margin: 0, borderRadius: 12, fontSize: 11 }}>{v}</Tag> : <span style={{ color: '#d1d5db' }}>—</span>,
            },
            { title: t('doc_capabilities'), width: 300,
              render: (_, r) => (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {capPill(t('doc_colOpenAccount'), r.needOpenAccount)}
                  {capPill(t('doc_colDeposit'), r.supportDeposit)}
                  {capPill(t('doc_colWithdraw'), r.supportWithdraw)}
                  {capPill(t('doc_colRefund'), r.supportRefund)}
                  {capPill(t('doc_colPartialRefund'), r.supportPartialRefund)}
                  {capPill(t('doc_colSameNameVa'), r.sameNameVa)}
                </div>
              ),
            },
            { title: '', key: 'action', width: 60, fixed: 'right', align: 'center',
              render: (_, r) => (
                <Button type="text" size="small" onClick={() => setDetailData(r)}
                  style={{ color: COLORS.accent, fontWeight: 500, fontSize: 12, padding: '2px 8px', borderRadius: 6 }}
                  icon={<RightOutlined style={{ fontSize: 10 }} />}
                />
              ),
            },
          ]}
        />
      </div>

      {/* Detail Modal — redesigned */}
      <Modal
        open={!!detailData} onCancel={() => setDetailData(null)} footer={null}
        width={620} destroyOnClose
        title={null} closable
        styles={{ body: { padding: 0 } }}
      >
        {detailData && (
          <div>
            {/* Modal header */}
            <div style={{
              padding: '24px 28px 20px', borderBottom: '1px solid #f0f2f5',
              display: 'flex', alignItems: 'center', gap: 16,
            }}>
              <div style={{
                width: 48, height: 48, borderRadius: 12,
                background: `${getChannelLogo(detailData.channelCode).color}12`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 22, border: `1px solid ${getChannelLogo(detailData.channelCode).color}20`,
              }}>{getChannelLogo(detailData.channelCode).emoji}</div>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, color: COLORS.textPrimary }}>{detailData.channelName || detailData.channelCode}</div>
                <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                  {detailData.channelType && <Tag color={typeColor[detailData.channelType]} style={{ borderRadius: 12, fontSize: 11 }}>{detailData.channelType}</Tag>}
                  {detailData.businessType && <Tag color={bizColor[detailData.businessType]} style={{ borderRadius: 12, fontSize: 11 }}>{detailData.businessType}</Tag>}
                </div>
              </div>
            </div>

            {/* Modal body */}
            <div style={{ padding: '20px 28px 28px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 24px', marginBottom: 24 }}>
                {[
                  [t('doc_colEntity'), detailData.entity],
                  [t('doc_colKeyFunction'), detailData.keyFunction],
                  [t('doc_colRegion'), detailData.region],
                  [t('doc_colCountry'), detailData.country],
                  [t('doc_colCurrency'), detailData.currency ? `${getCurrencyIcon(detailData.currency)} ${detailData.currency}` : null],
                  [t('doc_colPaymentMethod'), detailData.paymentMethod],
                ].map(([label, value], i) => (
                  <div key={i}>
                    <div style={{ fontSize: 11, fontWeight: 500, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: 4 }}>{label}</div>
                    <div style={{ fontSize: 13.5, fontWeight: 500, color: value ? COLORS.textPrimary : '#d1d5db' }}>{value || '—'}</div>
                  </div>
                ))}
              </div>

              <div style={{ borderTop: '1px solid #f0f2f5', paddingTop: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 500, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: 10 }}>{t('doc_capabilities')}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {capPill(t('doc_colOpenAccount'), detailData.needOpenAccount)}
                  {capPill(t('doc_colDeposit'), detailData.supportDeposit)}
                  {capPill(t('doc_colWithdraw'), detailData.supportWithdraw)}
                  {capPill(t('doc_colRefund'), detailData.supportRefund)}
                  {capPill(t('doc_colPartialRefund'), detailData.supportPartialRefund)}
                  {capPill(t('doc_colSameNameVa'), detailData.sameNameVa)}
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>

      <style>{`
        .channel-matrix-table .ant-table-tbody > tr:last-child > td {
          border-bottom: none !important;
        }
      `}</style>
    </div>
  );
}

// ─── Copy JSON Button ───
function CopyJsonBtn({ data, t }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => { navigator.clipboard.writeText(JSON.stringify(data, null, 2)); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <Button size="small" type="text" icon={copied ? <CheckOutlined style={{ color: '#22c55e' }} /> : <CopyOutlined />} onClick={handleCopy} style={{ fontSize: 12, color: copied ? '#22c55e' : '#94a3b8' }}>
      {copied ? t('doc_copied') : t('doc_copy')}
    </Button>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT — Checkout.com-inspired 3-panel layout
// ═══════════════════════════════════════════════════════════════
export default function ApiDocumentation({ isPublic = false }) {
  const { t, language } = useLanguage();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedApiCode, setSelectedApiCode] = useState('ACCOUNT_CREATE');
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [metaLoading, setMetaLoading] = useState(false);
  const [userChannels, setUserChannels] = useState([]);
  const [metaCache, setMetaCache] = useState({});
  const [activeSection, setActiveSection] = useState('overview');
  const [webhookEventType, setWebhookEventType] = useState('ACCOUNT');
  const [corridorCountry, setCorridorCountry] = useState('');
  const [corridorCurrency, setCorridorCurrency] = useState('');
  const [corridorRequirements, setCorridorRequirements] = useState(null);
  const [corridorRawResponse, setCorridorRawResponse] = useState(null);
  const [corridorLoading, setCorridorLoading] = useState(false);
  const [selectedPayoutType, setSelectedPayoutType] = useState(null);

  // Country → primary currencies mapping
  const COUNTRY_CURRENCIES = {
    US: ['USD'], GB: ['GBP','EUR'], HK: ['HKD','USD'], SG: ['SGD','USD'],
    IN: ['INR'], JP: ['JPY'], AU: ['AUD'], CA: ['CAD'],
    DE: ['EUR'], FR: ['EUR'], CH: ['CHF','EUR'], NZ: ['NZD'],
    BR: ['BRL'], MX: ['MXN'], AE: ['AED'], SA: ['SAR'],
    ID: ['IDR'], TH: ['THB'], PH: ['PHP'], MY: ['MYR'],
    VN: ['VND'], KR: ['KRW'], CN: ['CNY'], NG: ['NGN'],
    KE: ['KES'], ZA: ['ZAR'], SE: ['SEK'], NO: ['NOK'],
    DK: ['DKK'], PL: ['PLN'], IL: ['ILS'], TR: ['TRY'],
  };
  const corridorCurrencyOptions = corridorCountry && COUNTRY_CURRENCIES[corridorCountry]
    ? COUNTRY_CURRENCIES[corridorCountry].map(c => ({ value: c, label: `${getCurrencyIcon(c)} ${c}` }))
    : [];

  const API_CODE_LABELS = useMemo(() => getApiCodeLabels(t), [language]);
  const SCENE_OPTIONS = useMemo(() => getSceneOptions(t), [language]);
  const COMMON_RESPONSE_FIELDS = useMemo(() => getCommonResponseFields(t), [language]);
  const ERROR_CODES = useMemo(() => getErrorCodes(t), [language]);
  const ERROR_CAT_CONFIG = useMemo(() => getErrorCatConfig(t), [language]);

  const isEn = language === 'en-US';
  const localizeFields = (fields) => {
    if (!isEn || !fields) return fields;
    return fields.map(f => ({ ...f, label: fieldLabelMap[f.label] || f.label, tip: f.tip ? (fieldTipMap[f.tip] || f.tip) : f.tip }));
  };

  /**
   * 过滤 tip 文本，只保留与选中渠道相关的部分
   * tip 格式: "EQUALS_MONEY/SGB必填; TAZAPAY按走廊动态要求; PYVIO仅USD必填"
   * 选中 TAZAPAY 后只显示 "TAZAPAY按走廊动态要求"
   * 如果 tip 是纯枚举值（A / B / C）或不含渠道名，原样返回
   */
  const filterTipByChannel = (tip, channel) => {
    if (!tip || !channel) return tip;
    // 纯枚举值（不含分号，只有 / 分隔的大写值）
    if (!tip.includes(';') && /^[A-Z_]+ \/ [A-Z_]/.test(tip)) return tip;
    // 按分号分割，找包含当前渠道名的片段
    if (tip.includes(';')) {
      const parts = tip.split(/;\s*/);
      const matched = parts.filter(p => p.toUpperCase().includes(channel.toUpperCase()));
      if (matched.length > 0) return matched.join('; ');
      // 没有匹配到任何渠道名的片段，可能是通用描述，原样返回
      return tip;
    }
    // 不含分号的单条 tip，如果包含其他渠道名但不包含当前渠道，隐藏
    // 检测是否包含渠道名模式（大写字母+下划线）
    const channelPattern = /\b[A-Z][A-Z_]+\b/g;
    const mentionedChannels = tip.match(channelPattern) || [];
    const knownChannels = ['EQUALS_MONEY', 'PAGSMILE', 'SGB', 'PYVIO', 'LEAN_TECH', 'KORAPAY', 'PALMPAY', 'BANK_FRICK', 'BANKING_CIRCLE', 'INSWITCH', 'TAZAPAY', 'BITSO', 'CVPAY', 'CALLPAY', 'LATAM_GATEWAY', 'MDAQ', 'CORPAY', 'DA5', 'MOOLAHGO', 'TRANSFI', 'AVENIA', 'GOWD', 'PAYPAL', 'ZAND_BANK', 'DLOCAL'];
    const mentionsOtherChannel = mentionedChannels.some(m => knownChannels.includes(m) && m !== channel);
    const mentionsCurrentChannel = mentionedChannels.some(m => m === channel);
    if (mentionsOtherChannel && !mentionsCurrentChannel) return null;
    return tip;
  };

  const isChannelSpecific = selectedApiCode && CHANNEL_SPECIFIC_APIS.has(selectedApiCode);
  // 是否显示顶部渠道选择器：渠道专属接口 或 支持按渠道覆盖的查询类接口
  const showChannelSelector = isChannelSpecific || (selectedApiCode && CHANNEL_SELECTABLE_APIS.has(selectedApiCode));

  const [initialized, setInitialized] = useState(false);
  useEffect(() => {
    if (!initialized && overview) {
      const apiChannels = overview.channelsByApi?.['ACCOUNT_CREATE'] || [];
      const candidates = userChannels.length > 0 ? userChannels.filter(ch => apiChannels.includes(ch)) : apiChannels;
      if (candidates[0]) setSelectedChannel(candidates[0]);
      setInitialized(true);
    }
  }, [userChannels, overview, initialized]);

  useEffect(() => { loadOverview(); if (!isPublic) loadUserChannels(); }, []);

  const loadUserChannels = async () => {
    try {
      const { data } = await testApi.getChannelCurrencies();
      const responseData = data.data;
      const channelCurrencyMap = responseData?.responseBody || responseData || {};
      setUserChannels(Object.keys(channelCurrencyMap));
    } catch (e) { console.error('Failed to load user channels:', e); }
  };

  const loadOverview = async () => {
    try {
      const { data } = await metaApi.getOverview();
      setOverview(data);
      setMetaCache({});
    } catch (e) { console.error('Failed to load metadata overview:', e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (selectedChannel && selectedApiCode) loadMetadata(selectedApiCode, selectedChannel);
    else if (selectedChannel && !selectedApiCode) {
      const apiCode = Object.entries(overview?.channelsByApi || {}).find(([, chs]) => chs.includes(selectedChannel))?.[0];
      if (apiCode) loadMetadata(apiCode, selectedChannel);
      else setMetadata(null);
    } else setMetadata(null);
  }, [selectedApiCode, selectedChannel, overview]);

  const loadMetadata = async (apiCode, channelCode) => {
    const cacheKey = `${apiCode}|${channelCode}`;
    if (metaCache[cacheKey]) { setMetadata(metaCache[cacheKey]); return; }
    setMetaLoading(true);
    try {
      const { data } = await metaApi.get(apiCode, channelCode);
      setMetaCache(prev => ({ ...prev, [cacheKey]: data }));
      setMetadata(data);
    } catch (e) { setMetadata(null); }
    finally { setMetaLoading(false); }
  };

  const loadCorridorRequirements = async (country, currency) => {
    if (!country || country.length !== 2 || !currency || currency.length !== 3) return;
    setCorridorLoading(true);
    setSelectedPayoutType(null);
    try {
      const { data } = await metaApi.getPayoutRequirements(selectedChannel || 'TAZAPAY', country, currency);
      setCorridorRawResponse(data);
      const reqData = data?.data || null;
      setCorridorRequirements(reqData?.payoutMethods?.length > 0 || reqData?.data?.length > 0 ? reqData : null);
      // Auto-select first payout type
      if (reqData?.payoutMethods?.length > 0) {
        setSelectedPayoutType(reqData.payoutMethods[0].payoutType);
      }
    } catch (e) {
      console.error('Failed to load corridor requirements:', e);
      setCorridorRequirements(null);
      setCorridorRawResponse(null);
    } finally {
      setCorridorLoading(false);
    }
  };

  const availableChannels = useMemo(() => {
    // 可按渠道覆盖的查询类接口（如 USER_QUERY）：overview 里可能没有该接口的 channelsByApi，
    // 需要兜底候选才能让渠道选择器可用。
    //
    // 兜底来源改为「overview 中出现过的全部渠道」而不是写死名单：
    // 原先硬编码 ['TRANSFI','PYVIO','MDAQ','MOOLAHGO','AVENIA']，新接入的渠道（如 GOWD）
    // 不在名单里就选不到，每加一个渠道都得改这行代码，且漏改时表现为「下拉里没有该渠道」，
    // 很难联想到是前端写死了名单。
    if (selectedApiCode && CHANNEL_SELECTABLE_APIS.has(selectedApiCode)) {
      const overviewChannels = overview?.channelsByApi?.[selectedApiCode] || [];
      const allKnown = new Set();
      Object.values(overview?.channelsByApi || {}).forEach(chs => chs.forEach(ch => allKnown.add(ch)));
      const candidates = [...new Set([...overviewChannels, ...allKnown])];
      // 已登录且拿到授权渠道时按其过滤，避免展示无权访问的渠道
      return userChannels.length ? candidates.filter(ch => userChannels.includes(ch)) : candidates;
    }
    if (selectedApiCode && overview) {
      const apiChannels = overview.channelsByApi?.[selectedApiCode] || [];
      if (!userChannels.length) return apiChannels;
      return userChannels.filter(ch => apiChannels.includes(ch));
    }
    if (!userChannels.length && overview) {
      const allChannels = new Set();
      Object.values(overview.channelsByApi || {}).forEach(chs => chs.forEach(ch => allChannels.add(ch)));
      return [...allChannels];
    }
    return userChannels;
  }, [userChannels, overview, selectedApiCode]);

  const channelCount = availableChannels.length;
  const channelOptions = useMemo(() => buildChannelOptions(availableChannels), [availableChannels]);

  // ─── Checkout.com-style parameter row renderer ───
  const renderParamRow = (field) => {
    const f = field;
    // Metadata 中 tip 是可选字段。统一归一化为空字符串，禁止直接对 undefined 调用 includes/split。
    const fieldTip = typeof f.tip === 'string' ? f.tip : '';
    const tipText = [f.requiredCondition ? `条件必填：${f.requiredCondition}` : null, fieldTip]
      .filter(Boolean).join('; ');
    const isEnum = f.type === 'ENUM' || (f.type === 'STRING' && tipText && /^[A-Z_]+ \/ [A-Z_]/.test(tipText.replace(/^[^:]*:\s*/, '')));
    const condRequired = !f.required && (Boolean(f.requiredCondition) || (tipText && (() => {
      // 移除所有"非必填"后，检查剩余文本是否仍含"必填"或"required"
      const stripped = tipText.replace(/非必填/g, '').replace(/不必填/g, '');
      return /(必填|[Rr]equired)/.test(stripped);
    })()));
    const typeDisplay = isEnum ? 'enum' : (f.type || 'string').toLowerCase();
    const typeColors = { string: '#3b82f6', enum: '#8b5cf6', integer: '#f59e0b', long: '#f59e0b', decimal: '#f59e0b', boolean: '#22c55e', object: '#ec4899', array: '#06b6d4', json_string: '#8b5cf6', file: '#64748b' };

    // Split conditional tips by semicolons into structured items
    const renderTip = () => {
      if (!tipText) return null;
      // Pure enum-like values: "A / B / C" (no semicolons, no colons, no commas)
      if (tipText.includes(' / ') && !tipText.includes(';') && !tipText.includes(':') && !tipText.includes(',')) {
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
            {tipText.split(' / ').map((v, i) => (
              <span key={i} style={{ fontSize: 11, padding: '1px 6px', borderRadius: 3, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' }}>{v.trim()}</span>
            ))}
          </div>
        );
      }
      // Conditional-required tips with semicolons — yellow structured list
      if (condRequired && tipText.includes(';')) {
        const parts = tipText.split(/;\s*/);
        return (
          <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {parts.map((part, i) => (
              <div key={i} style={{
                fontSize: 11.5, color: '#92400e', background: '#fefce8',
                border: '1px solid #fde68a', borderRadius: 4, padding: '2px 8px',
                display: 'inline-flex', alignItems: 'center', gap: 4, width: 'fit-content',
              }}>
                <span style={{ color: '#d97706', fontSize: 10 }}>▸</span> {part.trim()}
              </div>
            ))}
          </div>
        );
      }
      // Conditional tip (single line, no semicolons)
      if (condRequired) {
        return <div style={{ marginTop: 4, fontSize: 11.5, color: '#92400e', background: '#fefce8', border: '1px solid #fde68a', borderRadius: 4, padding: '2px 8px', display: 'inline-flex', alignItems: 'center', gap: 4, width: 'fit-content' }}><span style={{ color: '#d97706', fontSize: 10 }}>▸</span> {tipText}</div>;
      }
      // Non-conditional tips with semicolons — gray structured list (e.g. enum value ranges per channel)
      if (fieldTip.includes(';')) {
        const parts = fieldTip.split(/;\s*/);
        return (
          <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {parts.map((part, i) => {
              // If a part contains "/ " enum list, render as inline tags
              const enumMatch = part.match(/^(.+?):\s*(.+\/.+)$/);
              if (enumMatch) {
                const [, prefix, enumStr] = enumMatch;
                const vals = enumStr.split(/\s*\/\s*/);
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 11.5, color: '#64748b', whiteSpace: 'nowrap' }}>{prefix.trim()}:</span>
                    {vals.map((v, j) => (
                      <span key={j} style={{ fontSize: 11, padding: '1px 6px', borderRadius: 3, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' }}>{v.trim()}</span>
                    ))}
                  </div>
                );
              }
              return (
                <div key={i} style={{ fontSize: 11.5, color: '#64748b', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 4, padding: '2px 8px', display: 'inline-flex', alignItems: 'center', gap: 4, width: 'fit-content' }}>
                  <span style={{ color: '#94a3b8', fontSize: 10 }}>▸</span> {part.trim()}
                </div>
              );
            })}
          </div>
        );
      }
      // Default: plain text
      return <div style={{ marginTop: 2, fontSize: 12, color: '#94a3b8' }}>{fieldTip}</div>;
    };

    const fieldName = typeof f.name === 'string' ? f.name : '';
    const displayName = f._isChild ? fieldName.split('.').pop() : fieldName;
    return (
      <div key={fieldName} style={{ padding: '12px 0', paddingLeft: (f._indentLevel || 0) * 16, borderBottom: '1px solid #f1f5f9', display: 'flex', gap: 24, alignItems: 'flex-start' }}>
        <div style={{ flex: '0 0 320px', minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {f._isChild && <span style={{ color: COLORS.textMuted, fontSize: 10, marginRight: -2 }}>↳</span>}
            <code style={{ fontSize: 13, fontWeight: 500, color: f._isChild ? COLORS.textSecondary : COLORS.textPrimary, fontFamily: "'JetBrains Mono', 'SF Mono', monospace", background: '#f8fafc', padding: '1px 6px', borderRadius: 4, border: '1px solid #e2e8f0', whiteSpace: 'normal', wordBreak: 'break-all' }}>{displayName}</code>
            {f.required && <span style={{ fontSize: 10, color: COLORS.red, fontWeight: 700, whiteSpace: 'nowrap' }}>required</span>}
            {condRequired && !f.required && <span style={{ fontSize: 10, color: COLORS.orange, fontWeight: 600, whiteSpace: 'nowrap' }}>conditional</span>}
            {f._corridorRequired && <span style={{ fontSize: 10, color: '#fff', fontWeight: 600, whiteSpace: 'nowrap', background: COLORS.red, padding: '1px 5px', borderRadius: 3 }}>corridor required</span>}
            {f._corridorRecommended && <span style={{ fontSize: 10, color: '#fff', fontWeight: 600, whiteSpace: 'nowrap', background: COLORS.orange, padding: '1px 5px', borderRadius: 3 }}>corridor recommended</span>}
          </div>
          <div style={{ marginTop: 3, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, color: typeColors[typeDisplay] || '#64748b', fontWeight: 500 }}>{typeDisplay}</span>
            {f.maxLen && <span style={{ fontSize: 10, color: '#94a3b8' }}>{f.minLen ? `${f.minLen}~${f.maxLen}` : `≤${f.maxLen}`}</span>}
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12.5, color: COLORS.textSecondary, lineHeight: 1.6 }}>{f.label}</div>
          {f.options && f.options.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
              {f.options.map((v, i) => (
                <span key={i} style={{ fontSize: 11, padding: '1px 6px', borderRadius: 3, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' }}>{v}</span>
              ))}
            </div>
          )}
          {renderTip()}
        </div>
      </div>
    );
  };

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;

  // api-doc「配置管理」权限：ADMIN 或 DEV 均可。
  // DEV = 普通用户全部权限 + api-doc 配置管理，但不含其它管理员权限（用户管理等）；
  // 故此处按「能否管理 api-doc」判定，而非单纯 ADMIN。后端 ChannelApiMetadataController 同步放行 ADMIN/DEV。
  const canManageApiDoc = !isPublic && ['ADMIN', 'DEV'].includes(localStorage.getItem('userRole'));

  // ═══════════════════════════════════════════════════════════
  // OVERVIEW TAB — Checkout.com "Get started" style
  // ═══════════════════════════════════════════════════════════
  const overviewTab = (
    <div style={{ maxWidth: 1600, margin: '0 auto' }}>
      {/* Hero */}
      <div style={{ marginBottom: 40 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: COLORS.textPrimary, margin: '0 0 6px', letterSpacing: '-0.3px' }}>Fiat Payment API Reference</h1>
        <p style={{ fontSize: 13, color: COLORS.textSecondary, margin: 0, lineHeight: 1.6 }}>
          {t('doc_totalApis', Object.keys(API_CODE_LABELS).length)}
        </p>
      </div>

      {/* API Groups — Checkout.com endpoint list style */}
      {SCENE_OPTIONS.map((group) => (
        <div key={group.label} style={{ marginBottom: 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <span style={{ fontSize: 15, fontWeight: 600, color: COLORS.textPrimary }}>{group.label}</span>
            <span style={{ fontSize: 12, color: COLORS.textMuted, background: '#f1f5f9', padding: '1px 8px', borderRadius: 10 }}>{group.options.length}</span>
          </div>
          <div style={{ borderRadius: 8, border: `1px solid ${COLORS.border}`, overflow: 'hidden', background: '#fff' }}>
            {group.options.map((o, i) => {
              const api = API_CODE_LABELS[o.value];
              return (
                <div key={o.value}
                  onClick={() => { setSelectedApiCode(o.value); setActiveSection('detail'); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px',
                    borderBottom: i < group.options.length - 1 ? `1px solid ${COLORS.borderLight}` : 'none',
                    cursor: 'pointer', transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                >
                  <MethodBadge method={api.method} />
                  <code style={{ fontSize: 13, color: COLORS.textPrimary, fontFamily: "'JetBrains Mono', 'SF Mono', monospace", flex: 1 }}>{api.uri}</code>
                  <span style={{ fontSize: 13, color: COLORS.textSecondary, flex: '0 0 auto' }}>{api.label}</span>
                  <RightOutlined style={{ fontSize: 10, color: COLORS.textMuted }} />
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Common Response Structure */}
      <div style={{ marginBottom: 32 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, color: COLORS.textPrimary, margin: '0 0 16px' }}>{t('doc_commonResponse')}</h2>
        <p style={{ fontSize: 13.5, color: COLORS.textSecondary, margin: '0 0 16px', lineHeight: 1.6 }}>
          {t('doc_commonResponseDesc')} <code style={{ background: '#f1f5f9', padding: '1px 6px', borderRadius: 3, fontSize: 12 }}>data</code> {t('doc_commonResponseDesc2')}
        </p>
        <div style={{ background: '#fff', borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: '4px 20px' }}>
          {localizeFields(COMMON_RESPONSE_FIELDS).map(f => renderParamRow(f))}
        </div>
      </div>

      {/* Error Codes */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, color: COLORS.textPrimary, margin: 0 }}>{t('doc_errorCodeLookup')}</h2>
          <Space size={6}>
            {Object.entries(ERROR_CAT_CONFIG).map(([k, v]) => (
              <span key={k} style={{ fontSize: 10, padding: '1px 8px', borderRadius: 10, color: v.color, background: v.bg, border: `1px solid ${v.color}22`, fontWeight: 500 }}>{v.label}</span>
            ))}
          </Space>
        </div>
        <div style={{ background: '#fff', borderRadius: 8, border: `1px solid ${COLORS.border}`, overflow: 'hidden' }}>
          <Table dataSource={ERROR_CODES} rowKey="code" size="small" pagination={false}
            columns={[
              { title: t('doc_colCategory'), dataIndex: 'cat', width: 90, align: 'center',
                render: v => { const c = ERROR_CAT_CONFIG[v]; return <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, color: c.color, background: c.bg, fontWeight: 500 }}>{c.label}</span>; },
                onCell: (record, index) => {
                  const data = ERROR_CODES;
                  if (index === 0 || data[index].cat !== data[index - 1].cat) {
                    let span = 1;
                    for (let i = index + 1; i < data.length && data[i].cat === record.cat; i++) span++;
                    return { rowSpan: span };
                  }
                  return { rowSpan: 0 };
                },
              },
              { title: 'subCode', dataIndex: 'code', width: 120, render: (v, r) => <code style={{ color: ERROR_CAT_CONFIG[r.cat].color, fontWeight: 600, fontSize: 12 }}>{v}</code> },
              { title: t('doc_colDesc'), dataIndex: 'desc' },
              { title: t('doc_colAction'), dataIndex: 'action', render: v => <span style={{ fontSize: 12, color: COLORS.textSecondary }}>{v}</span> },
            ]}
          />
        </div>
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════
  // DETAIL TAB — Checkout.com 3-panel: left params + right code
  // ═══════════════════════════════════════════════════════════
  const detailTab = (() => {
    const apiInfo = API_CODE_LABELS[selectedApiCode] || {};
    // TRANSFI 渠道专属覆盖：仅当选中 TRANSFI 且该接口有覆盖定义时生效，避免混入其它渠道信息；其它渠道不受影响。
    const transfiOverride = selectedChannel === 'TRANSFI' ? TRANSFI_PARAM_OVERRIDES[selectedApiCode] : null;
    // 渠道专属覆盖；未命中时保持共享参数默认行为。
    const zandbankOverride = selectedChannel === 'ZAND_BANK' ? ZAND_BANK_PARAM_OVERRIDES[selectedApiCode] : null;
    const dlocalOverride = selectedChannel === 'DLOCAL' ? DLOCAL_PARAM_OVERRIDES[selectedApiCode] : null;
    const channelOverride = transfiOverride || zandbankOverride || dlocalOverride;
    const rawBaseParams = (channelOverride && channelOverride.request) || BASE_PARAMS[selectedApiCode] || [];
    const metadataFields = isChannelSpecific
      ? (metadata?.fields || []).filter(f => f && typeof f.name === 'string' && f.name.length > 0)
      : [];
    const mainMetadataFields = metadataFields.filter(f => !f.group);
    const hiddenMainFields = new Set(metadataFields.filter(f => f.group === '__hidden').map(f => f.name));
    const mainOverrides = new Map(mainMetadataFields.map(f => [f.name, f]));
    const baseParams = rawBaseParams
      .filter(f => !hiddenMainFields.has(f.name))
      .map(f => mainOverrides.has(f.name) ? { ...f, ...mainOverrides.get(f.name) } : f);
    mainMetadataFields.forEach(f => {
      if (!baseParams.some(base => base.name === f.name)) baseParams.push(f);
    });
    const extParamFields = metadataFields
      .filter(f => typeof f.group === 'string' && f.group.startsWith('extParam'))
      .map(f => ({ ...f, _isChild: f.name.includes('.'), _indentLevel: Math.max(0, f.name.split('.').length - 1) }));
    const responseParams = (() => {
      // 有走廊查询结果时，从实际返回数据动态生成响应字段描述
      if (selectedApiCode === 'PAYOUT_REQUIREMENTS' && corridorRequirements) {
        return buildDynamicResponseParams(corridorRequirements, selectedChannel);
      }
      // 无查询结果时，显示静态定义
      if (selectedApiCode === 'PAYOUT_REQUIREMENTS' && selectedChannel === 'MDAQ') {
        return RESPONSE_PARAMS_MDAQ_PAYOUT_REQUIREMENTS;
      }
      // 渠道专属响应覆盖，避免展示共享 DTO 中该渠道从不赋值的字段。
      if (channelOverride && channelOverride.response) return channelOverride.response;
      if (selectedChannel === 'AVENIA' && AVENIA_RESPONSE_OVERRIDES[selectedApiCode]) {
        return AVENIA_RESPONSE_OVERRIDES[selectedApiCode];
      }
      // 通用响应覆盖：元数据里 group='response' 的字段视为该渠道的响应字段声明。
      //
      // 原先只有 TransFi / Avenia 两份硬编码 override 常量，新渠道要想修正响应区
      // 就得改代码加一份常量；漏加时响应区会沿用共享 DTO 的通用文案，
      // 出现「其他渠道才有的枚举值/字段说明」（如出金方式列出 INTERAC/EFT）。
      // 改为从元数据读取后，新渠道纯配置即可，无需发版。
      const metaResponse = (metadata?.fields || [])
        .filter(f => f && f.group === 'response' && typeof f.name === 'string' && f.name)
        .map(f => ({ ...f, _isChild: f.name.includes('.'),
          _indentLevel: Math.max(0, f.name.split('.').length - 1) }));
      if (metaResponse.length) return metaResponse;
      return RESPONSE_PARAMS[selectedApiCode] || [];
    })();
    const examples = isChannelSpecific ? null : API_EXAMPLES[selectedApiCode];
    // 渠道专属查询示例覆盖，避免沿用其他渠道的示例字段。
    const channelExample = channelOverride
      ? (selectedChannel === 'ZAND_BANK' ? ZAND_BANK_API_EXAMPLES[selectedApiCode]
        : selectedChannel === 'DLOCAL' ? DLOCAL_API_EXAMPLES[selectedApiCode]
        : TRANSFI_API_EXAMPLES[selectedApiCode])
      : null;
    const aveniaExample = selectedChannel === 'AVENIA' ? AVENIA_API_EXAMPLES[selectedApiCode] : null;
    const exampleRequest = isChannelSpecific ? metadata?.exampleRequest : (aveniaExample?.request || channelExample?.request || examples?.request);
    const exampleResponse = (() => {
      if (selectedApiCode === 'PAYOUT_REQUIREMENTS' && corridorRawResponse) {
        return corridorRawResponse;
      }
      if (aveniaExample?.response) return aveniaExample.response;
      if (channelExample?.response) return channelExample.response;
      return isChannelSpecific ? metadata?.exampleResponse : examples?.response;
    })();

    return (
      <div style={{ maxWidth: 1600, margin: '0 auto' }}>
        {/* Top bar: API selector + channel selector */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '12px 24px',
          background: '#fff', borderBottom: `1px solid ${COLORS.border}`, position: 'sticky', top: 0, zIndex: 10,
        }}>
          <Select value={selectedApiCode} onChange={v => {
            setSelectedApiCode(v); setMetadata(null);
            // 既非渠道专属、也非可选渠道的查询接口 → 清空渠道；否则初始化一个可用渠道
            if (v && !CHANNEL_SPECIFIC_APIS.has(v) && !CHANNEL_SELECTABLE_APIS.has(v)) setSelectedChannel(null);
            else if (v && CHANNEL_SPECIFIC_APIS.has(v)) {
              const apiChannels = overview?.channelsByApi?.[v] || [];
              const available = userChannels.length > 0 ? userChannels.filter(ch => apiChannels.includes(ch)) : apiChannels;
              if (!selectedChannel || !available.includes(selectedChannel)) {
                const preferred = v === 'DEPOSIT_CLOSE' && available.includes('CVPAY') ? 'CVPAY' : available[0] || null;
                setSelectedChannel(preferred);
              }
            }
            // CHANNEL_SELECTABLE_APIS（如 USER_QUERY）：保留当前 selectedChannel，用户可自行切换
          }} style={{ minWidth: 240 }} size="middle" options={SCENE_OPTIONS} variant="borderless" popupMatchSelectWidth={300} />
          {(!selectedApiCode || showChannelSelector) && (
            <>
              <div style={{ width: 1, height: 20, background: COLORS.border }} />
              <Select value={selectedChannel} onChange={v => { setSelectedChannel(v); setCorridorRequirements(null); setCorridorRawResponse(null); setSelectedPayoutType(null); setCorridorCountry(''); setCorridorCurrency(''); }} style={{ minWidth: 200 }} size="middle" allowClear showSearch={false}
                placeholder={t('doc_selectChannel')} options={channelOptions} variant="borderless" popupMatchSelectWidth={260} />
              <span style={{ fontSize: 11, color: COLORS.textMuted }}>{t('doc_channelCount', channelCount)}</span>
            </>
          )}
        </div>

        {!selectedApiCode ? (
          <Empty description={t('doc_selectApi')} style={{ padding: 80 }} />
        ) : (
          /* ─── Three-panel layout ─── */
          <div style={{ display: 'flex', minHeight: 'calc(100vh - 180px)' }}>
            {/* LEFT: Documentation */}
            <div style={{ flex: 1, padding: '28px 32px', overflow: 'auto', borderRight: `1px solid ${COLORS.borderLight}`, minWidth: 0 }}>
              {/* Endpoint header */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <MethodBadge method={apiInfo.method} />
                  <code style={{ fontSize: 15, fontWeight: 500, color: COLORS.textPrimary, fontFamily: "'JetBrains Mono', 'SF Mono', monospace" }}>{apiInfo.uri}</code>
                </div>
                <p style={{ fontSize: 14, color: COLORS.textSecondary, margin: 0 }}>{(selectedChannel === 'AVENIA' && AVENIA_API_DESC[selectedApiCode]) || (selectedChannel === 'ZAND_BANK' && ZAND_BANK_API_DESC[selectedApiCode]) || (selectedChannel === 'DLOCAL' && DLOCAL_API_DESC[selectedApiCode]) || (transfiOverride && TRANSFI_API_DESC[selectedApiCode]) || apiInfo.desc}</p>
              </div>

              {/* Channel selector badge for channel-specific / channel-selectable APIs */}
              {showChannelSelector && selectedChannel && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ padding: '8px 12px', background: COLORS.accentBg, borderRadius: 6, border: `1px solid ${COLORS.accent}22`, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 12, color: COLORS.accent, fontWeight: 500 }}>
                      {(() => { const logo = getChannelLogo(selectedChannel); return `${logo.emoji} ${selectedChannel}`; })()}
                    </span>
                  </div>
                  {metadata?.description && (
                    <div style={{ marginTop: 8, fontSize: 13, color: COLORS.textSecondary, lineHeight: 1.6 }}>
                      {metadata.description}
                    </div>
                  )}
                </div>
              )}

              {/* Corridor Requirements Query Panel (TAZAPAY CARD_BIND or TAZAPAY PAYOUT_REQUIREMENTS) */}
              {((selectedChannel === 'TAZAPAY' && selectedApiCode === 'CARD_BIND') || (selectedChannel === 'TAZAPAY' && selectedApiCode === 'PAYOUT_REQUIREMENTS')) && (
                <div style={{ marginBottom: 24, borderRadius: 8, border: `1px solid ${COLORS.accent}22`, overflow: 'hidden' }}>
                  {/* Header */}
                  <div style={{ padding: '12px 20px', background: COLORS.accentBg, borderBottom: `1px solid ${COLORS.accent}22`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 14 }}>🔍</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.textPrimary }}>{t('doc_corridorTitle')}</span>
                    </div>
                    {corridorLoading && <Spin size="small" />}
                  </div>
                  {/* Selectors */}
                  <div style={{ padding: '16px 20px' }}>
                    <div style={{ fontSize: 12, color: COLORS.textSecondary, marginBottom: 12 }}>{t('doc_corridorDesc')}</div>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                      <div>
                        <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 4, fontWeight: 500 }}>{t('doc_corridorCountry')}</div>
                        <Select
                          showSearch placeholder={t('doc_corridorSelectCountry')} value={corridorCountry || undefined}
                          onChange={v => {
                            setCorridorCountry(v);
                            const currencies = v ? (COUNTRY_CURRENCIES[v] || []) : [];
                            if (currencies.length === 1) {
                              setCorridorCurrency(currencies[0]);
                              loadCorridorRequirements(v, currencies[0]);
                            } else {
                              setCorridorCurrency('');
                              setCorridorRequirements(null);
                              setCorridorRawResponse(null);
                              setSelectedPayoutType(null);
                            }
                          }}
                          style={{ width: 160 }} allowClear size="middle"
                          filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                          options={[
                            {value:'US',label:'🇺🇸 US'},{value:'GB',label:'🇬🇧 GB'},{value:'HK',label:'🇭🇰 HK'},{value:'SG',label:'🇸🇬 SG'},
                            {value:'IN',label:'🇮🇳 IN'},{value:'JP',label:'🇯🇵 JP'},{value:'AU',label:'🇦🇺 AU'},{value:'CA',label:'🇨🇦 CA'},
                            {value:'DE',label:'🇩🇪 DE'},{value:'FR',label:'🇫🇷 FR'},{value:'CH',label:'🇨🇭 CH'},{value:'NZ',label:'🇳🇿 NZ'},
                            {value:'BR',label:'🇧🇷 BR'},{value:'MX',label:'🇲🇽 MX'},{value:'AE',label:'🇦🇪 AE'},{value:'SA',label:'🇸🇦 SA'},
                            {value:'ID',label:'🇮🇩 ID'},{value:'TH',label:'🇹🇭 TH'},{value:'PH',label:'🇵🇭 PH'},{value:'MY',label:'🇲🇾 MY'},
                            {value:'VN',label:'🇻🇳 VN'},{value:'KR',label:'🇰🇷 KR'},{value:'CN',label:'🇨🇳 CN'},{value:'NG',label:'🇳🇬 NG'},
                            {value:'KE',label:'🇰🇪 KE'},{value:'ZA',label:'🇿🇦 ZA'},{value:'SE',label:'🇸🇪 SE'},{value:'NO',label:'🇳🇴 NO'},
                            {value:'DK',label:'🇩🇰 DK'},{value:'PL',label:'🇵🇱 PL'},{value:'IL',label:'🇮🇱 IL'},{value:'TR',label:'🇹🇷 TR'},
                          ]}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 4, fontWeight: 500 }}>{t('doc_corridorCurrency')}</div>
                        <Select
                          showSearch placeholder={corridorCountry ? t('doc_corridorSelectCurrency') : t('doc_corridorSelectCountryFirst')}
                          value={corridorCurrency || undefined}
                          onChange={v => { setCorridorCurrency(v); if (corridorCountry && v) loadCorridorRequirements(corridorCountry, v); }}
                          style={{ width: 140 }} allowClear disabled={!corridorCountry} size="middle"
                          filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                          options={corridorCurrencyOptions}
                        />
                      </div>
                    </div>

                    {/* Results */}
                    {corridorRequirements?.payoutMethods?.length > 0 && (
                      <div style={{ marginTop: 16 }}>
                        {/* Payout Type Selector */}
                        <div style={{ marginBottom: 12 }}>
                          <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 6, fontWeight: 500 }}>{t('doc_corridorPayoutType') || 'Payout Type'}</div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            {corridorRequirements.payoutMethods.map(pm => {
                              const isSelected = selectedPayoutType === pm.payoutType;
                              const baseColor = pm.payoutType === 'swift' ? COLORS.blue : COLORS.green;
                              return (
                                <div
                                  key={pm.payoutType}
                                  onClick={() => setSelectedPayoutType(pm.payoutType)}
                                  style={{
                                    padding: '6px 16px', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 12,
                                    background: isSelected ? baseColor : '#fff',
                                    color: isSelected ? '#fff' : baseColor,
                                    border: `1.5px solid ${baseColor}`,
                                    transition: 'all 0.2s',
                                    boxShadow: isSelected ? `0 2px 8px ${baseColor}40` : 'none',
                                  }}
                                >
                                  {pm.payoutType.toUpperCase()}
                                  {pm.transferLimit && (
                                    <span style={{ fontSize: 10, marginLeft: 6, opacity: 0.8 }}>
                                      {pm.transferLimit.minimum}~{pm.transferLimit.maximum}
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                        {/* Selected payout type details */}
                        {selectedPayoutType && (() => {
                          const pm = corridorRequirements.payoutMethods.find(m => m.payoutType === selectedPayoutType);
                          if (!pm) return null;
                          const baseColor = pm.payoutType === 'swift' ? COLORS.blue : COLORS.green;
                          return (
                            <div style={{ padding: '14px 16px', background: '#fff', borderRadius: 8, border: `1.5px solid ${baseColor}30` }}>
                              {/* Required fields */}
                              {(pm.requiredFields?.mainFields?.length > 0 || pm.requiredFields?.extParamFields?.length > 0) && (
                                <div style={{ marginBottom: 8 }}>
                                  <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.red, marginBottom: 4 }}>● {t('doc_corridorRequired')}</div>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, paddingLeft: 12 }}>
                                    {(pm.requiredFields.mainFields || []).map(f => <Tag key={f} color="error" style={{ fontSize: 11, margin: 0 }}>{f}</Tag>)}
                                    {(pm.requiredFields.extParamFields || []).map(f => <Tag key={f} color="error" style={{ fontSize: 11, margin: 0, borderStyle: 'dashed' }}>ext.{f}</Tag>)}
                                  </div>
                                </div>
                              )}
                              {/* Recommended fields */}
                              {(pm.recommendedFields?.mainFields?.length > 0 || pm.recommendedFields?.extParamFields?.length > 0) && (
                                <div style={{ marginBottom: 8 }}>
                                  <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.orange, marginBottom: 4 }}>○ {t('doc_corridorRecommended')}</div>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, paddingLeft: 12 }}>
                                    {(pm.recommendedFields.mainFields || []).map(f => <Tag key={f} color="warning" style={{ fontSize: 11, margin: 0 }}>{f}</Tag>)}
                                    {(pm.recommendedFields.extParamFields || []).map(f => <Tag key={f} color="warning" style={{ fontSize: 11, margin: 0, borderStyle: 'dashed' }}>ext.{f}</Tag>)}
                                  </div>
                                </div>
                              )}
                              {/* Networks */}
                              {pm.fundTransferNetworks?.length > 0 && (
                                <div>
                                  <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.blue, marginBottom: 4 }}>◆ {t('doc_corridorNetworks')}</div>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, paddingLeft: 12 }}>
                                    {pm.fundTransferNetworks.map(n => (
                                      <Tooltip key={n.name} title={n.description}>
                                        <Tag color="processing" style={{ fontSize: 11, margin: 0, cursor: 'help' }}>{n.name}{n.transferLimit ? ` (${n.transferLimit.minimum}~${n.transferLimit.maximum})` : ''}</Tag>
                                      </Tooltip>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Request Parameters */}
              <div style={{ marginBottom: 28 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                  <h3 style={{ fontSize: 15, fontWeight: 600, color: COLORS.textPrimary, margin: 0 }}>{t('doc_requestParams')}</h3>
                  <span style={{ fontSize: 11, color: COLORS.textMuted, background: '#f1f5f9', padding: '1px 8px', borderRadius: 10 }}>{baseParams.length}</span>
                </div>
                <div style={{ background: '#fff', borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: '0 20px' }}>
                  {localizeFields(baseParams).map(f => {
                    let field = f;
                    // 选中渠道时，过滤 tip 只显示该渠道相关的描述
                    if (selectedChannel && f.tip) {
                      field = { ...f, tip: filterTipByChannel(f.tip, selectedChannel) };
                    }
                    // TAZAPAY/MDAQ corridor: mark main fields as dynamically required/recommended
                    if ((selectedChannel === 'TAZAPAY' || selectedChannel === 'MDAQ') && selectedPayoutType && corridorRequirements) {
                      const pm = corridorRequirements.payoutMethods?.find(m => m.payoutType === selectedPayoutType);
                      if (pm) {
                        const isReq = (pm.requiredFields?.mainFields || []).includes(f.name);
                        const isRec = (pm.recommendedFields?.mainFields || []).includes(f.name);
                        if (isReq) field = { ...field, _corridorRequired: true };
                        if (isRec) field = { ...field, _corridorRecommended: true };
                      }
                    }
                    return renderParamRow(field);
                  })}
                </div>
              </div>

              {/* extParam diff fields (channel-specific) */}
              {isChannelSpecific && (
                <Spin spinning={metaLoading}>
                  <div style={{ marginBottom: 28 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                      <h3 style={{ fontSize: 15, fontWeight: 600, color: COLORS.textPrimary, margin: 0 }}>{t('doc_extParamFields')}</h3>
                      <span style={{ fontSize: 11, color: COLORS.textMuted, background: '#f1f5f9', padding: '1px 8px', borderRadius: 10 }}>{extParamFields.length}</span>
                    </div>
                    {extParamFields.length > 0 ? (
                      <div style={{ background: '#fff', borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: '0 20px' }}>
                        {localizeFields(extParamFields).map(f => {
                          let field = f;
                          // TAZAPAY/MDAQ corridor: mark ext fields as dynamically required/recommended
                          if ((selectedChannel === 'TAZAPAY' || selectedChannel === 'MDAQ') && selectedPayoutType && corridorRequirements) {
                            const pm = corridorRequirements.payoutMethods?.find(m => m.payoutType === selectedPayoutType);
                            if (pm) {
                              const isReq = (pm.requiredFields?.extParamFields || []).includes(f.name);
                              const isRec = (pm.recommendedFields?.extParamFields || []).includes(f.name);
                              if (isReq) field = { ...field, _corridorRequired: true };
                              if (isRec) field = { ...field, _corridorRecommended: true };
                            }
                          }
                          return renderParamRow(field);
                        })}
                      </div>
                    ) : (
                      <div style={{ padding: '20px 0', color: COLORS.textMuted, fontSize: 13 }}>{metadata ? t('doc_noExtParam') : t('doc_noConfig')}</div>
                    )}
                  </div>
                </Spin>
              )}

              {/* Response Parameters */}
              {responseParams.length > 0 && (
                <div style={{ marginBottom: 28 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                    <h3 style={{ fontSize: 15, fontWeight: 600, color: COLORS.textPrimary, margin: 0 }}>{t('doc_responseParams')}</h3>
                    <span style={{ fontSize: 11, color: COLORS.textMuted, background: '#f1f5f9', padding: '1px 8px', borderRadius: 10 }}>data: {responseParams.length}</span>
                  </div>
                  <div style={{ background: '#fff', borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: '0 20px' }}>
                    {localizeFields(responseParams).flatMap(f => {
                      const rows = [renderParamRow(f)];
                      if (f.children && f.children.length > 0) {
                        // Check if children have _channel grouping
                        const hasChannelGroups = f.children.some(c => c._channel);
                        if (hasChannelGroups) {
                          // Group children by _channel
                          const groups = {};
                          const ungrouped = [];
                          f.children.forEach(c => {
                            if (c._channel) {
                              if (!groups[c._channel]) groups[c._channel] = [];
                              groups[c._channel].push(c);
                            } else {
                              ungrouped.push(c);
                            }
                          });
                          rows.push(
                            <div key={`${f.name}_children`} style={{ paddingLeft: 24, marginLeft: 12, marginBottom: 4 }}>
                              {Object.entries(groups).map(([channel, fields]) => {
                                const logo = getChannelLogo(channel);
                                return (
                                  <div key={channel} style={{ marginBottom: 8 }}>
                                    <div style={{
                                      display: 'inline-flex', alignItems: 'center', gap: 6,
                                      padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                                      background: `${logo.color}12`, color: logo.color,
                                      border: `1px solid ${logo.color}30`, marginBottom: 2, marginTop: 4,
                                    }}>
                                      {logo.emoji} {channel}
                                    </div>
                                    <div style={{ borderLeft: `2px solid ${logo.color}40`, paddingLeft: 12 }}>
                                      {localizeFields(fields).map(child => renderParamRow({ ...child, name: `${f.name}.${child.name}`, _isChild: true }))}
                                    </div>
                                  </div>
                                );
                              })}
                              {ungrouped.length > 0 && (
                                <div style={{ borderLeft: `2px solid ${COLORS.accent}22`, paddingLeft: 12 }}>
                                  {localizeFields(ungrouped).map(child => renderParamRow({ ...child, name: `${f.name}.${child.name}`, _isChild: true }))}
                                </div>
                              )}
                            </div>
                          );
                        } else {
                          rows.push(
                            <div key={`${f.name}_children`} style={{ paddingLeft: 24, borderLeft: `2px solid ${COLORS.accent}22`, marginLeft: 12, marginBottom: 4 }}>
                              {localizeFields(f.children).flatMap(child => {
                                const childRows = [renderParamRow({ ...child, name: `${f.name}.${child.name}`, _isChild: true })];
                                // Render grandchildren (2nd level nesting)
                                if (child.children && child.children.length > 0) {
                                  childRows.push(
                                    <div key={`${f.name}.${child.name}_children`} style={{ paddingLeft: 24, borderLeft: `2px solid ${COLORS.accent}11`, marginLeft: 12, marginBottom: 4 }}>
                                      {localizeFields(child.children).map(gc => renderParamRow({ ...gc, name: `${f.name}.${child.name}.${gc.name}`, _isChild: true }))}
                                    </div>
                                  );
                                }
                                return childRows;
                              })}
                            </div>
                          );
                        }
                      }
                      return rows;
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* RIGHT: Code examples (dark panel) */}
            <div style={{
              flex: '0 0 420px', maxWidth: 420, background: COLORS.codeBg, padding: '28px 20px',
              overflow: 'auto', minHeight: '100%',
            }}>
              <div style={{ position: 'sticky', top: 0 }}>
                {exampleRequest && <DarkCodePanel title={t('doc_exampleRequest')} data={exampleRequest} t={t} />}
                {exampleResponse && <DarkCodePanel title={t('doc_exampleResponse')} data={exampleResponse} t={t} />}
                {!exampleRequest && !exampleResponse && (
                  <div style={{ padding: '40px 20px', textAlign: 'center', color: '#475569' }}>
                    <CodeOutlined style={{ fontSize: 32, marginBottom: 12, color: '#334155' }} />
                    <div style={{ fontSize: 13 }}>{isChannelSpecific && !selectedChannel ? t('doc_selectChannel') : t('doc_noConfig')}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  })();

  // ═══════════════════════════════════════════════════════════
  // WEBHOOK TAB
  // ═══════════════════════════════════════════════════════════
  const WEBHOOK_EVENT_DATA = {
    ACCOUNT: [
      { name: 'accountId', type: 'String', required: true, desc: '账户ID' },
      { name: 'status', type: 'String', required: true, desc: 'PROCESSING / ACTIVE / FAILED / CLOSED / DENIED' },
      { name: 'subStatus', type: 'String', required: true, desc: 'INIT / PRE_CHECKING / CREATING_CHANNEL / WAITING_CONNECT / SUCCESS / FAILED' },
      { name: 'externalUserId', type: 'String', required: true, desc: '业务线用户ID' },
      { name: 'channelCode', type: 'String', required: true, desc: '渠道' },
      { name: 'paymentMethod', type: 'String', required: true, desc: '支付方式' },
      { name: 'currency', type: 'String', required: true, desc: '币种' },
      { name: 'errorCode', type: 'String', required: false, desc: '失败时返回错误码' },
      { name: 'errorMsg', type: 'String', required: false, desc: '失败时返回错误信息' },
      { name: 'createTime', type: 'Long', required: true, desc: '创建时间' },
      { name: 'updateTime', type: 'Long', required: true, desc: '更新时间' },
      { name: 'accountName', type: 'String', required: true, desc: '账户名' },
      { name: 'accountNumber', type: 'String', required: true, desc: 'IBAN 或 accountNumber' },
      { name: 'bic', type: 'String', required: false, desc: 'SWIFT Code' },
      { name: 'bankName', type: 'String', required: false, desc: '银行名称' },
      { name: 'bankCountry', type: 'String', required: false, desc: '银行所在国家' },
      { name: 'bankAddress', type: 'String', required: false, desc: '银行地址' },
      { name: 'currencies', type: 'List<String>', required: false, desc: 'TAZAPAY: 多币种账户支持的币种列表，如 ["USD","EUR","GBP"]。currency="Multi" 时返回' },
      { name: 'multiCurrency', type: 'Boolean', required: false, desc: 'TAZAPAY: 是否多币种账户。true=多币种(currency="Multi")，false=单币种' },
      { name: 'extParam', type: 'JsonString', required: false, desc: '扩展参数', children: [
        { name: 'extParam.customerId', type: 'String', required: true, desc: 'LEAN_TECH: 渠道用户ID' },
        { name: 'extParam.accessToken', type: 'String', required: true, desc: 'LEAN_TECH: Access Token' },
        { name: 'extParam.channelAppToken', type: 'String', required: true, desc: 'LEAN_TECH: Channel App Token' },
        { name: 'extParam.paymentDestinationId', type: 'String', required: true, desc: 'LEAN_TECH: 支付目的地ID' },
        { name: 'extParam.fullNameRetrieved', type: 'String', required: false, desc: 'LEAN_TECH: 从银行获取的用户全名称' },
        { name: 'extParam.nameMatchType', type: 'String', required: false, desc: 'LEAN_TECH: 同名校验结果 PERFECT_MATCH / PARTIAL_MATCH / NO_MATCH / RECONNECT_REQUIRED / NO_RESULT' },
      ] },
    ],
    BANK_ACCOUNT: [
      { name: 'recipientId', type: 'String', required: true, desc: '绑卡ID' },
      { name: 'status', type: 'String', required: true, desc: 'PENDING / SUCCESS / FAIL' },
      { name: 'channelCode', type: 'String', required: true, desc: '渠道' },
      { name: 'externalUserId', type: 'String', required: false, desc: '业务线用户ID' },
      { name: 'accountName', type: 'String', required: true, desc: '账户姓名' },
      { name: 'bic', type: 'String', required: false, desc: 'SWIFT Code' },
      { name: 'iban', type: 'String', required: false, desc: 'IBAN' },
      { name: 'accountNumber', type: 'String', required: true, desc: '银行户名' },
      { name: 'bankName', type: 'String', required: false, desc: '银行名字' },
      { name: 'currency', type: 'String', required: true, desc: '币种' },
      { name: 'bankCountry', type: 'String', required: false, desc: '银行国家 (ISO 3166-1 alpha-3)' },
      { name: 'errorCode', type: 'String', required: false, desc: '失败时返回错误码' },
      { name: 'errorMsg', type: 'String', required: false, desc: '失败时返回错误信息' },
    ],
    DEPOSIT: [
      { name: 'externalOrderId', type: 'String', required: true, desc: '外部订单号' },
      { name: 'orderId', type: 'String', required: true, desc: '订单ID' },
      { name: 'status', type: 'String', required: true, desc: 'PROCESSING / SUCCESS / FAILED' },
      { name: 'subStatus', type: 'String', required: true, desc: '子状态' },
      { name: 'channelCode', type: 'String', required: true, desc: '渠道' },
      { name: 'depositMethod', type: 'String', required: true, desc: 'VIRTUAL_ACCOUNT / PIX / VIETQR / ZEN_WALLET / INTERACE_REQUEST' },
      { name: 'currency', type: 'String', required: true, desc: '币种' },
      { name: 'amount', type: 'String', required: true, desc: '订单金额' },
      { name: 'fee', type: 'String', required: true, desc: '手续费' },
      { name: 'createTime', type: 'Long', required: true, desc: '创建时间' },
      { name: 'updateTime', type: 'Long', required: true, desc: '更新时间' },
      { name: 'completedTime', type: 'Long', required: false, desc: '完成时间' },
      { name: 'errorCode', type: 'String', required: false, desc: '错误码' },
      { name: 'errorMsg', type: 'String', required: false, desc: '错误信息' },
      { name: 'senderInfo', type: 'Object', required: false, desc: 'Payer 信息', children: [
        { name: 'senderInfo.senderAcctName', type: 'String', required: true, desc: '发送方账户名称' },
        { name: 'senderInfo.senderAcctNo', type: 'String', required: false, desc: '发送方账户号' },
        { name: 'senderInfo.senderBic', type: 'String', required: false, desc: '发送方 SWIFT/BIC Code' },
        { name: 'senderInfo.senderAddress', type: 'String', required: false, desc: '发送方地址' },
        { name: 'senderInfo.senderCountryCode', type: 'String', required: false, desc: '发送方国家代码' },
        { name: 'senderInfo.purpose', type: 'String', required: false, desc: '充值意图' },
        { name: 'senderInfo.remarks', type: 'String', required: false, desc: '备注' },
        { name: 'senderInfo.tranTime', type: 'LocalDateTime', required: false, desc: '转账时间' },
      ] },
      { name: 'paymentInfo', type: 'Object', required: false, desc: '支付信息', children: [
        { name: 'paymentInfo.payType', type: 'String', required: true, desc: 'URL / QRCODE / AUTHORIZATION_ID' },
        { name: 'paymentInfo.payUrl', type: 'String', required: false, desc: '支付链接' },
        { name: 'paymentInfo.qrCode', type: 'String', required: false, desc: 'QR Code' },
        { name: 'paymentInfo.qrPicture', type: 'String', required: false, desc: '二维码图片' },
        { name: 'paymentInfo.authorizationId', type: 'String', required: false, desc: '授权ID (AUTHORIZATION_ID时有值)' },
        { name: 'paymentInfo.authorizationToken', type: 'String', required: false, desc: '授权Token (AUTHORIZATION_ID时有值)' },
        { name: 'paymentInfo.authorizationAppToken', type: 'String', required: false, desc: '前端授权Token (AUTHORIZATION_ID时有值)' },
        { name: 'paymentInfo.connectType', type: 'String', required: false, desc: 'REVERSE_ENGINEERING / OPEN_BANKING' },
      ] },
    ],
    WITHDRAW: [
      { name: 'externalOrderId', type: 'String', required: true, desc: '外部订单号' },
      { name: 'orderId', type: 'String', required: true, desc: '订单ID' },
      { name: 'status', type: 'String', required: true, desc: 'PROCESSING / SUCCESS / FAILED' },
      { name: 'subStatus', type: 'String', required: true, desc: 'INIT / PRE_CHECKING / CREATING_CHANNEL_ORDER / WAITING_CHANNEL_CALLBACK / SUCCESS / FAILED' },
      { name: 'channelCode', type: 'String', required: true, desc: '渠道' },
      { name: 'withdrawMethod', type: 'String', required: true, desc: 'BANK_TRANSFER / PIX_CODE / INTERAC / EFT' },
      { name: 'currency', type: 'String', required: true, desc: '币种' },
      { name: 'amount', type: 'String', required: true, desc: '订单金额' },
      { name: 'fee', type: 'String', required: true, desc: '手续费' },
      { name: 'feeCurrency', type: 'String', required: true, desc: '手续费币种' },
      { name: 'createTime', type: 'Long', required: true, desc: '创建时间' },
      { name: 'updateTime', type: 'Long', required: true, desc: '更新时间' },
      { name: 'completedTime', type: 'Long', required: false, desc: '完成时间' },
      { name: 'errorCode', type: 'String', required: false, desc: '错误码' },
      { name: 'errorMsg', type: 'String', required: false, desc: '错误信息' },
    ],
    REFUND: [
      { name: 'externalOrderId', type: 'String', required: true, desc: '退款单号' },
      { name: 'orderId', type: 'String', required: true, desc: '退款单ID' },
      { name: 'status', type: 'String', required: true, desc: 'INIT / PENDING / SUCCESS / FAILED' },
      { name: 'errorCode', type: 'String', required: false, desc: '错误码' },
      { name: 'errorMsg', type: 'String', required: false, desc: '错误信息' },
      { name: 'refundReason', type: 'String', required: false, desc: '退款原因' },
      { name: 'amount', type: 'String', required: true, desc: '原订单金额' },
      { name: 'arrivalAmount', type: 'String', required: false, desc: '退款到账金额' },
      { name: 'currency', type: 'String', required: true, desc: '币种' },
      { name: 'channelCode', type: 'String', required: true, desc: '渠道' },
      { name: 'paymentMethod', type: 'String', required: true, desc: '支付方式' },
      { name: 'createTime', type: 'Long', required: true, desc: '创建时间' },
      { name: 'completedTime', type: 'Long', required: false, desc: '完成时间' },
    ],
    USER: [
      { name: 'userId', type: 'String', required: true, desc: '用户ID' },
      { name: 'clientId', type: 'String', required: true, desc: '渠道 ClientID' },
      { name: 'status', type: 'String', required: true, desc: 'SUCCESS / PROCESSING / FAILED / DENIED' },
      { name: 'externalUserId', type: 'String', required: true, desc: '商户用户ID' },
      { name: 'channelUserId', type: 'String', required: true, desc: '渠道用户ID' },
      { name: 'failedReason', type: 'String', required: false, desc: '失败原因' },
    ],
  };

  const webhookEventOptions = [
    { value: 'ACCOUNT', label: `ACCOUNT - ${t('doc_webhookEventAccount')}`, color: '#1677ff' },
    { value: 'BANK_ACCOUNT', label: `BANK_ACCOUNT - ${t('doc_webhookEventCard')}`, color: '#52c41a' },
    { value: 'DEPOSIT', label: `DEPOSIT - ${t('doc_webhookEventDeposit')}`, color: '#fa8c16' },
    { value: 'WITHDRAW', label: `WITHDRAW - ${t('doc_webhookEventWithdraw')}`, color: '#ff4d4f' },
    { value: 'REFUND', label: `REFUND - ${t('doc_webhookEventRefund')}`, color: '#722ed1' },
    { value: 'USER', label: `USER - ${t('doc_webhookEventUser')}`, color: '#13c2c2' },
  ];

  const localizeWebhookDesc = (desc) => {
    if (language === 'zh-CN') return desc;
    const map = { '账户ID': 'Account ID', '业务线用户ID': 'External User ID', '渠道': 'Channel Code', '支付方式': 'Payment Method', '币种': 'Currency', '失败时返回错误码': 'Error code on failure', '失败时返回错误信息': 'Error message on failure', '创建时间': 'Create Time', '更新时间': 'Update Time', '账户名': 'Account Name', '银行名称': 'Bank Name', '银行所在国家': 'Bank Country', '银行地址': 'Bank Address', '扩展参数': 'Extension Parameters', '绑卡ID': 'Recipient ID', '账户姓名': 'Account Name', '银行户名': 'Account Number', '银行名字': 'Bank Name', '银行国家 (ISO 3166-1 alpha-3)': 'Bank Country (ISO 3166-1 alpha-3)', '外部订单号': 'External Order ID', '订单ID': 'Order ID', '子状态': 'Sub Status', '订单金额': 'Amount', '手续费': 'Fee', '完成时间': 'Completed Time', '错误码': 'Error Code', '错误信息': 'Error Message', '手续费币种': 'Fee Currency', '退款单号': 'Refund Order ID', '退款单ID': 'Refund ID', '退款原因': 'Refund Reason', '原订单金额': 'Original Amount', '退款到账金额': 'Refund Arrival Amount', '用户ID': 'User ID', '商户用户ID': 'Merchant User ID', '渠道用户ID': 'Channel User ID', '失败原因': 'Failed Reason', 'LEAN_TECH: 渠道用户ID': 'LEAN_TECH: Channel User ID', 'LEAN_TECH: Access Token': 'LEAN_TECH: Access Token', 'LEAN_TECH: Channel App Token': 'LEAN_TECH: Channel App Token', 'LEAN_TECH: 支付目的地ID': 'LEAN_TECH: Payment Destination ID', 'LEAN_TECH: 从银行获取的用户全名称': 'LEAN_TECH: Full name retrieved from bank', 'LEAN_TECH: 同名校验结果 PERFECT_MATCH / PARTIAL_MATCH / NO_MATCH / RECONNECT_REQUIRED / NO_RESULT': 'LEAN_TECH: Name match result', '发送方账户名称': 'Sender Account Name', '发送方账户号': 'Sender Account No.', '发送方 SWIFT/BIC Code': 'Sender SWIFT/BIC Code', '发送方地址': 'Sender Address', '发送方国家代码': 'Sender Country Code', '充值意图': 'Deposit Purpose', '备注': 'Remarks', '转账时间': 'Transfer Time', '支付链接': 'Payment URL', '二维码图片': 'QR Code Image', '授权ID (AUTHORIZATION_ID时有值)': 'Authorization ID', '授权Token (AUTHORIZATION_ID时有值)': 'Authorization Token', '前端授权Token (AUTHORIZATION_ID时有值)': 'App Authorization Token', 'Payer 信息': 'Payer Info', '支付信息': 'Payment Info' };
    return map[desc] || desc;
  };

  const webhookTab = (
    <div style={{ maxWidth: 1600, margin: '0 auto', lineHeight: 1.8, fontSize: 13.5, color: COLORS.textPrimary }}>
      {/* Title */}
      <div style={{ padding: '20px 24px', background: 'linear-gradient(135deg, #fff1f0 0%, #fff7e6 100%)', borderRadius: 8, marginBottom: 20, borderLeft: '3px solid #eb2f96' }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#1a1a1a', marginBottom: 4 }}>🔔 {t('doc_webhookTitle')}</div>
        <div style={{ fontSize: 13, color: '#666' }}>{t('doc_webhookSubtitle')}</div>
      </div>

      {/* Security & Retry */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <div style={{ background: '#fff', borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: '16px 20px', height: '100%', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 28, height: 28, borderRadius: '50%', background: '#fff1f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>🔒</span>
              {t('doc_webhookSecurity')}
            </div>
            {[t('doc_webhookSecurityDesc1'), t('doc_webhookSecurityDesc2'), t('doc_webhookSecurityDesc3')].map((item, i) => (
              <div key={i} style={{ fontSize: 12, padding: '4px 0', display: 'flex', alignItems: 'center', gap: 8, color: COLORS.textSecondary }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#eb2f96', flexShrink: 0 }} /> {item}
              </div>
            ))}
          </div>
        </Col>
        <Col span={12}>
          <div style={{ background: '#fff', borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: '16px 20px', height: '100%', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 28, height: 28, borderRadius: '50%', background: '#fff7e6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>🔄</span>
              {t('doc_webhookRetry')}
            </div>
            <div style={{ fontSize: 12, color: COLORS.textSecondary, marginBottom: 8 }}>{t('doc_webhookRetryDesc')}</div>
            <div style={{ padding: '8px 12px', background: '#fffbe6', borderRadius: 6, fontSize: 12, color: '#ad6800', border: '1px solid #ffe58f' }}>
              ⚠️ {t('doc_webhookRetryWarning')}
            </div>
          </div>
        </Col>
      </Row>

      {/* Verify & Headers */}
      <div style={{ background: '#fff', borderRadius: 8, border: `1px solid ${COLORS.border}`, marginBottom: 16, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 20px', borderBottom: `1px solid ${COLORS.borderLight}`, background: '#fafbfc' }}>
          <span style={{ width: 28, height: 28, borderRadius: '50%', background: '#f0f5ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13 }}>🔑</span>
          <span style={{ fontWeight: 600, fontSize: 14 }}>{t('doc_webhookVerifyTitle')}</span>
        </div>
        <div style={{ padding: '16px 20px' }}>
          <p style={{ margin: '0 0 8px', color: COLORS.textSecondary }}>{t('doc_webhookVerifyDesc')}</p>
          <p style={{ margin: '0 0 8px', color: COLORS.textSecondary }}>{t('doc_webhookHeaderDesc')}</p>
          <pre style={{ background: COLORS.codeBg, color: COLORS.textCode, padding: '14px 18px', borderRadius: 6, fontSize: 12, fontFamily: "'JetBrains Mono', 'SF Mono', monospace", lineHeight: 1.7, margin: '8px 0 10px', border: 'none' }}>
{`X-SIGNATURE: base64_ecc_signature
X-TIMESTAMP: 1700000000`}
          </pre>
          <div style={{ padding: '8px 12px', background: '#fff2f0', borderRadius: 6, fontSize: 12, color: '#cf1322', border: '1px solid #ffccc7' }}>
            🔒 {t('doc_webhookVerifyWarning')}
          </div>
        </div>
      </div>

      {/* Request Body */}
      <div style={{ background: '#fff', borderRadius: 8, border: `1px solid ${COLORS.border}`, marginBottom: 16, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 20px', borderBottom: `1px solid ${COLORS.borderLight}`, background: '#fafbfc' }}>
          <span style={{ width: 28, height: 28, borderRadius: '50%', background: '#e6fffb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13 }}>📨</span>
          <span style={{ fontWeight: 600, fontSize: 14 }}>{t('doc_webhookRequestBody')}</span>
        </div>
        <div style={{ padding: '16px 20px' }}>
          <Table size="small" pagination={false} bordered dataSource={[
            { name: 'msgId', type: 'String', required: true, desc: t('doc_webhookFieldMsgId') },
            { name: 'eventType', type: 'String', required: true, desc: t('doc_webhookFieldEventType') },
            { name: 'version', type: 'String', required: true, desc: t('doc_webhookFieldVersion') },
            { name: 'data', type: 'JsonString', required: true, desc: t('doc_webhookFieldData') },
          ]} columns={[
            { title: t('doc_webhookFieldName'), dataIndex: 'name', width: 120, render: v => <Text code>{v}</Text> },
            { title: t('doc_webhookFieldType'), dataIndex: 'type', width: 100, render: v => <Tag color="processing">{v}</Tag> },
            { title: t('doc_webhookFieldRequired'), dataIndex: 'required', width: 70, align: 'center', render: v => v ? <Tag color="red">Y</Tag> : <Tag>N</Tag> },
            { title: t('doc_webhookFieldDesc'), dataIndex: 'desc' },
          ]} rowKey="name" />

          <div style={{ marginTop: 14 }}>
            <Text strong>{t('doc_webhookExample')}:</Text>
            <pre style={{ background: COLORS.codeBg, color: COLORS.textCode, padding: '14px 18px', borderRadius: 6, fontSize: 12, fontFamily: "'JetBrains Mono', 'SF Mono', monospace", lineHeight: 1.7, margin: '8px 0 0', border: 'none' }}>
{`{
  "msgId": "msg_20260320_001",
  "eventType": "DEPOSIT",
  "version": "1.0.0",
  "data": "{\\"orderId\\":\\"ORD20260320001\\",\\"status\\":\\"SUCCESS\\",...}"
}`}
            </pre>
          </div>
        </div>
      </div>

      {/* Event Type Data Structures */}
      <div style={{ background: '#fff', borderRadius: 8, border: `1px solid ${COLORS.border}`, marginBottom: 16, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: `1px solid ${COLORS.borderLight}`, background: '#fafbfc' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ width: 28, height: 28, borderRadius: '50%', background: '#f6ffed', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13 }}>📋</span>
            <span style={{ fontWeight: 600, fontSize: 14 }}>{t('doc_webhookDataStructure')}</span>
          </div>
          <Select
            value={webhookEventType}
            onChange={setWebhookEventType}
            style={{ width: 280 }}
            options={webhookEventOptions}
          />
        </div>
        <div style={{ padding: '16px 20px' }}>
          {/* Event type tags */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
            {webhookEventOptions.map(opt => (
              <Tag
                key={opt.value}
                color={webhookEventType === opt.value ? opt.color : undefined}
                style={{ cursor: 'pointer', borderStyle: webhookEventType === opt.value ? 'solid' : 'dashed' }}
                onClick={() => setWebhookEventType(opt.value)}
              >
                {opt.value}
              </Tag>
            ))}
          </div>

          <Table
            size="small"
            pagination={false}
            bordered
            dataSource={(WEBHOOK_EVENT_DATA[webhookEventType] || []).flatMap(item =>
              item.children ? [item, ...item.children.map(c => ({ ...c, _isChild: true }))] : [item]
            )}
            columns={[
              { title: t('doc_webhookFieldName'), dataIndex: 'name', width: 200, render: (v, r) => r._isChild
                ? <span style={{ paddingLeft: 16 }}><Text code style={{ fontSize: 11, color: '#8c8c8c' }}>{v.replace(/^\w+\./, '↳ ')}</Text></span>
                : <Text code>{v}</Text>
              },
              { title: t('doc_webhookFieldType'), dataIndex: 'type', width: 110, render: v => <Tag color="processing">{v}</Tag> },
              { title: t('doc_webhookFieldRequired'), dataIndex: 'required', width: 70, align: 'center', render: v => v ? <Tag color="red">Y</Tag> : <Tag>N</Tag> },
              { title: t('doc_webhookFieldDesc'), dataIndex: 'desc', render: v => <span style={{ fontSize: 12 }}>{localizeWebhookDesc(v)}</span> },
            ]}
            rowKey="name"
            rowClassName={r => r._isChild ? 'webhook-child-row' : ''}
          />
          <style>{`.webhook-child-row td { background: #fafbfc !important; }`}</style>
        </div>
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════
  // GUIDE TAB — Clean Checkout.com-inspired cards
  // ═══════════════════════════════════════════════════════════
  const codeBlock = (text) => (
    <pre style={{ background: COLORS.codeBg, color: COLORS.textCode, padding: '14px 18px', borderRadius: 6, fontSize: 12, fontFamily: "'JetBrains Mono', 'SF Mono', monospace", lineHeight: 1.7, overflowX: 'auto', margin: '10px 0 14px', border: 'none' }}>{text}</pre>
  );
  const guideSection = (num, title, children) => (
    <div style={{ background: '#fff', borderRadius: 8, border: `1px solid ${COLORS.border}`, marginBottom: 20, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 20px', borderBottom: `1px solid ${COLORS.borderLight}`, background: '#fafbfc' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: '50%', background: COLORS.accent, color: '#fff', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{num}</span>
        <span style={{ fontSize: 14, fontWeight: 600, color: COLORS.textPrimary }}>{title}</span>
      </div>
      <div style={{ padding: '16px 20px' }}>{children}</div>
    </div>
  );
  const guideAlert = (text, type = 'warning') => {
    const s = { warning: { bg: COLORS.orangeBg, border: '#fde68a', color: '#92400e', icon: '⚠️' }, danger: { bg: COLORS.redBg, border: '#fecaca', color: '#991b1b', icon: '🔒' }, info: { bg: COLORS.blueBg, border: COLORS.blueBorder, color: '#1e40af', icon: '💡' } }[type];
    return <div style={{ padding: '8px 12px', background: s.bg, border: `1px solid ${s.border}`, borderRadius: 6, fontSize: 12, color: s.color, marginTop: 10, lineHeight: 1.7 }}>{s.icon} {text}</div>;
  };

  const guideTab = (
    <div style={{ maxWidth: 1600, margin: '0 auto', lineHeight: 1.8, fontSize: 13.5, color: COLORS.textPrimary }}>
      <div style={{ padding: '16px 20px', background: COLORS.accentBg, borderRadius: 8, marginBottom: 24, fontSize: 13, color: COLORS.textSecondary, borderLeft: `3px solid ${COLORS.accent}` }}>
        {t('doc_guideIntro')}
      </div>

      {guideSection(1, t('doc_guideS1Title'), <>
        <Table size="small" pagination={false} bordered style={{ marginBottom: 10 }} dataSource={[
          { env: 'Sandbox', endpoint: 'https://sandbox-api.fiat.osl.com', endpointCn: 'https://sandbox-api.fiat.osl-pay.com' },
          { env: 'Production', endpoint: 'https://api.fiat.osl.com', endpointCn: 'https://api.fiat.osl-pay.com' },
        ]} columns={[
          { title: t('doc_guideS1Env'), dataIndex: 'env', width: 100, render: v => <Tag color={v === 'Sandbox' ? 'blue' : 'green'}>{v}</Tag> },
          { title: t('doc_guideS1Endpoint'), dataIndex: 'endpoint', render: v => <code style={{ fontSize: 12 }}>{v}</code> },
          { title: t('doc_guideS1CnLine'), dataIndex: 'endpointCn', render: v => <code style={{ fontSize: 12 }}>{v}</code> },
        ]} rowKey="env" />
        <div style={{ fontSize: 12, color: COLORS.textMuted }}>Content-Type: <code>application/json; charset=utf-8</code> | {t('doc_guideS1Timestamp')}</div>
        {guideAlert(t('doc_guideS1Whitelist'), 'warning')}
      </>)}

      {guideSection(2, t('doc_guideS2Title'), <>
        <p style={{ margin: '0 0 10px', color: COLORS.textSecondary }}>{t('doc_guideS2Desc')}</p>
        <Table size="small" pagination={false} bordered dataSource={[
          { header: 'Content-Type', desc: 'application/json', required: true },
          { header: 'appId', desc: t('doc_guideS2AppIdDesc'), required: true },
          { header: 'timestamp', desc: t('doc_guideS2TimestampDesc'), required: true },
          { header: 'signature', desc: t('doc_guideS2SignatureDesc'), required: true },
        ]} columns={[
          { title: 'Header', dataIndex: 'header', width: 140, render: v => <code style={{ fontWeight: 600, fontSize: 12 }}>{v}</code> },
          { title: t('doc_colDesc'), dataIndex: 'desc' },
          { title: t('doc_guideS2Required'), dataIndex: 'required', width: 70, align: 'center', render: v => v ? <span style={{ color: COLORS.red, fontWeight: 600, fontSize: 11 }}>required</span> : <span style={{ color: COLORS.textMuted, fontSize: 11 }}>optional</span> },
        ]} rowKey="header" />
        {guideAlert(t('doc_guideS2Warning'), 'danger')}
      </>)}

      {guideSection(3, t('doc_guideS3Title'), <>
        <p style={{ margin: '0 0 12px', color: COLORS.textSecondary }}>{t('doc_guideS3Desc')} <strong>{t('doc_guideS3ECC')}</strong> {t('doc_guideS3Desc2')} <code>SHA384withECDSA</code>{t('doc_guideS3Desc3')}</p>
        <Row gutter={16} style={{ marginBottom: 14 }}>
          <Col span={12}>
            <div style={{ padding: '12px 16px', background: COLORS.accentBg, borderRadius: 8, fontSize: 12, border: `1px solid ${COLORS.accent}22` }}>
              <strong style={{ fontSize: 13, color: COLORS.accent }}>🔐 {t('doc_guideS3MerchantKey')}</strong>
              <div style={{ marginTop: 6, color: COLORS.textSecondary }}>{t('doc_guideS3MerchantPriv')}</div>
              <div style={{ color: COLORS.textSecondary }}>{t('doc_guideS3MerchantPub')}</div>
            </div>
          </Col>
          <Col span={12}>
            <div style={{ padding: '12px 16px', background: COLORS.greenBg, borderRadius: 8, fontSize: 12, border: `1px solid ${COLORS.greenBorder}` }}>
              <strong style={{ fontSize: 13, color: COLORS.green }}>🔔 {t('doc_guideS3WebhookKey')}</strong>
              <div style={{ marginTop: 6, color: COLORS.textSecondary }}>{t('doc_guideS3WebhookPriv')}</div>
              <div style={{ color: COLORS.textSecondary }}>{t('doc_guideS3WebhookPub')}</div>
            </div>
          </Col>
        </Row>
        <strong>{t('doc_guideS3Plaintext')}</strong>
        <div style={{ marginTop: 6 }}>
          <Tag color="blue">GET</Tag>
          {codeBlock('appId=[appId]&method=GET&timestamp=[timestamp]&url=[path?querystring]')}
          <Tag color="green">POST</Tag>
          {codeBlock('appId=[appId]&method=POST&timestamp=[timestamp]&url=[path]&body=[bodyJsonString]')}
        </div>
        <strong>{t('doc_guideS3BodyRule')}</strong>
        <p style={{ fontSize: 12, color: COLORS.textSecondary, margin: '4px 0 6px' }}>{t('doc_guideS3BodyDesc')}</p>
        {codeBlock(`ObjectMapper mapper = new ObjectMapper();\nmapper.enable(SerializationFeature.ORDER_MAP_ENTRIES_BY_KEYS);\nmapper.setSerializationInclusion(JsonInclude.Include.NON_NULL);\nmapper.enable(MapperFeature.SORT_PROPERTIES_ALPHABETICALLY);\nmapper.configure(DeserializationFeature.FAIL_ON_UNKNOWN_PROPERTIES, false);\nmapper.registerModule(new JavaTimeModule());`)}
        <strong>{t('doc_guideS3KeyGen')}</strong>
        {codeBlock(`PRIV=$(openssl ecparam -genkey -name secp384r1 2>/dev/null)\necho "Private Key:"\necho "$PRIV" | openssl pkcs8 -topk8 -nocrypt -outform DER 2>/dev/null | base64 -w 0\necho ""\necho "Public Key:"\necho "$PRIV" | openssl ec -pubout -outform DER 2>/dev/null | base64 -w 0`)}
        <div style={{ fontSize: 12, color: COLORS.textMuted }}>{t('doc_guideS3KeyNote')}</div>
      </>)}

      {guideSection(4, t('doc_guideS4Title'), <>
        <p style={{ margin: '0 0 10px', color: COLORS.textSecondary }}>{t('doc_guideS4Desc')} <strong>{t('doc_guideS4Two')}</strong> {t('doc_guideS4Desc2')}</p>
        <Table size="small" pagination={false} bordered dataSource={[
          { rule: t('doc_guideS4NoSci'), example: '1e3', ok: false },
          { rule: t('doc_guideS4NoComma'), example: '1,000.00', ok: false },
          { rule: t('doc_guideS4UseDot'), example: '1000.50', ok: true },
          { rule: t('doc_guideS4NoTrail'), example: '"1000.50 "', ok: false },
          { rule: t('doc_guideS4NoExtra'), example: t('doc_guideS4ExceedPrecision'), ok: false },
        ]} columns={[
          { title: t('doc_guideS4Rule'), dataIndex: 'rule', width: 200 },
          { title: t('doc_example'), dataIndex: 'example', width: 160 },
          { title: '✓/✗', dataIndex: 'ok', width: 60, align: 'center', render: v => v ? <Tag color="success">✓</Tag> : <Tag color="error">✗</Tag> },
        ]} rowKey="rule" />
      </>)}

      {guideSection(5, t('doc_guideS5Title'), <>
        {codeBlock(`{\n  "code": 200,\n  "subCode": "000000",\n  "msg": "success",\n  "requestTime": 1686015475000,\n  "requestId": "requestIdxxxxx",\n  "data": {}\n}`)}
        <Table size="small" pagination={false} bordered dataSource={[
          { field: 'code', type: 'Integer', desc: t('doc_guideS5CodeDesc') },
          { field: 'subCode', type: 'String', desc: t('doc_guideS5SubCodeDesc') },
          { field: 'msg', type: 'String', desc: t('doc_guideS5MsgDesc') },
          { field: 'requestTime', type: 'Long', desc: t('doc_guideS5TimeDesc') },
          { field: 'requestId', type: 'String', desc: t('doc_guideS5ReqIdDesc') },
          { field: 'data', type: 'Object', desc: t('doc_guideS5DataDesc') },
        ]} columns={[
          { title: t('doc_guideS5Field'), dataIndex: 'field', width: 120, render: v => <code style={{ fontSize: 12 }}>{v}</code> },
          { title: t('doc_guideS5Type'), dataIndex: 'type', width: 80, render: v => <span style={{ fontSize: 11, color: '#3b82f6', fontWeight: 500 }}>{v.toLowerCase()}</span> },
          { title: t('doc_guideS5Desc'), dataIndex: 'desc' },
        ]} rowKey="field" />
      </>)}

      {guideSection(6, t('doc_guideS6Title'), <>
        <Table size="small" pagination={false} bordered dataSource={[
          { code: '200 / 000000', desc: t('doc_guideS6Success'), action: t('doc_guideS6SuccessAction'), color: COLORS.green },
          { code: '400xxx', desc: t('doc_guideS6Client'), action: t('doc_guideS6ClientAction'), color: COLORS.orange },
          { code: '4002xx', desc: t('doc_guideS6Sign'), action: t('doc_guideS6SignAction'), color: COLORS.red },
          { code: '500xxx', desc: t('doc_guideS6System'), action: t('doc_guideS6SystemAction'), color: '#8b5cf6' },
        ]} columns={[
          { title: t('doc_guideS6ErrCode'), dataIndex: 'code', width: 140, render: (v, r) => <code style={{ color: r.color, fontWeight: 600, fontSize: 12 }}>{v}</code> },
          { title: t('doc_guideS6Desc'), dataIndex: 'desc' },
          { title: t('doc_guideS6Action'), dataIndex: 'action', width: 200 },
        ]} rowKey="code" />
      </>)}

      {guideSection(7, t('doc_guideS7Title'), <>
        <p style={{ margin: '0 0 10px', color: COLORS.textSecondary }}>{t('doc_guideS7Desc')}</p>
        {codeBlock('X-SIGNATURE: base64_ecc_signature\nX-TIMESTAMP: 1700000000')}
        <p style={{ margin: '8px 0 6px', color: COLORS.textSecondary }}>{t('doc_guideS7Verify')}</p>
        {guideAlert(t('doc_guideS7Warning'), 'warning')}
      </>)}

      {guideSection(8, t('doc_guideS8Title'), <>
        <Table size="small" pagination={false} bordered dataSource={t('doc_guideS8Items')} columns={[
          { title: t('doc_guideS8CheckItem'), dataIndex: 'item', width: 380 },
          { title: t('doc_guideS8Cat'), dataIndex: 'cat', width: 90, render: v => <span style={{ fontSize: 11, padding: '1px 8px', borderRadius: 10, background: COLORS.accentBg, color: COLORS.accent, fontWeight: 500 }}>{v}</span> },
          { title: 'Sandbox', width: 80, align: 'center', render: () => <span style={{ display: 'inline-block', width: 16, height: 16, border: `2px solid ${COLORS.border}`, borderRadius: 3, background: '#fff' }} /> },
          { title: 'Prod', width: 80, align: 'center', render: () => <span style={{ display: 'inline-block', width: 16, height: 16, border: `2px solid ${COLORS.border}`, borderRadius: 3, background: '#fff' }} /> },
        ]} rowKey="item" />
      </>)}

      {guideSection(9, t('doc_guideS9Title'), <>
        <div style={{ position: 'relative', paddingLeft: 28 }}>
          <div style={{ position: 'absolute', left: 11, top: 8, bottom: 8, width: 2, background: `linear-gradient(180deg, ${COLORS.accent} 0%, ${COLORS.accentLight} 100%)`, borderRadius: 2 }} />
          {t('doc_guideS9Steps').map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 16, position: 'relative' }}>
              <div style={{ width: 22, height: 22, borderRadius: '50%', background: COLORS.accent, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 10, fontWeight: 700, position: 'relative', zIndex: 1 }}>{i + 1}</div>
              <div style={{ background: '#f8fafc', borderRadius: 6, padding: '8px 12px', flex: 1, border: `1px solid ${COLORS.border}` }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.textPrimary }}>{item.step}</div>
                <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </>)}

      {guideSection(10, t('doc_guideS10Title'), <>
        <Row gutter={16}>
          <Col span={12}>
            <div style={{ padding: '14px 18px', background: COLORS.blueBg, borderRadius: 8, border: `1px solid ${COLORS.blueBorder}` }}>
              <strong style={{ fontSize: 13, display: 'block', marginBottom: 10, color: '#1e40af' }}>🏢 {t('doc_guideS10FiatProvides')}</strong>
              {t('doc_guideS10FiatItems').map((item, i) => (
                <div key={i} style={{ fontSize: 12, padding: '3px 0', display: 'flex', alignItems: 'center', gap: 8, color: COLORS.textSecondary }}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: COLORS.blue, flexShrink: 0 }} /> {item}
                </div>
              ))}
            </div>
          </Col>
          <Col span={12}>
            <div style={{ padding: '14px 18px', background: COLORS.greenBg, borderRadius: 8, border: `1px solid ${COLORS.greenBorder}` }}>
              <strong style={{ fontSize: 13, display: 'block', marginBottom: 10, color: '#166534' }}>💼 {t('doc_guideS10BizProvides')}</strong>
              {t('doc_guideS10BizItems').map((item, i) => (
                <div key={i} style={{ fontSize: 12, padding: '3px 0', display: 'flex', alignItems: 'center', gap: 8, color: COLORS.textSecondary }}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: COLORS.green, flexShrink: 0 }} /> {item}
                </div>
              ))}
            </div>
          </Col>
        </Row>
      </>)}
    </div>
  );

  // ═══════════════════════════════════════════════════════════
  // ADMIN PANEL
  // ═══════════════════════════════════════════════════════════
  const AdminPanel = () => {
    const [adminData, setAdminData] = useState([]);
    const [adminLoading, setAdminLoading] = useState(false);
    const [editModal, setEditModal] = useState(false);
    const [editRecord, setEditRecord] = useState(null);
    const [editForm] = Form.useForm();

    const loadAdminData = async () => {
      setAdminLoading(true);
      try { const { data } = await metaApi.adminList(); setAdminData(data || []); }
      catch (e) { message.error(t('doc_loadFailed')); }
      finally { setAdminLoading(false); }
    };
    useEffect(() => { loadAdminData(); }, []);

    const handleSave = async () => {
      try {
        const values = editForm.getFieldsValue();
        JSON.parse(values.fieldsJson);
        if (values.exampleJson) JSON.parse(values.exampleJson);
        if (editRecord?.id) { await metaApi.adminUpdate(editRecord.id, values); message.success(t('doc_updateSuccess')); }
        else { await metaApi.adminCreate(values); message.success(t('doc_createSuccess')); }
        setEditModal(false); loadAdminData(); loadOverview();
      } catch (e) {
        if (e instanceof SyntaxError) message.error(t('doc_jsonError'));
        else message.error(t('doc_saveFailed') + ': ' + (e.response?.data?.message || e.message));
      }
    };
    const handleDelete = async (id) => {
      try { await metaApi.adminDelete(id); message.success(t('doc_deleteSuccess')); loadAdminData(); loadOverview(); }
      catch (e) { message.error(t('doc_deleteFailed')); }
    };
    const handleRefresh = async () => {
      try { await metaApi.adminRefresh(); message.success(t('doc_cacheRefreshed')); loadOverview(); }
      catch (e) { message.error(t('doc_refreshFailed')); }
    };
    const handleCopy = (record) => {
      setEditRecord(null);
      editForm.setFieldsValue({ apiCode: record.apiCode, channelCode: record.channelCode + '_COPY', apiName: record.apiName + ' (Copy)', description: record.description, fieldsJson: record.fieldsJson, exampleJson: record.exampleJson });
      setEditModal(true);
    };

    return (
      <div style={{ maxWidth: 1600, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditRecord(null); editForm.resetFields(); setEditModal(true); }}>{t('doc_addConfig')}</Button>
          <Button icon={<ReloadOutlined />} onClick={handleRefresh}>{t('doc_refreshCache')}</Button>
          <span style={{ fontSize: 12, color: COLORS.textMuted }}>{t('doc_totalConfigs', adminData.length)}</span>
        </div>
        <div style={{ background: '#fff', borderRadius: 8, border: `1px solid ${COLORS.border}`, overflow: 'hidden' }}>
          <Table
            columns={[
              { title: 'ID', dataIndex: 'id', width: 60 },
              { title: 'API Code', dataIndex: 'apiCode', width: 140, render: v => <Tag color={API_CODE_LABELS[v]?.color}>{v}</Tag> },
              { title: t('doc_adminChannel'), dataIndex: 'channelCode', width: 140 },
              { title: t('doc_apiNameLabel'), dataIndex: 'apiName', width: 160, ellipsis: true },
              { title: t('doc_adminStatus'), dataIndex: 'status', width: 80, render: v => <Tag color={v === 'ACTIVE' ? 'green' : 'default'}>{v}</Tag> },
              { title: t('doc_adminVersion'), dataIndex: 'version', width: 60 },
              { title: t('doc_adminCreator'), dataIndex: 'createdBy', width: 100 },
              { title: t('doc_adminAction'), key: 'action', width: 180, fixed: 'right', render: (_, record) => (
                <Space size={4}>
                  <Button type="link" size="small" icon={<EditOutlined />} onClick={() => { setEditRecord(record); editForm.setFieldsValue(record); setEditModal(true); }}>{t('doc_adminEdit')}</Button>
                  <Button type="link" size="small" icon={<CopyOutlined />} onClick={() => handleCopy(record)}>{t('doc_adminCopy')}</Button>
                  <Popconfirm title={t('doc_confirmDelete')} onConfirm={() => handleDelete(record.id)}>
                    <Button type="link" size="small" danger icon={<DeleteOutlined />}>{t('doc_adminDelete')}</Button>
                  </Popconfirm>
                </Space>
              ) },
            ]}
            dataSource={adminData} rowKey="id" size="small" loading={adminLoading} scroll={{ x: 920 }}
            pagination={{ pageSize: 20, showSizeChanger: true, showTotal: n => t('doc_totalPagination', n) }}
          />
        </div>
        <Modal title={editRecord?.id ? t('doc_editMeta') : t('doc_addMeta')} open={editModal} onOk={handleSave} onCancel={() => setEditModal(false)} width={800} okText={t('save')} cancelText={t('cancel')}>
          <Form form={editForm} layout="vertical" size="small">
            <Row gutter={16}>
              <Col span={12}><Form.Item name="apiCode" label="API Code" rules={[{ required: true }]}><Select options={['ACCOUNT_CREATE', 'CARD_BIND', 'DEPOSIT_CREATE', 'DEPOSIT_CLOSE', 'DEPOSIT_CAPTURE', 'WITHDRAW_CREATE', 'REFUND_CREATE', 'USER_CREATE'].map(v => ({ value: v, label: v }))} /></Form.Item></Col>
              <Col span={12}><Form.Item name="channelCode" label={t('doc_channelCodeLabel')} rules={[{ required: true }]}><Input placeholder="e.g. TAZAPAY" /></Form.Item></Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}><Form.Item name="apiName" label={t('doc_apiNameLabel')}><Input /></Form.Item></Col>
              <Col span={12}><Form.Item name="description" label={t('doc_descLabel')}><Input /></Form.Item></Col>
            </Row>
            <Form.Item name="fieldsJson" label={t('doc_fieldConfig')} rules={[{ required: true }]} extra={t('doc_fieldConfigExtra')}>
              <JsonEditor rows={14} placeholder='[{"name":"fieldName","label":"Label","type":"STRING","required":true,"group":"extParam"}]' />
            </Form.Item>
            <Form.Item name="exampleJson" label={t('doc_exampleRequestJson')}>
              <JsonEditor rows={6} placeholder='{"channelCode":"TAZAPAY","key":"value"}' />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════
  // FINAL LAYOUT — Admin Console style: gradient header + sidebar + content card
  // ═══════════════════════════════════════════════════════════
  const sideMenuItems = [
    { key: 'overview', icon: <FileTextOutlined />, label: t('doc_tabOverview') },
    { key: 'detail', icon: <ApiOutlined />, label: t('doc_tabDetail') },
    { key: 'webhook', icon: <BellOutlined />, label: t('doc_tabWebhook') },
    { key: 'guide', icon: <BookOutlined />, label: t('doc_tabGuide') },
    { key: 'matrix', icon: <BarChartOutlined />, label: t('doc_tabMatrix') },
    ...(canManageApiDoc ? [{ key: 'admin', icon: <SettingOutlined />, label: t('doc_tabAdmin') }] : []),
  ];

  const sectionContent = {
    overview: overviewTab,
    detail: detailTab,
    webhook: webhookTab,
    guide: guideTab,
    matrix: <div style={{ maxWidth: 1600, margin: '0 auto' }}>
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: COLORS.textPrimary, margin: '0 0 6px', letterSpacing: '-0.3px' }}>{t('doc_tabMatrix')}</h1>
          <p style={{ fontSize: 13, color: COLORS.textSecondary, margin: 0 }}>{t('doc_matrixSubtitle')}</p>
        </div>
        <ChannelCapabilityMatrix t={t} />
      </div>,
    admin: canManageApiDoc ? <AdminPanel /> : null,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 64px)' }}>
      {/* ── Top gradient header bar (Admin Console style) ── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 28px', height: 48,
        background: COLORS.accentGradient,
        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <ApiOutlined style={{ color: '#fff', fontSize: 16 }} />
          <span style={{ color: '#fff', fontSize: 15, fontWeight: 700, letterSpacing: 0.3 }}>Fiat Payment API</span>
          <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11, marginLeft: 2 }}>Reference v1</span>
        </div>
      </div>

      <div style={{ display: 'flex', flex: 1, background: COLORS.bgContent }}>
        {/* ── Left sidebar (Admin Console style) ── */}
        <div style={{
          width: 220, background: '#fff', flexShrink: 0,
          boxShadow: '2px 0 8px rgba(0,0,0,0.04)',
          overflow: 'auto',
        }}>
          <Menu
            mode="inline"
            selectedKeys={[activeSection]}
            onClick={({ key }) => setActiveSection(key)}
            items={sideMenuItems}
            className="apidoc-sidebar-menu"
            style={{ borderRight: 0, paddingTop: 12, fontSize: 13 }}
          />
        </div>

        {/* ── Main content (wrapped in white card like Admin Console) ── */}
        <div style={{ flex: 1, overflow: 'auto', minWidth: 0, padding: 24 }}>
          <div style={{
            background: '#fff', borderRadius: 10, padding: 24,
            minHeight: 'calc(100vh - 160px)',
            boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
          }}>
            {sectionContent[activeSection]}
          </div>
        </div>
      </div>

      {/* Admin Console sidebar menu styles */}
      <style>{`
        .apidoc-sidebar-menu .ant-menu-item {
          border-radius: 8px !important;
          margin: 2px 8px !important;
        }
        .apidoc-sidebar-menu .ant-menu-item-selected {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
          color: #fff !important;
        }
        .apidoc-sidebar-menu .ant-menu-item-selected .anticon {
          color: #fff !important;
        }
        .apidoc-sidebar-menu .ant-menu-item:not(.ant-menu-item-selected):hover {
          background: #f5f7fa !important;
        }
      `}</style>
    </div>
  );
}
